/**
 * ProjectView Component
 * @version 4.5.0
 *
 * Icon change flow (matches your IconSelect which returns PascalCase names):
 *
 *  1. User clicks SVG in iframe
 *     → useComponentCommunication sends SELECT_COMPONENT
 *       with iconContent = PascalCase name (e.g. "FileText")
 *       and svgOuterHTML = the current raw SVG for source patching
 *
 *  2. PropertiesPanel calls getCurrentIconName(selectedElement)
 *     → passes PascalCase name to <IconSelect value={…} />
 *     → IconSelect shows picker with current icon pre-selected
 *
 *  3. User picks new icon → IconSelect calls onChange(iconName)
 *     → PropertiesPanel calls onIconChange(iconName)
 *     → handleIconChange(iconName) in ProjectView
 *       • generates SVG string via lucide-react renderToString
 *       • posts UPDATE_COMPONENT { property: "icon", value: svgString } to iframe
 *
 *  4. Iframe replaces SVG in DOM, sends COMPONENT_UPDATED
 *     with svgOuterHTML (new SVG) + isIconUpdate: true
 *
 *  5. ProjectView patches source file using replaceSvgInSource()
 *     which looks for the OLD SVG by lucide class / JSX component name
 *     and replaces it with the NEW icon as JSX: <ArrowRight className="…" />
 */

import PropTypes from "prop-types";
import React, { useCallback, useMemo, useRef, useState} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import SCMService from "../../services/api/sourceCodeManagement";
import axios from "axios";
import {
  constructPreviewUrls,
  convertHtmlStringToJsxString,
  replaceComponentById,
} from "../../settings/shared/commonMethods";
import {
  ACCESS_TOKEN,
  PREVIEW_BACKEND_API_URL,
  PREVIEW_UI_LOCALHOST,
  PREVIEW_UI_TEMPLATE,
  API_RESPONSE,
  PREVIEW_DATABASE_URL,
  ALLOWED_IMAGE_MIME_TYPES,
  MAX_IMAGE_UPLOAD_SIZE,
  projectType,
  planAction,
} from "../../settings/shared/constants";
import DrawWorkFlow          from "../Workflow/DrawWorkFlow";
import RadioButtonGroup      from "../common/RadioButtonGroup";
import ChatComponent         from "./ChatComponent";
import ChatToggleButton      from "./ChatToggleButton";
import CheckpointDrawer      from "./CheckpointDrawer";
import CodeViewComponent, { getCodeview, extractFilePayload } from "./CodeViewComponent";
import CodeViewSamplePage    from "./CodeViewSamplePage";
import CustomSwaggerUI       from "./CustomSwaggerUI";
import DatabaseComponent     from "./DatabaseComponent";
import IframeComponent       from "./IframeComponent";
import PlanWorkspace         from "./PlanWorkspace";
import PropertiesPanel       from "./PropertiesPanel";
import WorkflowHeader        from "../common/WorkflowHeader";
import { peekFindingChatDraft } from "../../settings/shared/findingChatDraft";
import BuildingScreen        from "../common/BuildingScreen";
import { BsCursor }          from "react-icons/bs";
import { showNotificationGlobal } from "../common/NotificationProvider";
import AgentService          from "../../services/api/agents";
import UtilsService          from "../../services/api/Utils";
import { PiSpinnerGapLight } from "react-icons/pi";
import StorageConfigService  from "../../services/api/storageConfig/index";
import { validateImageFile, formatMB } from "../../utils/fileValidation";
import { getCurrentIconName } from "../IconSelect";  // re-export helper
import { getEnv } from "../../runtimeEnv";
import WorkflowService from "../../services/api/workflow";
import {
  appendProjectJsonPart,
  buildAppRequestFromPlan,
  buildPlanRequestBody,
  hasExistingLivingPlan,
  isProjectInPlanMode,
  PLAN_WORKSPACE_MODE_EVENT,
  readComposerMode,
  resolvePlanBuildPrompt,
  writeComposerMode,
  writePlanMeta,
  writePlanWorkspaceBuildMode,
  writePlanWorkspaceViewOpen,
} from "./planUtils";
// ─── Config ───────────────────────────────────────────────────────────────────

const DEFAULT_PREVIEW_URL = getEnv("REACT_APP_DEFAULT_PREVIEW_URL");
const ZOOM_STEP = 25, ZOOM_MIN = 25, ZOOM_MAX = 200, ZOOM_DEFAULT = 100;
const CHAT_LOAD_DELAY = 100;
const UPDATE_COMPONENT_DEBOUNCE_MS = 300;

const SCREEN_SIZES = {
  mobile:  { width: "375px",  height: "667px",  label: "375 × 667px"  },
  tablet:  { width: "768px",  height: "1024px", label: "768 × 1024px" },
  desktop: { width: "100%",   height: "100%",   label: "Desktop"      },
};

const SCORE = {
  NATIVE_ID:       100,  // id="login-title" — nearly unique
  LUCIDE_JSX:       90,  // <FileText /> — precise icon name
  CLASS_TAG:        70,  // className="login-card" on a <div>
  PARENT_ID_TAG:    60,  // parent has id="login-form", child is <h1>
  ANCESTOR_CLASS:   50,  // grandparent/ancestor class match
  TEXT_TAG:         40,  // text + tag match (can duplicate)
  TEXT_ONLY:        20,  // text alone (weakest)
  POSITIONAL:       20,  // nthChild position matches
  PARENT_TAG_BONUS: 15,  // parent tag also present
  TAG_ONLY:          5,  // just the tag (useless alone)
};

const MIN_SCORE       = 20;   // below this → ambiguous, don't save wrong file
const AMBIGUITY_SCORE = 60;   // below this → warn about potential ambiguity


// ─── Utilities ────────────────────────────────────────────────────────────────

const resolvePreviewUrl = (project) => {
  if (!project?.id) return "";
  const environmentType = project.environmentType || "K8S";
  const template = environmentType === "K8S" ? PREVIEW_UI_TEMPLATE : PREVIEW_UI_LOCALHOST;
  return constructPreviewUrls(template, project.id) || "";
};

const sanitizeUrl = (url) => {
  if (!url) return null;
  try {
    const p = new URL(url);
    if (p.protocol !== "http:" && p.protocol !== "https:") return null;
    return url;
  } catch { return null; }
};

const getRelativeImagePath = (imagePath) => {
  if (!imagePath || typeof imagePath !== "string") return imagePath;
  if (imagePath.startsWith("data:") || imagePath.startsWith("http://") || imagePath.startsWith("https://")) return imagePath;
  const normalized = imagePath.replace(/\\/g, "/");
  const srcIndex   = normalized.indexOf("src/");
  return srcIndex !== -1 ? normalized.substring(srcIndex) : imagePath;
};

const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const safeConvertToJsx = (html) => {
  if (!html) return html;
  try {
    const result = convertHtmlStringToJsxString(html);
    return (!result || result.length < 3) ? html : result;
  } catch (e) {
    console.warn("[safeConvertToJsx] failed:", e.message);
    return html;
  }
};

// ─── generateLucideSvgString ─────────────────────────────────────────────────
//
// Generates the raw <svg> string for a given Lucide icon name.
// Uses lucide-react's built-in `renderToString` utility which each icon exposes.
// Falls back to a simple SVG wrapper if the icon is not found.
//
// @param  {string} iconName  — PascalCase e.g. "FileText"
// @param  {object} props     — optional override props (width, height, color)
// @returns {string}          — raw <svg>…</svg> string

const generateLucideSvgString = (iconName, props = {}) => {
  if (!iconName) return null;

  try {
    // lucide-react exports icons as React components.
    // We can get the SVG string by creating an element and rendering it.
    const IconComponent = LucideIcons[iconName];
    if (!IconComponent) {
      console.warn("[generateLucideSvgString] Unknown icon:", iconName);
      return null;
    }

    // Use the icon's toSvg method if available (lucide-react >= 0.263)
    if (typeof IconComponent.toSvg === "function") {
      return IconComponent.toSvg({ ...props });
    }

    // Fallback: create a temporary container and render using ReactDOM
    // This works in a browser environment
    const { renderToStaticMarkup } = require("react-dom/server");
    const svgString = renderToStaticMarkup(
        React.createElement(IconComponent, {
          width:       props.width  || 24,
          height:      props.height || 24,
          stroke:      props.stroke || "currentColor",
          strokeWidth: props.strokeWidth || 2,
          fill:        "none",
          xmlns:       "http://www.w3.org/2000/svg",
          ...props,
        })
    );
    return svgString;
  } catch (err) {
    console.warn("[generateLucideSvgString] error:", err);
    return null;
  }
};

// ─── iconNameToJsx ────────────────────────────────────────────────────────────
//
// Converts a PascalCase icon name to the JSX import string that should
// appear in the source file.
//
// e.g. "FileText" → '<FileText className="lucide lucide-file-text" />'
//
// The className follows the Lucide convention so existing CSS still applies.


export const ensureCallOrchestratorImport = (sourceContents) => {
  if (!sourceContents) return sourceContents;

  const hasNamedImport =
      /import\s*\{[^}]*\bcallOrchestrator\b[^}]*\}\s*from\s*['"]@\/services\/orchestrator['"]/m.test(
          sourceContents
      );
  if (hasNamedImport) return sourceContents;

  const existingImportRe =
      /^([ \t]*import\s*\{)([^}]+)(\}\s*from\s*['"]@\/services\/orchestrator['"][;]?[ \t]*)$/m;
  const match = existingImportRe.exec(sourceContents);

  if (match) {
    const prefix = match[1];
    const names = match[2]
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

    if (names.includes("callOrchestrator")) return sourceContents;

    const hasSpaces = match[2].startsWith(" ") || match[2].endsWith(" ");
    const inner = hasSpaces
        ? ` ${[...names, "callOrchestrator"].join(", ")} `
        : [...names, "callOrchestrator"].join(", ");

    return sourceContents.replace(existingImportRe, `${prefix}${inner}${match[3]}`);
  }

  const newImportLine = "import { callOrchestrator } from '@/services/orchestrator';";

  const lastImportRegex = /^import\s.+$/gm;
  let lastImportMatch = null;
  let m;
  while ((m = lastImportRegex.exec(sourceContents)) !== null) {
    lastImportMatch = m;
  }

  if (lastImportMatch) {
    const insertPos = lastImportMatch.index + lastImportMatch[0].length;
    return (
        sourceContents.slice(0, insertPos) +
        "\n" +
        newImportLine +
        sourceContents.slice(insertPos)
    );
  }

  return `${newImportLine}\n${sourceContents}`;
};

const withCallOrchestratorImport = (sourceContents) =>
    sourceContents?.includes("callOrchestrator(")
        ? ensureCallOrchestratorImport(sourceContents)
        : sourceContents;

/** Drop callOrchestrator import when no remaining call sites. */
const withoutUnusedCallOrchestratorImport = (sourceContents) => {
  if (!sourceContents || sourceContents.includes("callOrchestrator(")) {
    return sourceContents;
  }

  let next = sourceContents.replace(
      /^[ \t]*import\s*\{\s*callOrchestrator\s*\}\s*from\s*['"]@\/services\/orchestrator['"];?[ \t]*\r?\n?/m,
      ""
  );

  next = next.replace(
      /^([ \t]*import\s*\{)([^}]+)(\}\s*from\s*['"]@\/services\/orchestrator['"][;]?[ \t]*)$/m,
      (full, prefix, names, suffix) => {
        const filtered = names
            .split(",")
            .map((s) => s.trim())
            .filter((n) => n && n !== "callOrchestrator");
        if (filtered.length === 0) return "";
        const hasSpaces = names.startsWith(" ") || names.endsWith(" ");
        const inner = hasSpaces ? ` ${filtered.join(", ")} ` : filtered.join(", ");
        return `${prefix}${inner}${suffix}`;
      }
  );

  return next;
};

/**
 * Remove orchestrator onClick from the exact usage-site tag for data-element-id.
 * Leaves unrelated onClick handlers alone.
 */
const removeOnClickHandler = (sourceContents, fingerprint) => {
  if (!sourceContents) return sourceContents;

  const dataElementId = resolveDataElementId({}, fingerprint);
  if (!dataElementId) {
    console.warn("[removeOnClickHandler] missing data-element-id");
    return sourceContents;
  }

  const range = findJsxElementRangeByDataElementId(sourceContents, dataElementId);
  if (!range) {
    console.warn("[removeOnClickHandler] data-element-id not in this file:", dataElementId);
    return sourceContents;
  }

  const openTag = sourceContents.substring(range.openStart, range.openEnd);
  if (!/\bcallOrchestrator\s*\(/.test(openTag)) {
    return sourceContents;
  }

  const nextOpenTag = stripJsxAttribute(openTag, "onClick");
  if (nextOpenTag === openTag) return sourceContents;

  const updated =
      sourceContents.substring(0, range.openStart) +
      nextOpenTag +
      sourceContents.substring(range.openEnd);

  return withoutUnusedCallOrchestratorImport(updated);
};

export const patchLucideImport = (sourceContents, oldIconName, newIconName) => {
  if (!newIconName) return sourceContents;

  // ── Find the lucide-react import line ─────────────────────────────────────
  // Matches single-line imports: import { ... } from "lucide-react"
  // Also matches 'lucide-react' (single quotes)
  const importLineRegex =
      /^([ \t]*import\s*\{)([^}]+)(\}\s*from\s*['"]lucide-react['"][;]?[ \t]*)$/m;

  const match = importLineRegex.exec(sourceContents);

  if (match) {
    // ── There IS a lucide-react import line — update it ───────────────────

    const prefix      = match[1];  // "import {"
    const iconList    = match[2];  // " FileText, Star "
    const suffix      = match[3];  // "} from "lucide-react""

    // Parse existing icon names (trim whitespace, filter empty)
    const existingIcons = iconList
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

    // Remove old icon (if present)
    const withoutOld = existingIcons.filter((name) => name !== oldIconName);

    // Add new icon (if not already there)
    const withNew = withoutOld.includes(newIconName)
        ? withoutOld
        : [...withoutOld, newIconName];

    // Rebuild the import line, preserving original formatting style
    const hasSpaces = iconList.startsWith(" ") || iconList.endsWith(" ");
    const inner = hasSpaces
        ? ` ${withNew.join(", ")} `
        : withNew.join(", ");

    const newImportLine = `${prefix}${inner}${suffix}`;
    const result = sourceContents.replace(importLineRegex, newImportLine);

    console.debug(
        "[patchLucideImport] Updated import:",
        `{${iconList.trim()}}  →  {${inner.trim()}}`
    );

    return result;

  } else {
    // ── No lucide-react import found — check for multi-line import ────────

    // Try multi-line:
    //   import {
    //     FileText,
    //     Star,
    //   } from "lucide-react"
    const multiLineRegex =
        /^([ \t]*import\s*\{)([\s\S]*?)(\}\s*from\s*['"]lucide-react['"][;]?[ \t]*)$/m;

    const multiMatch = multiLineRegex.exec(sourceContents);

    if (multiMatch) {
      const prefix   = multiMatch[1];
      const iconList = multiMatch[2];
      const suffix   = multiMatch[3];

      const existingIcons = iconList
          .split(/[\n,]/)
          .map((s) => s.trim())
          .filter(Boolean);

      const withoutOld = existingIcons.filter((name) => name !== oldIconName);
      const withNew    = withoutOld.includes(newIconName)
          ? withoutOld
          : [...withoutOld, newIconName];

      // Preserve indentation from first icon line
      const indentMatch = iconList.match(/^([ \t]+)/m);
      const indent      = indentMatch ? indentMatch[1] : "  ";
      const inner       = "\n" + withNew.map((n) => `${indent}${n},`).join("\n") + "\n";

      const newImportLine = `${prefix}${inner}${suffix}`;
      const result = sourceContents.replace(multiLineRegex, newImportLine);

      console.debug("[patchLucideImport] Updated multi-line import");
      return result;

    } else {
      // ── No lucide-react import at all — add a new import line ─────────
      // Insert after the last existing import statement, or at the top

      console.debug(
          "[patchLucideImport] No lucide-react import found — adding new import for",
          newIconName
      );

      const newImportLine = `import { ${newIconName} } from "lucide-react";`;

      // Find the last import line position
      const lastImportRegex = /^import\s.+$/gm;
      let lastImportMatch   = null;
      let m;
      while ((m = lastImportRegex.exec(sourceContents)) !== null) {
        lastImportMatch = m;
      }

      if (lastImportMatch) {
        // Insert after the last import
        const insertPos = lastImportMatch.index + lastImportMatch[0].length;
        return (
            sourceContents.slice(0, insertPos) +
            "\n" + newImportLine +
            sourceContents.slice(insertPos)
        );
      } else {
        // No imports at all — prepend to file
        return newImportLine + "\n" + sourceContents;
      }
    }
  }
};


export const removeUnusedLucideImport = (sourceContents, iconName) => {
  if (!iconName) return sourceContents;

  // Count occurrences of the icon name in non-import lines
  // (simple check: does it appear as a JSX tag or identifier outside imports?)
  const usageRegex = new RegExp(`<${iconName}[\\s/>]|${iconName}[^\\w]`, "g");
  const importLineRegex = /^[ \t]*import\s*\{[^}]+\}\s*from\s*['"]lucide-react['"][;]?/m;
  const importMatch = importLineRegex.exec(sourceContents);

  if (!importMatch) return sourceContents; // no import to remove from

  // Remove the import line temporarily to check usage in the rest of the file
  const withoutImport = sourceContents.replace(importLineRegex, "");
  const usages        = (withoutImport.match(usageRegex) || []).length;

  if (usages > 0) {
    // Still used somewhere — keep the import
    return sourceContents;
  }

  // Not used — remove from import
  return patchLucideImport(sourceContents, iconName, null);
};

const iconNameToLucideClass = (iconName) => {
  if (!iconName) return "";
  const kebab = iconName.replace(/([A-Z])/g, (m, l, offset) =>
      offset === 0 ? l.toLowerCase() : "-" + l.toLowerCase()
  );
  return `lucide-${kebab}`;
};

/** True when value is a PascalCase Lucide icon name (not SVG markup). */
const isLucideIconName = (value) =>
    typeof value === "string" &&
    !value.trim().startsWith("<") &&
    /^[A-Z][A-Za-z0-9]*$/.test(value.trim());

const resolveLucideIconName = (...candidates) => {
  for (const c of candidates) {
    if (isLucideIconName(c)) return String(c).trim();
  }
  return null;
};

/** Layout / visual props safe to keep when swapping Lucide icons. */
const ICON_STYLE_KEEP = new Set([
  "color", "fill", "stroke", "opacity",
  "display", "verticalAlign", "flexShrink",
  "margin", "marginTop", "marginRight", "marginBottom", "marginLeft",
]);

const pickIconStyles = (styleObj) => {
  if (!styleObj || typeof styleObj !== "object") return null;
  const next = {};
  Object.entries(styleObj).forEach(([k, v]) => {
    if (ICON_STYLE_KEEP.has(k) && v != null && v !== "") next[k] = v;
  });
  // Never carry computed pixel width/height — size comes from size= / className
  return Object.keys(next).length ? next : null;
};

/**
 * Pull size / className / color props from an existing Lucide JSX or SVG tag
 * so the replacement keeps alignment.
 */
const extractPreservedIconProps = (oldTag = "", fallbackClassName = "", fallbackStyle = null) => {
  const props = {
    className: fallbackClassName || "",
    size: null,
    width: null,
    height: null,
    strokeWidth: null,
    color: null,
    style: pickIconStyles(fallbackStyle),
    dataElementId: null,
  };

  if (!oldTag) return props;

  const classMatch =
      oldTag.match(/\bclassName=["']([^"']*)["']/) ||
      oldTag.match(/\bclass=["']([^"']*)["']/);
  if (classMatch) props.className = classMatch[1];

  const sizeMatch = oldTag.match(/\bsize=\{(\d+(?:\.\d+)?)\}/) || oldTag.match(/\bsize=["'](\d+(?:\.\d+)?)["']/);
  if (sizeMatch) props.size = sizeMatch[1];

  const widthMatch = oldTag.match(/\bwidth=\{(\d+(?:\.\d+)?)\}/) || oldTag.match(/\bwidth=["'](\d+(?:\.\d+)?)["']/);
  if (widthMatch) props.width = widthMatch[1];

  const heightMatch = oldTag.match(/\bheight=\{(\d+(?:\.\d+)?)\}/) || oldTag.match(/\bheight=["'](\d+(?:\.\d+)?)["']/);
  if (heightMatch) props.height = heightMatch[1];

  const swMatch =
      oldTag.match(/\bstrokeWidth=\{(\d+(?:\.\d+)?)\}/) ||
      oldTag.match(/\bstroke-width=["']([^"']+)["']/);
  if (swMatch) props.strokeWidth = swMatch[1];

  const colorMatch = oldTag.match(/\bcolor=["']([^"']+)["']/);
  if (colorMatch) props.color = colorMatch[1];

  const styleMatch = oldTag.match(/\bstyle=\{(\{[\s\S]*?\})\}/);
  if (styleMatch) {
    try {
      const normalized = styleMatch[1]
          .replace(/(\w+):/g, '"$1":')
          .replace(/'/g, '"');
      props.style = pickIconStyles({ ...JSON.parse(normalized), ...(props.style || {}) });
    } catch {
      /* keep fallback */
    }
  }

  const idMatch = oldTag.match(/\bdata-element-id=["']([^"']+)["']/);
  if (idMatch) props.dataElementId = idMatch[1];

  return props;
};

const buildIconJsx = (iconName, existingClassName = "", styleObj = null, preserved = null) => {
  const props = preserved || {
    className: existingClassName,
    style: pickIconStyles(styleObj),
  };

  const nonLucideClasses = String(props.className || "")
      .split(/\s+/)
      .filter((c) => c && !c.startsWith("lucide"))
      .join(" ");

  const lucideClass = `lucide ${iconNameToLucideClass(iconName)}`;
  const finalClass = [lucideClass, nonLucideClasses].filter(Boolean).join(" ");

  const attrs = [`className="${finalClass}"`];

  // Prefer explicit size prop (Lucide) — keeps flex/text alignment stable
  if (props.size) {
    attrs.push(`size={${props.size}}`);
  } else if (props.width || props.height) {
    if (props.width) attrs.push(`width={${props.width}}`);
    if (props.height) attrs.push(`height={${props.height}}`);
  }

  if (props.strokeWidth) attrs.push(`strokeWidth={${props.strokeWidth}}`);
  if (props.color) attrs.push(`color="${props.color}"`);
  if (props.dataElementId) attrs.push(`data-element-id="${props.dataElementId}"`);

  const style = pickIconStyles(props.style);
  if (style && Object.keys(style).length) {
    // Ensure Lucide-like inline alignment when style is present
    if (!style.display) style.display = "inline-block";
    if (!style.verticalAlign) style.verticalAlign = "middle";
    attrs.push(`style=${formatJsxStyleValue(style)}`);
  }

  return `<${iconName} ${attrs.join(" ")} />`;
};

// ─── findSourceFile ───────────────────────────────────────────────────────────
const findSourceFile = (appCodes, editedCodes, fingerprint, options = {}) => {
  if (!fingerprint) return null;

  const {
    nativeId,
    className,
    fullClassName,
    tagName,
    textSnippet,
    parentId,
    parentTag,
    nthChild,
    siblingIndex,
    ancestorPath = [],
  } = fingerprint;

  const {
    preferJsx      = false,
    oldIconName    = null,
    activePagePath = null,   // ← NEW
  } = options;

  // Edited files take priority
  const prioritized = [
    ...editedCodes,
    ...appCodes.filter((a) => !editedCodes.some((e) => e.name === a.name)),
  ];

  const jsxExt = /\.(jsx?|tsx?)$/;
  const searchPool = (preferJsx || tagName === "svg")
      ? prioritized.filter((c) => jsxExt.test(c.name))
      : prioritized.filter((c) => !c.name.endsWith(".svg"));

  if (searchPool.length === 0) return null;

  // ══════════════════════════════════════════════════════════════════════
  // STRATEGY 0 (NEW — highest priority):
  //   Use the active page URL to find the file, then CONFIRM it contains
  //   the element being edited. If confirmed → return immediately.
  //   This short-circuits all scoring for the common case.
  // ══════════════════════════════════════════════════════════════════════

  if (activePagePath) {
    const pageFile = resolveFileFromPagePath(
        activePagePath,
        appCodes,
        editedCodes,
        tagName,
        textSnippet
    );

    if (pageFile) {
      const src = pageFile.contents;

      // Confirm: the resolved file actually contains the element
      const confirmedByText  = textSnippet && textSnippet.length > 3 && src.includes(textSnippet);
      const confirmedByClass = className && src.includes(className);
      const confirmedById    = nativeId && !nativeId.startsWith("rt-") &&
          (src.includes(`id="${nativeId}"`) || src.includes(`id='${nativeId}'`));
      const confirmedByTag   = tagName && src.includes(`<${tagName}`);
      const confirmedByIcon  = oldIconName &&
          new RegExp(`<${escapeRegex(oldIconName)}[\\s/>]`).test(src);

      if (confirmedByText || confirmedByClass || confirmedById || confirmedByIcon) {
        console.debug(
            "[findSourceFile] Strategy 0 (page path) matched:",
            pageFile.name,
            "confirmed by:",
            confirmedByText  ? "textSnippet"  :
                confirmedByClass ? "className"    :
                    confirmedById    ? "nativeId"     :
                        confirmedByIcon  ? "lucideIcon"   : "tag"
        );
        return pageFile;
      }

      // Page file found but doesn't contain the element — fall through to scoring
      console.debug(
          "[findSourceFile] Strategy 0 resolved", pageFile.name,
          "but it doesn't contain the element. Falling through to scoring."
      );
    }
  }

  // ══════════════════════════════════════════════════════════════════════
  // STRATEGIES 1–7: confidence scoring (unchanged from previous version)
  // ══════════════════════════════════════════════════════════════════════

  const SCORE_WEIGHTS = {
    NATIVE_ID:       100,
    LUCIDE_JSX:       90,
    CLASS_TAG:        70,
    PARENT_ID_TAG:    60,
    ANCESTOR_CLASS:   50,
    TEXT_TAG:         40,
    TEXT_ONLY:        20,
    POSITIONAL:       20,
    PARENT_TAG_BONUS: 15,
    TAG_ONLY:          5,
  };

  const scored = searchPool.map((file) => {
    const src   = file.contents;
    let   score = 0;

    if (nativeId && !nativeId.startsWith("rt-")) {
      if (src.includes(`id="${nativeId}"`) || src.includes(`id='${nativeId}'`))
        score += SCORE_WEIGHTS.NATIVE_ID;
    }

    if (oldIconName) {
      if (new RegExp(`<${escapeRegex(oldIconName)}[\\s/>]`).test(src))
        score += SCORE_WEIGHTS.LUCIDE_JSX;
    }

    if (className) {
      const classTokens   = (fullClassName || className).split(/\s+/).filter((c) => c.length > 2);
      const hasAnyClass   = classTokens.some((t) => src.includes(t));
      const hasTag        = src.includes(`<${tagName}`);
      if (hasAnyClass && hasTag) {
        score += SCORE_WEIGHTS.CLASS_TAG;
        const count = classTokens.filter((t) => src.includes(t)).length;
        if (count > 1) score += Math.min(count * 5, 20);
      }
    }

    if (parentId && !parentId.startsWith("rt-")) {
      if ((src.includes(`id="${parentId}"`) || src.includes(`id='${parentId}'`)) &&
          src.includes(`<${tagName}`))
        score += SCORE_WEIGHTS.PARENT_ID_TAG;
    }

    ancestorPath.forEach((a, depth) => {
      const w = Math.max(SCORE_WEIGHTS.ANCESTOR_CLASS - depth * 10, 10);
      if (a.id && !a.id.startsWith("rt-") &&
          (src.includes(`id="${a.id}"`) || src.includes(`id='${a.id}'`))) {
        score += w;
      } else if (a.className && src.includes(a.className) && src.includes(`<${a.tag}`)) {
        score += Math.floor(w * 0.6);
      }
    });

    if (textSnippet && textSnippet.length > 3) {
      const hasText = src.includes(textSnippet);
      const hasTag  = src.includes(`<${tagName}`);
      if (hasText && hasTag) {
        score += SCORE_WEIGHTS.TEXT_TAG;
        if (parentTag && src.includes(`<${parentTag}`)) score += SCORE_WEIGHTS.PARENT_TAG_BONUS;
        if (typeof nthChild === "number" && nthChild >= 0) {
          const tagRegex      = new RegExp(`<${tagName}[\\s>/]`, "gi");
          const textIndex     = src.indexOf(textSnippet);
          if (textIndex > 0) {
            const before       = src.substring(0, textIndex);
            const matchesBefore = (before.match(tagRegex) || []).length;
            if (Math.abs(matchesBefore - nthChild) <= 2) score += SCORE_WEIGHTS.POSITIONAL;
          }
        }
      } else if (hasText) {
        score += SCORE_WEIGHTS.TEXT_ONLY;
      }
    }

    if (tagName && !["div","span","p"].includes(tagName)) {
      if (src.includes(`<${tagName}`) || src.includes(`<${tagName.charAt(0).toUpperCase() + tagName.slice(1)}`))
        score += SCORE_WEIGHTS.TAG_ONLY;
    }

    return { file, score };
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const aEdited = editedCodes.some((e) => e.name === a.file.name);
    const bEdited = editedCodes.some((e) => e.name === b.file.name);
    if (aEdited !== bEdited) return bEdited ? 1 : -1;
    return 0;
  });

  const best = scored[0];
  if (!best || best.score < 20) {
    console.debug("[findSourceFile] No confident match. Best:", best?.file.name, best?.score);
    return null;
  }

  console.debug("[findSourceFile] Scoring winner:", best.file.name, "score:", best.score);
  return { name: best.file.name, contents: best.file.contents };
};

const resolveMessageFilePath = (filePath) => {
  if (!filePath) return null;
  if (typeof filePath === "string" && filePath.trim()) return filePath.trim().replace(/\\/g, "/");
  if (typeof filePath === "object") {
    const raw = filePath.path || filePath.fullPath || filePath.name || filePath.file || null;
    return raw ? String(raw).trim().replace(/\\/g, "/") : null;
  }
  return null;
};

/** Match element-map paths (src/pages/X.tsx) to codeview/sync file names. */
const pathsLooselyEqual = (a, b) => {
  if (!a || !b) return false;
  const na = String(a).replace(/\\/g, "/").replace(/^\.\//, "");
  const nb = String(b).replace(/\\/g, "/").replace(/^\.\//, "");
  if (na === nb) return true;
  if (na.endsWith("/" + nb) || nb.endsWith("/" + na)) return true;
  const ba = na.split("/").pop();
  const bb = nb.split("/").pop();
  return Boolean(ba && bb && ba === bb && na.endsWith(ba) && nb.endsWith(bb));
};

const findCodeEntryByPath = (codes, filePath) => {
  const normalizedPath = resolveMessageFilePath(filePath);
  if (!normalizedPath || !Array.isArray(codes)) return null;

  return (
      codes.find((c) => c?.name === normalizedPath) ||
      codes.find((c) => pathsLooselyEqual(c?.name, normalizedPath)) ||
      null
  );
};

/**
 * Load source for a filePath.
 * - useSessionEdits: prefer current unsaved editedCodes (already "pasted" patches this session)
 * - forceFresh: never use appCodes cache; fetch from source-sync / getCodeview
 */
const loadSourceFileByPath = async ({
                                      filePath,
                                      editedCodes = [],
                                      appCodes = [],
                                      projectId,
                                      userId,
                                      settingsTab,
                                      forceFresh = false,
                                      useSessionEdits = true,
                                    }) => {
  const normalizedPath = resolveMessageFilePath(filePath);
  if (!normalizedPath) return null;

  // Working buffer only — previous paste in this session, not stale appCodes
  if (useSessionEdits) {
    const edited = findCodeEntryByPath(editedCodes, normalizedPath);
    if (edited?.contents != null && !edited.isPending) {
      return { name: edited.name || normalizedPath, contents: edited.contents };
    }
  }

  // Stale appCodes cache — skipped when forceFresh
  if (!forceFresh) {
    const cached = findCodeEntryByPath(appCodes, normalizedPath);
    if (cached?.contents != null) {
      return { name: cached.name || normalizedPath, contents: cached.contents };
    }
  }

  if (!projectId || !userId) return null;

  try {
    const payload = await getCodeview(projectId, userId, settingsTab, {
      file: normalizedPath,
    });
    const fileData = extractFilePayload(payload, normalizedPath);
    if (!fileData?.content) {
      // Retry with basename — some sync backends key by file name only
      const base = normalizedPath.split("/").pop();
      if (base && base !== normalizedPath) {
        const basePayload = await getCodeview(projectId, userId, settingsTab, {
          file: base,
        });
        const baseData = extractFilePayload(basePayload, base);
        if (baseData?.content) {
          return {
            name: baseData.name || normalizedPath,
            contents: baseData.content,
          };
        }
      }
      return null;
    }

    // Prefer the canonical name from codeview/sync so Save posts the path the service expects
    return {
      name: fileData.name || normalizedPath,
      contents: fileData.content,
    };
  } catch (error) {
    console.error("[loadSourceFileByPath] failed:", normalizedPath, error);
    return null;
  }
};

const getTagNamePattern = (tagName) => {
  if (!tagName) return "[A-Za-z][\\w.]*";
  const lower = tagName.toLowerCase();
  const pascal = lower.charAt(0).toUpperCase() + lower.slice(1);
  if (lower === pascal) return escapeRegex(lower);
  return `(?:${escapeRegex(lower)}|${escapeRegex(pascal)})`;
};

const stripPreviewAttributes = (jsx) =>
    (jsx ?? "")
        .replace(/\s*data-component-id="[^"]*"/g, "")
        .replace(/\s*data-component-type="[^"]*"/g, "")
        .replace(/\s*data-component-id='[^']*'/g, "")
        .replace(/\s*data-component-type='[^']*'/g, "");

// data-element-id is the stable DOM/source key. Prefer elementId / id / runtimeId
// over map-style componentId (e.g. "Button__primary") when both differ.
const resolveDataElementId = (eventData = {}, fp = {}) =>
    eventData?.elementId ??
    eventData?.runtimeId ??
    fp?.elementId ??
    eventData?.fingerprint?.elementId ??
    eventData?.id ??
    fp?.runtimeId ??
    eventData?.componentId ??
    fp?.componentId ??
    null;

const ensureDataElementId = (jsx, dataElementId) => {
  if (!jsx || !dataElementId) return jsx;
  if (/\bdata-element-id=["']/.test(jsx)) {
    return jsx.replace(
        /\bdata-element-id=["'][^"']*["']/,
        `data-element-id="${dataElementId}"`
    );
  }
  return jsx.replace(
      /^(<[A-Za-z][\w.]*\b[^>]*?)(\s*\/?>)/,
      `$1 data-element-id="${dataElementId}"$2`
  );
};

const getPatchTargetRanges = (sourceContents, eventData, fp) => {
  const dataElementId = resolveDataElementId(eventData, fp);
  if (dataElementId) {
    const range = findJsxElementRangeByDataElementId(sourceContents, dataElementId);
    return { ranges: range ? [range] : [], dataElementId };
  }
  return {
    ranges: getElementRangesToTry(sourceContents, collectElementIds(eventData, fp), fp),
    dataElementId: null,
  };
};

const escapeSingleQuoted = (text) =>
    String(text ?? "").replace(/\\/g, "\\\\").replace(/'/g, "\\'");

const decodeHtmlEntities = (text) =>
    String(text ?? "")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

const extractTextFromChangedElement = (changedElement) => {
  if (!changedElement) return "";
  return decodeHtmlEntities(
      changedElement.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim()
  );
};

const findJsxOpeningTagEnd = (source, openStart) => {
  let pos = openStart + 1;
  let braceDepth = 0;
  let inString = null;

  while (pos < source.length) {
    const ch = source[pos];
    const prev = source[pos - 1];

    if (inString) {
      if (ch === inString && prev !== "\\") inString = null;
      pos++;
      continue;
    }

    if (ch === '"' || ch === "'" || ch === "`") {
      inString = ch;
      pos++;
      continue;
    }

    if (ch === "{") {
      braceDepth++;
      pos++;
      continue;
    }

    if (ch === "}") {
      braceDepth = Math.max(0, braceDepth - 1);
      pos++;
      continue;
    }

    if (ch === ">" && braceDepth === 0) return pos + 1;

    pos++;
  }

  return -1;
};

const findJsxElementCloseRange = (source, openStart, openEnd, componentName) => {
  // Case-SENSITIVE: <Label> must not pair with </label> (and vice versa),
  // or one Label's range can swallow sibling labels and rewrite them all.
  const openPat  = new RegExp(`<${escapeRegex(componentName)}[\\s>/]`, "g");
  const closePat = new RegExp(`<\\/${escapeRegex(componentName)}\\s*>`, "g");

  let depth = 1;
  let pos   = openEnd;
  let closeStart = -1;
  let closeEnd   = -1;

  while (depth > 0 && pos < source.length) {
    openPat.lastIndex  = pos;
    closePat.lastIndex = pos;
    const nextOpen  = openPat.exec(source);
    const nextClose = closePat.exec(source);
    if (!nextClose) break;

    if (nextOpen && nextOpen.index < nextClose.index) {
      depth++;
      pos = nextOpen.index + nextOpen[0].length;
    } else {
      depth--;
      closeStart = nextClose.index;
      closeEnd   = nextClose.index + nextClose[0].length;
      pos        = closeEnd;
    }
  }

  if (closeStart === -1) return null;

  return { closeStart, closeEnd };
};

const findJsxElementRangeByDataElementId = (source, elementId) => {
  if (!elementId || !source) return null;

  const idPatterns = [
    new RegExp(`\\bdata-element-id\\s*=\\s*(["'])${escapeRegex(elementId)}\\1`, "g"),
    new RegExp(
        `\\bdata-element-id\\s*=\\s*\\{\\s*(["'])${escapeRegex(elementId)}\\1\\s*\\}`,
        "g"
    ),
  ];

  for (const idRe of idPatterns) {
    idRe.lastIndex = 0;
    let idMatch;
    while ((idMatch = idRe.exec(source)) !== null) {
      const attrIdx = idMatch.index;
      const sliceStart = Math.max(0, attrIdx - 4000);
      const beforeAttr = source.substring(sliceStart, attrIdx);

      let bestOpenStart = -1;
      let bestOpenEnd   = -1;
      const openTagRe = /<([A-Za-z][\w.]*)/g;
      let openMatch;

      while ((openMatch = openTagRe.exec(beforeAttr)) !== null) {
        const globalOpenStart = sliceStart + openMatch.index;
        if (source[globalOpenStart + 1] === "/") continue;

        const openEnd = findJsxOpeningTagEnd(source, globalOpenStart);
        if (openEnd === -1) continue;
        if (attrIdx < globalOpenStart || attrIdx >= openEnd) continue;

        if (globalOpenStart > bestOpenStart) {
          bestOpenStart = globalOpenStart;
          bestOpenEnd   = openEnd;
        }
      }

      if (bestOpenStart === -1) continue;

      const range = findJsxElementRangeAtOpenTag(source, bestOpenStart);
      if (range) return range;
    }
  }

  return null;
};

const TERNARY_EXPR_RE =
    /\{\s*([^?{]+)\?\s*(('(?:\\'|[^'])*'|"(?:\\"|[^"])*"))\s*:\s*(('(?:\\'|[^'])*'|"(?:\\"|[^"])*"))\s*\}/;

const patchInnerTextContent = (inner, newText) => {
  if (inner == null || newText == null) return null;

  const ternaryInInner = TERNARY_EXPR_RE.exec(inner);
  if (ternaryInInner) {
    const falseBranch = ternaryInInner[4];
    return inner.replace(falseBranch, `'${escapeSingleQuoted(newText)}'`);
  }

  const trimmed = inner.trim();
  const bracedString = trimmed.match(/^\{\s*(['"])(.*?)\1\s*\}$/);
  if (bracedString) {
    const quote = bracedString[1];
    return inner.replace(bracedString[0], `{${quote}${escapeSingleQuoted(newText)}${quote}}`);
  }

  // Plain text only (typical <label>…</label> body)
  if (!trimmed.includes("<") && !trimmed.includes("{")) {
    return newText;
  }

  return null;
};

const TEXT_CONTENT_TAGS = new Set([
  "label", "span", "p", "a", "button",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "li", "td", "th", "strong", "em", "b", "i", "small", "legend", "figcaption",
  "formlabel",
]);

const isTextContentTagName = (name) => {
  if (!name) return false;
  const lower = String(name).toLowerCase();
  return TEXT_CONTENT_TAGS.has(lower) || lower.endsWith("label");
};

/**
 * Collect every JSX open-tag range for a given component/tag name (case-sensitive).
 */
const collectJsxRangesByTagName = (source, tagName) => {
  if (!source || !tagName) return [];
  const ranges = [];
  const openRe = new RegExp(`<${escapeRegex(tagName)}[\\s>/]`, "g");
  let match;
  while ((match = openRe.exec(source)) !== null) {
    if (source[match.index + 1] === "/") continue;
    const range = findJsxElementRangeAtOpenTag(source, match.index);
    if (range) ranges.push(range);
    // Avoid zero-width loops
    if (openRe.lastIndex <= match.index) openRe.lastIndex = match.index + 1;
  }
  return ranges;
};

/**
 * When data-element-id is not yet written into source, patch exactly one tag
 * occurrence using old text / sibling index — never the first shared className match.
 */
const patchTextByTagOccurrence = (sourceContents, {
  dataElementId,
  newText,
  oldText,
  tagName,
  siblingIndex,
  nthChild,
} = {}) => {
  if (!sourceContents || newText == null) return sourceContents;

  const rawTag = String(tagName || "label");
  const lower = rawTag.toLowerCase();
  const pascal = lower.charAt(0).toUpperCase() + lower.slice(1);
  const candidates = [...new Set(
      lower === "label"
          ? ["Label", "label", "FormLabel"]
          : [rawTag, pascal, lower]
  )];

  let ranges = [];
  candidates.forEach((name) => {
    ranges = ranges.concat(collectJsxRangesByTagName(sourceContents, name));
  });
  // De-dupe by openStart
  ranges = ranges
      .filter((r, i, arr) => arr.findIndex((x) => x.openStart === r.openStart) === i)
      .sort((a, b) => a.openStart - b.openStart);

  if (!ranges.length) return sourceContents;

  const oldSnippet = String(oldText || "").trim();
  let target = null;

  // Unique tag in file — safe to patch that one node
  if (ranges.length === 1) {
    target = ranges[0];
  }

  if (!target && oldSnippet.length > 0) {
    const textMatches = ranges.filter((range) => {
      const inner = sourceContents.substring(range.openEnd, range.closeStart);
      const plain = inner.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
      return plain === oldSnippet || inner.includes(oldSnippet);
    });
    // Only safe when exactly one Label still has the old text
    if (textMatches.length === 1) target = textMatches[0];
  }

  if (!target && Number.isInteger(siblingIndex) && siblingIndex >= 0 && siblingIndex < ranges.length) {
    target = ranges[siblingIndex];
  }

  if (!target && Number.isInteger(nthChild) && nthChild >= 0 && nthChild < ranges.length) {
    target = ranges[nthChild];
  }

  // Still ambiguous — refuse rather than patch the first Label
  if (!target) {
    console.warn("[patchTextByTagOccurrence] Ambiguous label target — refusing patch", {
      dataElementId, tagName, siblingIndex, oldText: oldSnippet?.slice?.(0, 40),
      candidates: ranges.length,
    });
    return sourceContents;
  }

  const openTag = sourceContents.substring(target.openStart, target.openEnd);
  const inner = sourceContents.substring(target.openEnd, target.closeStart);

  // Never wipe nested instrumented siblings inside this range
  if (/data-element-id\s*=/.test(inner)) {
    const nested = patchInnerTextContent(inner, newText);
    if (nested == null || nested === inner) return sourceContents;
    const withIdOpen = dataElementId
        ? upsertJsxAttribute(openTag, "data-element-id", dataElementId)
        : openTag;
    return (
        sourceContents.substring(0, target.openStart) +
        withIdOpen +
        nested +
        sourceContents.substring(target.closeStart)
    );
  }

  let patchedInner = patchInnerTextContent(inner, newText);
  if (patchedInner == null && isTextContentTagName(target.componentName)) {
    patchedInner = newText;
  }
  if (patchedInner == null || patchedInner === inner) return sourceContents;

  const withIdOpen = dataElementId
      ? upsertJsxAttribute(openTag, "data-element-id", dataElementId)
      : openTag;

  return (
      sourceContents.substring(0, target.openStart) +
      withIdOpen +
      patchedInner +
      sourceContents.substring(target.closeStart)
  );
};

/**
 * Patch ONLY the JSX element that has this data-element-id.
 * Never touches sibling labels / shared string literals elsewhere in the file.
 */
const patchTextByElementId = (sourceContents, elementId, newText) => {
  if (!elementId || newText == null) return sourceContents;

  const range = findJsxElementRangeByDataElementId(sourceContents, elementId);
  if (!range || range.selfClosing) return sourceContents;

  const openTag = sourceContents.substring(range.openStart, range.openEnd);
  const inner = sourceContents.substring(range.openEnd, range.closeStart);
  const tagMatch = openTag.match(/^<([A-Za-z][\w.]*)\b/);
  const tagName = tagMatch ? tagMatch[1].toLowerCase() : "";

  // Nested instrumented children — only try safe inner-text patterns, never wipe children
  if (/data-element-id\s*=/.test(inner)) {
    const patchedNested = patchInnerTextContent(inner, newText);
    if (patchedNested == null || patchedNested === inner) return sourceContents;
    return (
        sourceContents.substring(0, range.openEnd) +
        patchedNested +
        sourceContents.substring(range.closeStart)
    );
  }

  let patchedInner = patchInnerTextContent(inner, newText);

  // <label data-element-id="…">Old</label>  → replace that body only
  if (patchedInner == null && isTextContentTagName(tagName)) {
    patchedInner = newText;
  }

  if (patchedInner == null || patchedInner === inner) return sourceContents;

  return (
      sourceContents.substring(0, range.openEnd) +
      patchedInner +
      sourceContents.substring(range.closeStart)
  );
};

const collectElementIds = (eventData, fp) => {
  const primary = resolveDataElementId(eventData, fp);
  if (primary) return [primary];

  return [...new Set(
      [
        eventData?.elementId,
        eventData?.componentId,
        eventData?.runtimeId,
        fp?.elementId,
        fp?.runtimeId,
        eventData?.fingerprint?.elementId,
      ].filter(Boolean)
  )];
};

const collectFingerprintMarkers = (fp) => {
  if (!fp) return [];

  const markers = new Set();

  (fp.fullClassName || "")
      .split(/\s+/)
      .filter((t) => t.length > 2)
      .forEach((t) => markers.add(t));

  (fp.className || "")
      .split(/\s+/)
      .filter((t) => t.length > 2)
      .forEach((t) => markers.add(t));

  (fp.ancestorPath || []).forEach((ancestor) => {
    if (ancestor?.className?.length > 2) markers.add(ancestor.className);
    if (ancestor?.id) markers.add(ancestor.id);
  });

  if (fp.nativeId && !String(fp.nativeId).startsWith("rt-")) {
    markers.add(fp.nativeId);
  }

  return [...markers];
};

const findJsxElementRangeAtOpenTag = (source, openStart) => {
  const tagMatch = source.substring(openStart).match(/^<([A-Za-z][\w.]*)\b/);
  if (!tagMatch) return null;

  const componentName = tagMatch[1];
  const openEnd = findJsxOpeningTagEnd(source, openStart);
  if (openEnd === -1) return null;

  const openTag = source.substring(openStart, openEnd);
  if (openTag.trimEnd().endsWith("/>")) {
    return {
      openStart,
      openEnd,
      closeStart: openEnd,
      closeEnd: openEnd,
      componentName,
      selfClosing: true,
    };
  }

  const closeRange = findJsxElementCloseRange(source, openStart, openEnd, componentName);
  if (!closeRange) return null;

  return {
    openStart,
    openEnd,
    closeStart: closeRange.closeStart,
    closeEnd: closeRange.closeEnd,
    componentName,
    selfClosing: false,
  };
};

const findJsxElementRangeNearMarker = (source, markerIdx, tagPat) => {
  const sliceStart = Math.max(0, markerIdx - 1200);
  const sliceEnd   = Math.min(source.length, markerIdx + 400);
  const slice      = source.substring(sliceStart, sliceEnd);

  let bestOpenStart = -1;
  let bestOpenEnd   = -1;
  const openTagRe = new RegExp(`<(${tagPat})\\b`, "gi");
  let openMatch;

  while ((openMatch = openTagRe.exec(slice)) !== null) {
    const globalOpenStart = sliceStart + openMatch.index;
    if (source[globalOpenStart + 1] === "/") continue;

    const openEnd = findJsxOpeningTagEnd(source, globalOpenStart);
    if (openEnd === -1) continue;
    if (markerIdx < globalOpenStart || markerIdx >= openEnd) continue;

    if (globalOpenStart > bestOpenStart) {
      bestOpenStart = globalOpenStart;
      bestOpenEnd   = openEnd;
    }
  }

  if (bestOpenStart === -1) return null;
  return findJsxElementRangeAtOpenTag(source, bestOpenStart);
};

const findJsxElementRangeContainingText = (source, textIdx, tagPat) => {
  const sliceStart = Math.max(0, textIdx - 4000);
  const before = source.substring(sliceStart, textIdx);

  let bestOpenStart = -1;
  const openTagRe = new RegExp(`<(${tagPat})\\b`, "gi");
  let openMatch;

  while ((openMatch = openTagRe.exec(before)) !== null) {
    const globalOpenStart = sliceStart + openMatch.index;
    if (source[globalOpenStart + 1] === "/") continue;

    const range = findJsxElementRangeAtOpenTag(source, globalOpenStart);
    if (!range) continue;
    if (textIdx >= range.openEnd && textIdx < range.closeStart && globalOpenStart > bestOpenStart) {
      bestOpenStart = globalOpenStart;
    }
  }

  if (bestOpenStart === -1) return null;
  return findJsxElementRangeAtOpenTag(source, bestOpenStart);
};

const openingTagMatchesFingerprint = (openTag, fp) => {
  if (!openTag || !fp) return false;

  const markers = collectFingerprintMarkers(fp);
  for (const marker of markers) {
    if (marker.length > 2 && openTag.includes(marker)) return true;

    if (!marker.startsWith("_") && !marker.includes(".") && openTag.includes(`styles.${marker}`)) {
      return true;
    }

    if (marker.startsWith("_")) {
      const moduleKeyMatch = marker.match(/^_([A-Za-z][\w]*)_/);
      if (moduleKeyMatch && openTag.includes(`styles.${moduleKeyMatch[1]}`)) return true;
    }
  }

  return false;
};

const findJsxElementRangeByFingerprint = (source, fp) => {
  if (!fp) return null;

  const tagPat   = getTagNamePattern(fp.tagName || "div");
  const markers  = collectFingerprintMarkers(fp);
  const moduleTokens = markers.filter((t) => t.startsWith("_"));
  const plainTokens  = markers.filter((t) => !t.startsWith("_"));
  const orderedMarkers = [...moduleTokens, ...plainTokens];

  const tryMarker = (marker) => {
    if (!marker || marker.length < 2) return null;
    let searchFrom = 0;
    while (searchFrom < source.length) {
      const idx = source.indexOf(marker, searchFrom);
      if (idx === -1) break;
      searchFrom = idx + marker.length;
      const range = findJsxElementRangeNearMarker(source, idx, tagPat);
      if (range) return range;
    }
    return null;
  };

  for (const marker of orderedMarkers) {
    const range = tryMarker(marker);
    if (range) return range;

    if (!marker.startsWith("_") && !marker.includes(".")) {
      const byStyles = tryMarker(`styles.${marker}`);
      if (byStyles) return byStyles;
    }

    if (marker.startsWith("_")) {
      const moduleKeyMatch = marker.match(/^_([A-Za-z][\w]*)_/);
      if (moduleKeyMatch) {
        const byStyles = tryMarker(`styles.${moduleKeyMatch[1]}`);
        if (byStyles) return byStyles;
      }
    }
  }

  if (fp.textSnippet && fp.textSnippet.length > 3) {
    const textNeedles = [fp.textSnippet.trim()];
    const firstLine = textNeedles[0].split(/\s+/).slice(0, 4).join(" ");
    if (firstLine.length > 3 && firstLine !== textNeedles[0]) textNeedles.push(firstLine);

    for (const needle of textNeedles) {
      let searchFrom = 0;
      while (searchFrom < source.length) {
        const idx = source.indexOf(needle, searchFrom);
        if (idx === -1) break;
        searchFrom = idx + needle.length;

        const inBody = findJsxElementRangeContainingText(source, idx, tagPat);
        if (inBody) return inBody;

        const inOpenTag = findJsxElementRangeNearMarker(source, idx, tagPat);
        if (inOpenTag) return inOpenTag;
      }
    }
  }

  return null;
};

const resolvePatchTargetRange = (sourceContents, fingerprint, dataElementId) => {
  // data-element-id always wins — fingerprint className matching picks the FIRST
  // shared Label/button class and was rewriting sibling labels.
  if (dataElementId) {
    const byId = findJsxElementRangeByDataElementId(sourceContents, dataElementId);
    if (byId) return byId;
  }

  return findJsxElementRangeByFingerprint(sourceContents, fingerprint);
};

const sourceContainsPatchTarget = (sourceContents, fingerprint, dataElementId) =>
    Boolean(resolvePatchTargetRange(sourceContents, fingerprint, dataElementId));

const getElementRangesToTry = (sourceContents, elementIds, fp) => {
  const ranges = [];

  for (const id of elementIds) {
    const byId = findJsxElementRangeByDataElementId(sourceContents, id);
    if (byId) ranges.push(byId);
  }

  if (ranges.length > 0) return ranges;

  const byFp = findJsxElementRangeByFingerprint(sourceContents, fp);
  if (byFp) ranges.push(byFp);

  return ranges;
};

const patchOpeningTag = (openTag, mutator) => {
  const next = mutator(openTag);
  return next && next !== openTag ? next : null;
};

const removeJsxAttribute = (openTag, attr) => {
  let result = openTag;
  const attrName = escapeRegex(attr);
  const stringAttrRe = new RegExp(
      `\\s*${attrName}=(?:"[^"]*"|'[^']*')`,
      "g"
  );
  result = result.replace(stringAttrRe, "");

  const jsxAttrStart = new RegExp(`\\s*${attrName}=\\{`, "g");
  let match;
  while ((match = jsxAttrStart.exec(result)) !== null) {
    const braceStart = match.index + match[0].length - 1;
    let depth = 0;
    let end = -1;
    for (let i = braceStart; i < result.length; i++) {
      if (result[i] === "{") depth++;
      else if (result[i] === "}") {
        depth--;
        if (depth === 0) {
          end = i + 1;
          break;
        }
      }
    }
    if (end !== -1) {
      result = result.slice(0, match.index) + result.slice(end);
      jsxAttrStart.lastIndex = match.index;
    } else {
      break;
    }
  }

  return result;
};

const stripJsxAttribute = (openTag, attr) => {
  let result = openTag;
  let prev;
  do {
    prev = result;
    result = removeJsxAttribute(result, attr);
  } while (result !== prev);
  return result;
};

const upsertJsxAttribute = (openTag, attr, value, { jsxExpression = false } = {}) => {
  const formattedValue =
      jsxExpression && String(value).trim().startsWith("{")
          ? String(value)
          : jsxExpression
              ? `{${value}}`
              : `"${String(value).replace(/"/g, '\\"')}"`;

  const withoutAttr = stripJsxAttribute(openTag, attr);
  return withoutAttr.replace(/\s*\/?>$/, ` ${attr}=${formattedValue}$&`);
};

const camelCaseCssProp = (prop) =>
    prop.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());

const parseInlineStyleString = (styleText) => {
  if (!styleText) return null;
  const obj = {};
  styleText.split(";").forEach((rule) => {
    const colon = rule.indexOf(":");
    if (colon === -1) return;
    const key = camelCaseCssProp(rule.slice(0, colon));
    const val = rule.slice(colon + 1).trim();
    if (key && val) obj[key] = val;
  });
  return Object.keys(obj).length ? obj : null;
};

const extractInlineStyleFromHtml = (html) => {
  if (!html) return null;
  const match = html.match(/\bstyle="([^"]*)"/i);
  return match ? parseInlineStyleString(match[1]) : null;
};

const extractClassFromHtml = (html) => {
  if (!html) return null;
  const match = html.match(/\bclass(?:Name)?="([^"]*)"/i);
  return match ? match[1].trim() : null;
};

const formatJsxStyleValue = (styleObj) => {
  const body = Object.entries(styleObj)
      .map(([key, value]) => `${key}: '${String(value).replace(/'/g, "\\'")}'`)
      .join(", ");
  // Must be double-brace JSX: style={{ ... }} — single brace is invalid.
  return `{{ ${body} }}`;
};

const mergeStyleObjects = (existing, incoming) => ({ ...existing, ...incoming });

const parseExistingJsxStyleObject = (openTag) => {
  const match = openTag.match(/\bstyle=\{(\{[\s\S]*?\})\}/);
  if (!match) return {};

  try {
    const normalized = match[1]
        .replace(/(\w+):/g, '"$1":')
        .replace(/'/g, '"');
    return JSON.parse(normalized);
  } catch {
    return {};
  }
};

const patchOpeningTagByRanges = (sourceContents, ranges, mutator, dataElementId = null) => {
  for (const range of ranges) {
    const openTag = sourceContents.substring(range.openStart, range.openEnd);
    let patched = patchOpeningTag(openTag, mutator);
    if (!patched) continue;

    if (dataElementId) {
      patched = upsertJsxAttribute(patched, "data-element-id", dataElementId);
    }

    return (
        sourceContents.substring(0, range.openStart) +
        patched +
        sourceContents.substring(range.openEnd)
    );
  }
  return sourceContents;
};

const patchTextInDescendants = (sourceContents, parentRange, newText) => {
  if (!parentRange || parentRange.selfClosing) return sourceContents;

  const block = sourceContents.substring(parentRange.openStart, parentRange.closeEnd);
  const childIdRe = /data-element-id=["']([^"']+)["']/g;
  let childMatch;

  while ((childMatch = childIdRe.exec(block)) !== null) {
    const childId = childMatch[1];
    const result  = patchTextByElementId(sourceContents, childId, newText);
    if (result !== sourceContents) return result;
  }

  const ternaryMatch = TERNARY_EXPR_RE.exec(block);
  if (ternaryMatch) {
    const falseBranch  = ternaryMatch[4];
    const updatedBlock = block.replace(
        falseBranch,
        `'${escapeSingleQuoted(newText)}'`
    );
    if (updatedBlock !== block) {
      return (
          sourceContents.substring(0, parentRange.openStart) +
          updatedBlock +
          sourceContents.substring(parentRange.closeEnd)
      );
    }
  }

  const inner = sourceContents.substring(parentRange.openEnd, parentRange.closeStart);
  const patchedInner = patchInnerTextContent(inner, newText);
  if (patchedInner !== null && patchedInner !== inner) {
    return (
        sourceContents.substring(0, parentRange.openEnd) +
        patchedInner +
        sourceContents.substring(parentRange.closeStart)
    );
  }

  return sourceContents;
};

const patchTextNearMarkers = (sourceContents, markers, newText) => {
  for (const marker of markers) {
    const idx = sourceContents.indexOf(marker);
    if (idx === -1) continue;

    const sliceStart = Math.max(0, idx - 300);
    const sliceEnd   = Math.min(sourceContents.length, idx + 1500);
    const slice      = sourceContents.substring(sliceStart, sliceEnd);

    const ternaryMatch = TERNARY_EXPR_RE.exec(slice);
    if (ternaryMatch) {
      const falseBranch = ternaryMatch[4];
      const patchedExpr = ternaryMatch[0].replace(
          falseBranch,
          `'${escapeSingleQuoted(newText)}'`
      );
      const globalIdx = sliceStart + ternaryMatch.index;
      return (
          sourceContents.substring(0, globalIdx) +
          patchedExpr +
          sourceContents.substring(globalIdx + ternaryMatch[0].length)
      );
    }

    const literalRe = /(['"])([^'"]{2,})\1/g;
    let literalMatch;
    let bestLiteral = null;
    let bestScore   = 0;

    while ((literalMatch = literalRe.exec(slice)) !== null) {
      const literalText = literalMatch[2];
      if (literalText === newText) continue;

      let score = 0;
      if (newText.startsWith(literalText)) score = literalText.length;
      else if (literalText.startsWith(newText)) score = newText.length;
      else if (newText.includes(literalText) || literalText.includes(newText)) {
        score = Math.min(literalText.length, newText.length);
      }

      if (score > bestScore) {
        bestScore   = score;
        bestLiteral = literalMatch;
      }
    }

    if (bestLiteral && bestScore >= 3) {
      const replacement = `${bestLiteral[1]}${newText}${bestLiteral[1]}`;
      const globalIdx   = sliceStart + bestLiteral.index;
      return (
          sourceContents.substring(0, globalIdx) +
          replacement +
          sourceContents.substring(globalIdx + bestLiteral[0].length)
      );
    }
  }

  return sourceContents;
};

const patchTextBySimilarLiteral = (sourceContents, newText) => {
  if (!newText) return sourceContents;

  const re = new RegExp(TERNARY_EXPR_RE.source, "g");
  let match;
  let bestMatch = null;
  let bestScore = 0;

  while ((match = re.exec(sourceContents)) !== null) {
    const falseBranch = match[4];
    const falseText   = falseBranch.slice(1, -1).replace(/\\'/g, "'");

    if (newText === falseText) continue;

    let score = 0;
    if (newText.startsWith(falseText)) score = falseText.length;
    else if (falseText.startsWith(newText)) score = newText.length;
    else if (newText.includes(falseText) || falseText.includes(newText)) {
      score = Math.min(falseText.length, newText.length);
    }

    if (score > bestScore) {
      bestScore = score;
      bestMatch = match;
    }
  }

  if (!bestMatch || bestScore < 3) return sourceContents;

  const falseBranch = bestMatch[4];
  const replacement = `'${escapeSingleQuoted(newText)}'`;
  const patchedExpr = bestMatch[0].replace(falseBranch, replacement);

  return (
      sourceContents.substring(0, bestMatch.index) +
      patchedExpr +
      sourceContents.substring(bestMatch.index + bestMatch[0].length)
  );
};

const applyTextSemanticPatch = (sourceContents, eventData, fp, elementIds, markers) => {
  const newText =
      eventData.value ??
      eventData.textContent ??
      fp?.textSnippet ??
      extractTextFromChangedElement(eventData.changedElement);

  if (newText == null) return sourceContents;

  const dataElementId = resolveDataElementId(eventData, fp);
  const oldText =
      fp?.textSnippet ||
      eventData?.fingerprint?.textSnippet ||
      eventData?.previousText ||
      null;
  const tagName =
      eventData?.mapTagName ||
      fp?.tagName ||
      eventData?.tagName ||
      "label";

  // When we know the data-element-id, ONLY patch that element — never fuzzy-match
  // sibling <label> text elsewhere in the file.
  if (dataElementId) {
    const byId = patchTextByElementId(sourceContents, dataElementId, newText);
    if (byId !== sourceContents) return byId;

    // Id not yet in source — pin one Label by old text / sibling index, then write the id
    return patchTextByTagOccurrence(sourceContents, {
      dataElementId,
      newText,
      oldText,
      tagName,
      siblingIndex: fp?.siblingIndex,
      nthChild: fp?.nthChild,
    });
  }

  const ids = elementIds?.length ? elementIds : [];
  for (const id of ids) {
    const direct = patchTextByElementId(sourceContents, id, newText);
    if (direct !== sourceContents) return direct;
  }

  // No stable id — still avoid className fuzzy matching for labels
  if (isTextContentTagName(tagName)) {
    const byOccurrence = patchTextByTagOccurrence(sourceContents, {
      dataElementId: null,
      newText,
      oldText,
      tagName,
      siblingIndex: fp?.siblingIndex,
      nthChild: fp?.nthChild,
    });
    if (byOccurrence !== sourceContents) return byOccurrence;
  }

  // Last-resort fuzzy paths only when we truly have no id and occurrence failed
  const { ranges } = getPatchTargetRanges(sourceContents, eventData, fp);
  for (const range of ranges) {
    const descendant = patchTextInDescendants(sourceContents, range, newText);
    if (descendant !== sourceContents) return descendant;
  }

  const nearMarkers = patchTextNearMarkers(sourceContents, markers, newText);
  if (nearMarkers !== sourceContents) return nearMarkers;

  const literalPatch = patchTextBySimilarLiteral(sourceContents, newText);
  if (literalPatch !== sourceContents) return literalPatch;

  return sourceContents;
};

const applyStyleSemanticPatch = (sourceContents, eventData, fp, elementIds) => {
  const styleValue =
      eventData.value ??
      eventData.style ??
      extractInlineStyleFromHtml(eventData.changedElement);

  if (!styleValue) return sourceContents;

  let styleObj =
      typeof styleValue === "string"
          ? parseInlineStyleString(styleValue)
          : { ...styleValue };

  if (!styleObj || !Object.keys(styleObj).length) return sourceContents;

  // Lucide / SVG icons: map color → color (currentColor) so stroke picks it up
  if (
      (fp?.tagName === "svg" || eventData?.tagName === "svg" || eventData?.isIconUpdate) &&
      styleObj.color &&
      !styleObj.stroke
  ) {
    // keep color; Lucide uses stroke="currentColor"
  }

  const { ranges, dataElementId } = getPatchTargetRanges(sourceContents, eventData, fp);

  return patchOpeningTagByRanges(sourceContents, ranges, (openTag) => {
    const existing = parseExistingJsxStyleObject(openTag);
    const merged   = mergeStyleObjects(existing, styleObj);
    return upsertJsxAttribute(openTag, "style", formatJsxStyleValue(merged), {
      jsxExpression: true,
    });
  }, dataElementId);
};

const applyClassNameSemanticPatch = (sourceContents, eventData, fp, elementIds) => {
  const classValue =
      eventData.value ??
      eventData.className ??
      extractClassFromHtml(eventData.changedElement);

  if (!classValue) return sourceContents;

  const { ranges, dataElementId } = getPatchTargetRanges(sourceContents, eventData, fp);

  return patchOpeningTagByRanges(sourceContents, ranges, (openTag) => {
    if (/className=\{styles\.[^}]+\}/.test(openTag)) {
      return openTag;
    }
    return upsertJsxAttribute(openTag, "className", classValue);
  }, dataElementId);
};

const ATTRIBUTE_PROPERTY_MAP = {
  src: "src",
  href: "href",
  alt: "alt",
  title: "title",
  type: "type",
  placeholder: "placeholder",
  name: "name",
  value: "value",
  disabled: "disabled",
  readOnly: "readOnly",
  checked: "checked",
  imageContent: "src",
};

const applyAttributeSemanticPatch = (sourceContents, eventData, fp, elementIds, property) => {
  const attrName = ATTRIBUTE_PROPERTY_MAP[property] || property;
  let attrValue  = eventData.value;

  if (attrValue === undefined || attrValue === null) {
    const html = eventData.changedElement || "";
    const attrMatch = html.match(new RegExp(`\\b${attrName}=["']([^"']*)["']`, "i"));
    attrValue = attrMatch ? attrMatch[1] : attrValue;
  }

  if (attrValue === undefined || attrValue === null) return sourceContents;

  const { ranges, dataElementId } = getPatchTargetRanges(sourceContents, eventData, fp);
  const isBooleanAttr = attrName === "disabled" || attrName === "readOnly" || attrName === "checked";

  return patchOpeningTagByRanges(sourceContents, ranges, (openTag) => {
    if (isBooleanAttr) {
      const enabled = attrValue === true || attrValue === "true" || attrValue === attrName;
      return enabled
          ? upsertJsxAttribute(openTag, attrName, "true", { jsxExpression: true })
          : removeJsxAttribute(openTag, attrName);
    }

    return upsertJsxAttribute(openTag, attrName, String(attrValue));
  }, dataElementId);
};

const normalizeSemanticProperty = (property) => {
  if (!property) return null;

  const key = String(property);
  if (key === "text" || key === "innerText" || key === "textContent") return "textContent";
  if (key === "class" || key === "className") return "className";
  if (key === "style" || key === "filter") return "style";
  if (ATTRIBUTE_PROPERTY_MAP[key]) return key;

  return key;
};

const inferUpdateProperty = (eventData) => {
  if (eventData?.property) return eventData.property;
  if (eventData?.changedProperty) return eventData.changedProperty;

  const html = eventData?.changedElement || "";

  if (/\bstyle="/i.test(html)) return "style";
  if (/\bclass(?:Name)?="/i.test(html)) return "className";
  if (/\bsrc="/i.test(html)) return "src";
  if (/\bhref="/i.test(html)) return "href";
  if (/\btype="/i.test(html)) return "type";
  if (/\bdisabled\b/i.test(html)) return "disabled";

  const text = extractTextFromChangedElement(html);
  if (text) return "textContent";

  return null;
};

const applySemanticPatch = (sourceContents, eventData, fp) => {
  const property   = normalizeSemanticProperty(inferUpdateProperty(eventData));
  const elementIds   = collectElementIds(eventData, fp);
  const markers      = collectFingerprintMarkers(fp);

  if (property === "textContent") {
    return applyTextSemanticPatch(sourceContents, eventData, fp, elementIds, markers);
  }

  if (property === "style") {
    const stylePatch = applyStyleSemanticPatch(sourceContents, eventData, fp, elementIds);
    if (stylePatch !== sourceContents) return stylePatch;
  }

  if (property === "className") {
    const classPatch = applyClassNameSemanticPatch(sourceContents, eventData, fp, elementIds);
    if (classPatch !== sourceContents) return classPatch;
  }

  if (
      ATTRIBUTE_PROPERTY_MAP[property] ||
      ["type", "placeholder", "name", "value", "title", "alt", "href", "src", "disabled", "readOnly", "checked", "imageContent"].includes(property)
  ) {
    const attrPatch = applyAttributeSemanticPatch(sourceContents, eventData, fp, elementIds, property);
    if (attrPatch !== sourceContents) return attrPatch;
  }

  return applyTextSemanticPatch(sourceContents, eventData, fp, elementIds, markers);
};

/**
 * Canonical edit → editedCodes pipeline:
 *   1. componentId / elementId (data-element-id)
 *   2. filePath (element-map / selection / runtime map)
 *   3. load fresh source (session editedCodes if already pasted, else remote — never appCodes)
 *   4. semantic patch → paste into editedCodes
 */
const patchEditedCodeByComponentId = async ({
                                              componentId,
                                              filePath,
                                              property,
                                              value,
                                              fingerprint,
                                              eventData,
                                              editedCodes = [],
                                              appCodes = [],
                                              runtimeIdToFile,
                                              projectId,
                                              userId,
                                              settingsTab,
                                              activePagePath,
                                              mapTagName,
                                              previousText,
                                            }) => {
  const elementId =
      componentId ||
      resolveDataElementId(eventData || {}, fingerprint || {});

  if (!elementId) {
    console.warn("[patchEditedCodeByComponentId] missing component id");
    return {
      updatedCodes: editedCodes,
      patched: false,
      fileToUpdate: null,
      elementId: null,
      reason: "missing-component-id",
    };
  }

  const preferredPath =
      resolveMessageFilePath(filePath) ||
      resolveMessageFilePath(eventData?.filePath) ||
      runtimeIdToFile?.get(elementId) ||
      null;

  let sourceResult = null;

  // 2–3. elementId → filePath → fresh code (no appCodes cache)
  if (preferredPath) {
    sourceResult = await loadSourceFileByPath({
      filePath: preferredPath,
      editedCodes,
      appCodes: [],
      projectId,
      userId,
      settingsTab,
      forceFresh: true,
      useSessionEdits: true,
    });
  }

  // Last resort fingerprint search — only over session editedCodes, not stale appCodes
  if (!sourceResult) {
    const fpForFind = {
      ...(fingerprint || {}),
      elementId,
      componentId: elementId,
      runtimeId: elementId,
    };
    sourceResult = findSourceFile([], editedCodes, fpForFind, {
      activePagePath,
      preferJsx: fpForFind.tagName === "svg",
    });
  }

  // Absolute last resort: scan appCodes only if remote+session both failed
  if (!sourceResult && Array.isArray(appCodes) && appCodes.length) {
    const fpForFind = {
      ...(fingerprint || {}),
      elementId,
      componentId: elementId,
      runtimeId: elementId,
    };
    sourceResult = findSourceFile(appCodes, editedCodes, fpForFind, {
      activePagePath,
      preferJsx: fpForFind.tagName === "svg",
    });
  }

  if (!sourceResult?.contents && sourceResult?.contents !== "") {
    console.warn("[patchEditedCodeByComponentId] source not found", {
      elementId,
      preferredPath,
      property,
    });
    return {
      updatedCodes: editedCodes,
      patched: false,
      fileToUpdate: null,
      elementId,
      reason: "source-not-found",
      filePath: preferredPath,
    };
  }

  const fileToUpdate = sourceResult.name;
  runtimeIdToFile?.set(elementId, fileToUpdate);

  // 4. Patch source for this component / element id
  const patchPayload = {
    ...(eventData || {}),
    elementId,
    componentId: elementId,
    runtimeId: elementId,
    property,
    value,
    mapTagName: mapTagName || eventData?.mapTagName || null,
    previousText:
        previousText ??
        eventData?.previousText ??
        fingerprint?.textSnippet ??
        null,
    filePath: preferredPath || fileToUpdate,
  };

  const fp = {
    ...(fingerprint || {}),
    elementId,
    componentId: elementId,
    runtimeId: elementId,
    textSnippet:
        previousText ||
        eventData?.previousText ||
        fingerprint?.textSnippet ||
        "",
  };

  const updatedSource = applySemanticPatch(sourceResult.contents, patchPayload, fp);
  const patched = updatedSource !== sourceResult.contents;

  if (!patched) {
    console.warn("[patchEditedCodeByComponentId] patch produced no change", {
      elementId,
      fileToUpdate,
      property,
    });
  } else {
    console.info("[patchEditedCodeByComponentId] patched", {
      elementId,
      fileToUpdate,
      property,
    });
  }

  // Paste updated code into editedCodes (replace any prior entry for this path)
  const updatedCodes = editedCodes.some(
      (c) => c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
  )
      ? editedCodes.map((c) =>
          c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
              ? { ...c, name: fileToUpdate, contents: updatedSource, isPending: false }
              : c
      )
      : [...editedCodes, { name: fileToUpdate, contents: updatedSource }];

  return {
    updatedCodes,
    patched,
    fileToUpdate,
    elementId,
    filePath: fileToUpdate,
    reason: patched ? "ok" : "patch-noop",
  };
};

const mergeEditedCodesIntoState = (editedCodesRef, setState, patchResult, selectedPatch = null) => {
  if (!patchResult) return;
  editedCodesRef.current = patchResult.updatedCodes;
  setState((prev) => {
    const nextSelected = prev.selectedElement
        ? {
          ...prev.selectedElement,
          ...(selectedPatch || {}),
          filePath:
              selectedPatch?.filePath ||
              patchResult.fileToUpdate ||
              prev.selectedElement.filePath ||
              null,
        }
        : prev.selectedElement;

    return {
      ...prev,
      editedCodes: patchResult.updatedCodes,
      hasUnsavedChanges:
          Boolean(patchResult.patched) ||
          prev.hasUnsavedChanges ||
          (patchResult.updatedCodes?.length > 0),
      selectedElement: nextSelected,
    };
  });
};


const escapeJsStringLiteral = (value) =>
    String(value ?? "")
        .replace(/\\/g, "\\\\")
        .replace(/"/g, '\\"')
        .replace(/\r/g, "\\r")
        .replace(/\n/g, "\\n");

const buildCallOrchestratorExpr = (orchestratorId, query = "") =>
    `callOrchestrator("${escapeJsStringLiteral(orchestratorId)}", "${escapeJsStringLiteral(query)}")`;

/** Extract callOrchestrator(id, query) from a JSX open tag / source snippet. */
const parseCallOrchestratorFromSource = (sourceContents, dataElementId) => {
  if (!sourceContents || !dataElementId) return null;
  const range = findJsxElementRangeByDataElementId(sourceContents, dataElementId);
  if (!range) return null;
  const openTag = sourceContents.substring(range.openStart, range.openEnd);
  const match = openTag.match(
      /callOrchestrator\(\s*["']([^"']+)["']\s*(?:,\s*["']([^"']*)["'])?/
  );
  if (!match) return null;
  return {
    workflowId: String(match[1] || ""),
    query: String(match[2] ?? ""),
  };
};

const ORCHESTRATOR_API_TOKEN_KEY = "ORCHESTRATOR_API_TOKEN";

const getProfileApiToken = () => {
  try {
    const profile = JSON.parse(localStorage.getItem("profile") || "null");
    return profile?.apiToken || "";
  } catch {
    return "";
  }
};

/** Upsert KEY=value in a dotenv-style string. Returns { contents, changed }. */
const upsertEnvVar = (contents, key, value) => {
  const text = String(contents ?? "");
  const line = `${key}=${value}`;
  const re = new RegExp(`^${escapeRegex(key)}=.*$`, "m");
  if (re.test(text)) {
    const next = text.replace(re, line);
    return { contents: next, changed: next !== text };
  }
  const needsNewline = text.length > 0 && !text.endsWith("\n");
  return { contents: `${text}${needsNewline ? "\n" : ""}${line}\n`, changed: true };
};

/**
 * Fetch backend .env, ensure ORCHESTRATOR_API_TOKEN is set, and sync it back.
 * Uses source-view get + source-sync update (develop_mode=backend).
 */
const ensureOrchestratorApiTokenInEnv = async ({ projectId, userId }) => {
  if (!projectId || !userId) return { updated: false, reason: "missing_ids" };

  const apiToken = getProfileApiToken();
  if (!apiToken) {
    console.warn("[ensureOrchestratorApiTokenInEnv] No profile apiToken available");
    return { updated: false, reason: "missing_token" };
  }

  const payload = await getCodeview(projectId, userId, "backend", {
    path: "",
    file: ".env",
  });
  const fileData = extractFilePayload(payload, ".env");
  const currentContents =
      fileData?.content ??
      (typeof payload?.contents === "string" ? payload.contents : null);

  if (currentContents == null) {
    console.warn("[ensureOrchestratorApiTokenInEnv] Could not load backend .env");
    return { updated: false, reason: "env_not_found" };
  }

  const { contents: nextContents, changed } = upsertEnvVar(
      currentContents,
      ORCHESTRATOR_API_TOKEN_KEY,
      apiToken
  );

  if (!changed) {
    return { updated: false, reason: "already_set" };
  }

  const response = await AgentService.syncCode(
      {
        projectId,
        files: [{ name: ".env", contents: nextContents }],
      },
      userId,
      "backend"
  );

  const isSuccess = response?.status === API_RESPONSE.SUCCESS;
  if (!isSuccess) {
    throw new Error(
        response?.errorMessage ||
        response?.message ||
        response?.status ||
        "Failed to update .env with ORCHESTRATOR_API_TOKEN"
    );
  }

  return { updated: true };
};

const isLikelySharedUiKitPath = (filePath) => {
  const p = String(filePath || "").replace(/\\/g, "/").toLowerCase();
  if (!p) return false;
  if (/\/(ui|components\/ui)\/button(\.|\/)/.test(p)) return true;
  if (/\/(ui|shared|components\/ui)\/.*button\.(tsx|jsx|ts|js)$/.test(p)) return true;
  if (/(^|\/)button\.(tsx|jsx)$/.test(p) && !/\/pages?\//.test(p)) return true;
  return false;
};

const injectOnClickHandler = (sourceContents, fingerprint, orchestratorId, query = "") => {
  if (!sourceContents || !orchestratorId) return sourceContents;

  const dataElementId = resolveDataElementId({}, fingerprint);
  if (!dataElementId) {
    console.warn("[injectOnClickHandler] missing data-element-id — refuse fingerprint fallback");
    return sourceContents;
  }

  const callExpr = buildCallOrchestratorExpr(orchestratorId, query);
  const onClickValue = `() => ${callExpr}`;
  const callOrchestratorRe = new RegExp(
      String.raw`callOrchestrator\(\s*["']${escapeRegex(String(orchestratorId))}["'](?:\s*,\s*(?:"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^)]*))?\s*\)`
  );

  // Only patch the exact usage-site tag that owns this data-element-id.
  // Never fall back to fingerprint (that lands in shared Button.jsx).
  const range = findJsxElementRangeByDataElementId(sourceContents, dataElementId);
  if (!range) {
    console.warn("[injectOnClickHandler] data-element-id not in this file:", {
      dataElementId,
      tagName: fingerprint?.tagName,
      className: fingerprint?.className,
    });
    return sourceContents;
  }

  const openTag = sourceContents.substring(range.openStart, range.openEnd);

  if (openTag.includes(callExpr)) {
    let nextOpenTag = openTag;
    if (!/\bdata-element-id\b/.test(nextOpenTag)) {
      nextOpenTag = upsertJsxAttribute(nextOpenTag, "data-element-id", dataElementId);
    }
    if (nextOpenTag === openTag) {
      return withCallOrchestratorImport(sourceContents);
    }
    return withCallOrchestratorImport(
        sourceContents.substring(0, range.openStart) +
        nextOpenTag +
        sourceContents.substring(range.openEnd)
    );
  }

  if (callOrchestratorRe.test(openTag)) {
    let nextOpenTag = openTag.replace(callOrchestratorRe, callExpr);
    if (!/\bdata-element-id\b/.test(nextOpenTag)) {
      nextOpenTag = upsertJsxAttribute(nextOpenTag, "data-element-id", dataElementId);
    }
    if (nextOpenTag === openTag) {
      return withCallOrchestratorImport(sourceContents);
    }
    return withCallOrchestratorImport(
        sourceContents.substring(0, range.openStart) +
        nextOpenTag +
        sourceContents.substring(range.openEnd)
    );
  }

  let patchedOpenTag = stripJsxAttribute(openTag, "onClick");
  patchedOpenTag = upsertJsxAttribute(patchedOpenTag, "onClick", onClickValue, {
    jsxExpression: true,
  });
  patchedOpenTag = upsertJsxAttribute(patchedOpenTag, "data-element-id", dataElementId);

  if (patchedOpenTag === openTag) {
    console.warn("[injectOnClickHandler] Failed to patch opening tag:", { dataElementId });
    return sourceContents;
  }

  return withCallOrchestratorImport(
      sourceContents.substring(0, range.openStart) +
      patchedOpenTag +
      sourceContents.substring(range.openEnd)
  );
};

// ─── replaceInSource ─────────────────────────────────────────────────────────

const replaceInSource = (sourceContents, changedElementJsx, fingerprint) => {
  const { nativeId, className, fullClassName, tagName, elementId } = fingerprint;
  const tagPat = getTagNamePattern(tagName);

  const nestingAwareReplace = (openTagPattern) => {
    const match = openTagPattern.exec(sourceContents);
    if (!match) return sourceContents;

    const openTagStart = match.index;
    const openTagEnd   = openTagStart + match[0].length;

    if (match[0].trimEnd().endsWith("/>")) {
      return (
          sourceContents.substring(0, openTagStart) +
          changedElementJsx +
          sourceContents.substring(openTagEnd)
      );
    }

    const lowerTag = tagName.toLowerCase();
    const openPat  = new RegExp(`<${lowerTag}[\\s>/]`, "gi");
    const closePat = new RegExp(`<\\/${lowerTag}\\s*>`, "gi");

    let depth = 1, pos = openTagEnd, closeEnd = -1;
    while (depth > 0 && pos < sourceContents.length) {
      openPat.lastIndex  = pos;
      closePat.lastIndex = pos;
      const nextOpen  = openPat.exec(sourceContents);
      const nextClose = closePat.exec(sourceContents);
      if (!nextClose) break;
      if (nextOpen && nextOpen.index < nextClose.index) {
        depth++;
        pos = nextOpen.index + nextOpen[0].length;
      } else {
        depth--;
        pos = nextClose.index + nextClose[0].length;
        if (depth === 0) closeEnd = pos;
      }
    }

    if (closeEnd === -1) return sourceContents;
    return (
        sourceContents.substring(0, openTagStart) +
        changedElementJsx +
        sourceContents.substring(closeEnd)
    );
  };

  const nestingAwareReplaceComponent = (openTagPattern) => {
    const match = openTagPattern.exec(sourceContents);
    if (!match) return sourceContents;

    const openTagStart = match.index;
    const openTagEnd   = openTagStart + match[0].length;
    const openTag      = match[0];
    const componentName = (openTag.match(/^<([A-Za-z][\w.]*)/) || [])[1];
    if (!componentName) return sourceContents;

    if (openTag.trimEnd().endsWith("/>")) {
      return (
          sourceContents.substring(0, openTagStart) +
          changedElementJsx +
          sourceContents.substring(openTagEnd)
      );
    }

    const openPat  = new RegExp(`<${escapeRegex(componentName)}[\\s>/]`, "gi");
    const closePat = new RegExp(`<\\/${escapeRegex(componentName)}\\s*>`, "gi");

    let depth = 1, pos = openTagEnd, closeEnd = -1;
    while (depth > 0 && pos < sourceContents.length) {
      openPat.lastIndex  = pos;
      closePat.lastIndex = pos;
      const nextOpen  = openPat.exec(sourceContents);
      const nextClose = closePat.exec(sourceContents);
      if (!nextClose) break;
      if (nextOpen && nextOpen.index < nextClose.index) {
        depth++;
        pos = nextOpen.index + nextOpen[0].length;
      } else {
        depth--;
        pos = nextClose.index + nextClose[0].length;
        if (depth === 0) closeEnd = pos;
      }
    }

    if (closeEnd === -1) return sourceContents;
    return (
        sourceContents.substring(0, openTagStart) +
        changedElementJsx +
        sourceContents.substring(closeEnd)
    );
  };

  // A0: data-element-id (componentId from preview → data-element-id in source)
  if (elementId) {
    const r = nestingAwareReplaceComponent(
        new RegExp(`<[A-Za-z][\\w.]*\\b[^>]*\\bdata-element-id=["']${escapeRegex(elementId)}["'][^>]*>`, "i")
    );
    if (r !== sourceContents) return r;
    console.warn("[replaceInSource] data-element-id not found in source:", elementId);
    return sourceContents;
  }

  // A: replaceComponentById with nativeId
  if (nativeId && !nativeId.startsWith("rt-")) {
    const r = replaceComponentById(sourceContents, nativeId, changedElementJsx);
    if (r !== sourceContents) return r;
  }

  // B: id attribute (nesting-aware)
  if (nativeId && !nativeId.startsWith("rt-")) {
    const r = nestingAwareReplace(
        new RegExp(`<${tagPat}\\b[^>]*\\bid=["']${escapeRegex(nativeId)}["'][^>]*>`, "i")
    );
    if (r !== sourceContents) return r;
  }

  // C: fullClassName — prefer CSS module tokens (e.g. _signInButton_abc)
  if (fullClassName) {
    const tokens = fullClassName.split(/\s+/).filter((c) => c.length > 2);
    const moduleTokens = tokens.filter((t) => t.startsWith("_"));
    const orderedTokens = [...moduleTokens, ...tokens.filter((t) => !t.startsWith("_"))];

    for (const token of orderedTokens) {
      const r = nestingAwareReplaceComponent(
          new RegExp(`<[A-Za-z][\\w.]*\\b[^>]*\\bclassName=["'][^"']*${escapeRegex(token)}[^"']*["'][^>]*>`, "i")
      );
      if (r !== sourceContents) return r;
    }
  }

  // C2: ancestor module class — find button inside parent (e.g. form._form_abc)
  const ancestorPath = fingerprint.ancestorPath || [];
  for (const ancestor of ancestorPath) {
    if (!ancestor?.className?.startsWith("_")) continue;
    const ancIdx = sourceContents.indexOf(ancestor.className);
    if (ancIdx === -1) continue;

    const sliceFromAncestor = sourceContents.substring(ancIdx);
    const btnMatch = new RegExp(`<${tagPat}\\b[^>]*>`, "i").exec(sliceFromAncestor);
    if (!btnMatch) continue;

    const openTag     = btnMatch[0];
    const componentName = (openTag.match(/^<([A-Za-z][\w.]*)/) || [])[1] || tagName;
    const globalStart = ancIdx + btnMatch.index;
    const openTagEnd  = globalStart + openTag.length;
    const openPat     = new RegExp(`<${escapeRegex(componentName)}[\\s>/]`, "gi");
    const closePat    = new RegExp(`<\\/${escapeRegex(componentName)}\\s*>`, "gi");

    let depth = 1, pos = openTagEnd, closeEnd = -1;
    while (depth > 0 && pos < sourceContents.length) {
      openPat.lastIndex  = pos;
      closePat.lastIndex = pos;
      const nextOpen  = openPat.exec(sourceContents);
      const nextClose = closePat.exec(sourceContents);
      if (!nextClose) break;
      if (nextOpen && nextOpen.index < nextClose.index) {
        depth++;
        pos = nextOpen.index + nextOpen[0].length;
      } else {
        depth--;
        pos = nextClose.index + nextClose[0].length;
        if (depth === 0) closeEnd = pos;
      }
    }

    if (closeEnd !== -1) {
      return (
          sourceContents.substring(0, globalStart) +
          changedElementJsx +
          sourceContents.substring(closeEnd)
      );
    }
  }

  // D: first className
  if (className) {
    const r = nestingAwareReplace(
        new RegExp(`<${tagPat}\\b[^>]*\\bclassName=["'][^"']*${escapeRegex(className)}[^"']*["'][^>]*>`, "i")
    );
    if (r !== sourceContents) return r;
  }

  // E: HTML class=
  if (className) {
    const r = nestingAwareReplace(
        new RegExp(`<${tagPat}\\b[^>]*\\bclass=["'][^"']*${escapeRegex(className)}[^"']*["'][^>]*>`, "i")
    );
    if (r !== sourceContents) return r;
  }

  // F: textContent match (try without new text — match tag + any content)
  if (fingerprint.textSnippet && fingerprint.textSnippet.length > 4) {
    const r = nestingAwareReplaceComponent(
        new RegExp(`<[A-Za-z][\\w.]*\\b[^>]*>[^<]*${escapeRegex(fingerprint.textSnippet)}`, "i")
    );
    if (r !== sourceContents) return r;
  }

  // G: semantic tags fallback
  const specificTags = new Set([
    "header","footer","nav","main","aside","section",
    "h1","h2","h3","h4","h5","h6","button","a","img","form","table",
  ]);
  if (tagName && specificTags.has(tagName.toLowerCase())) {
    const r = nestingAwareReplace(new RegExp(`<${tagPat}\\b[^>]*>`, "i"));
    if (r !== sourceContents) return r;

    const r2 = nestingAwareReplaceComponent(
        new RegExp(`<[A-Za-z][\\w.]*\\b[^>]*>`, "i")
    );
    if (r2 !== sourceContents) return r2;
  }

  console.warn("[replaceInSource] No strategy matched:", { nativeId, className, tagName, elementId });
  return sourceContents;
};

const buildSelectionFingerprint = (selectedElement) => {
  const runtimeId = selectedElement?.id;
  const dataElementId =
      selectedElement?.elementId ??
      selectedElement?.fingerprint?.elementId ??
      runtimeId ??
      selectedElement?.componentId ??
      null;

  const baseFp = selectedElement?.fingerprint ?? {
    nativeId:      selectedElement?.nativeId ?? null,
    className:     selectedElement?.className ?? "",
    fullClassName: selectedElement?.fullClassName ?? "",
    tagName:       selectedElement?.tag ?? selectedElement?.tagName ?? "button",
    textSnippet:   selectedElement?.textContent ?? selectedElement?.text ?? "",
    nthChild:      selectedElement?.nthChild ?? 0,
    siblingIndex:  selectedElement?.siblingIndex ?? 0,
    parentTag:     selectedElement?.parentTag ?? "body",
    parentId:      selectedElement?.parentId ?? null,
    ancestorPath:  selectedElement?.ancestorPath ?? [],
    runtimeId,
  };

  return {
    ...baseFp,
    tagName: baseFp.tagName || selectedElement?.tag || selectedElement?.tagName || "button",
    textSnippet:
        selectedElement?.textContent ??
        selectedElement?.text ??
        baseFp.textSnippet ??
        "",
    runtimeId: dataElementId || runtimeId,
    componentId: dataElementId,
    elementId: dataElementId,
  };
};

const buildElementHtmlFromSelection = (selectedElement, fp, extraAttrs = []) => {
  const tagName = fp.tagName || "button";
  const attrs   = [...extraAttrs];

  if (fp.nativeId && !fp.nativeId.startsWith("rt-")) attrs.push(`id="${fp.nativeId}"`);
  if (fp.className) attrs.push(`className="${fp.className}"`);

  const styleObj = selectedElement?.style || {};
  if (Object.keys(styleObj).length > 0) {
    const styleStr = Object.entries(styleObj)
        .map(([k, v]) => `${k.replace(/([A-Z])/g, "-$1").toLowerCase()}:${v}`)
        .join("; ");
    if (styleStr) attrs.push(`style="${styleStr}"`);
  }

  const attrsStr = attrs.length > 0 ? ` ${attrs.join(" ")}` : "";
  const innerText =
      selectedElement?.textContent ??
      selectedElement?.text ??
      selectedElement?.innerText ??
      "";

  return `<${tagName}${attrsStr}>${innerText}</${tagName}>`;
};

const getSelectionDataElementId = (selectedElement) =>
    selectedElement?.elementId ??
    selectedElement?.fingerprint?.elementId ??
    selectedElement?.id ??
    selectedElement?.componentId ??
    null;

const patchSelectedElementInSource = ({
                                        selectedElement,
                                        appCodes,
                                        editedCodes,
                                        runtimeIdToFile,
                                        activePagePath,
                                      }) => {
  const runtimeId = selectedElement?.id;
  if (!runtimeId) return { updatedCodes: editedCodes, patched: false };

  const fp = buildSelectionFingerprint(selectedElement);
  // Keep ORIGINAL selected text for occurrence matching — buildSelectionFingerprint
  // may already carry the new textContent the user just typed.
  const previousText = selectedElement?.fingerprint?.textSnippet || "";
  const patchFp = { ...fp, textSnippet: previousText || fp.textSnippet };

  const preferredPath =
      resolveMessageFilePath(selectedElement?.filePath) ||
      runtimeIdToFile?.get(runtimeId) ||
      null;

  const cachedFile = preferredPath
      ? (findCodeEntryByPath(editedCodes, preferredPath) ||
          findCodeEntryByPath(appCodes, preferredPath) ||
          null)
      : null;

  const result = cachedFile
      ? { name: cachedFile.name || preferredPath, contents: cachedFile.contents }
      : findSourceFile(appCodes, editedCodes, patchFp, { activePagePath });

  if (!result) return { updatedCodes: editedCodes, patched: false };

  const { contents: sourceContents, name: fileToUpdate } = result;
  runtimeIdToFile?.set(runtimeId, fileToUpdate);

  const newText =
      selectedElement?.textContent ??
      selectedElement?.text ??
      selectedElement?.innerText ??
      "";

  const updatedSource = applyTextSemanticPatch(
      sourceContents,
      {
        elementId: patchFp.elementId || runtimeId,
        componentId: patchFp.elementId || runtimeId,
        runtimeId,
        value: newText,
        previousText,
        property: "textContent",
        mapTagName: selectedElement?.mapTagName || selectedElement?.tag || patchFp.tagName,
        tagName: selectedElement?.mapTagName || patchFp.tagName,
        filePath: preferredPath,
      },
      patchFp,
      collectElementIds(
          { elementId: patchFp.elementId || runtimeId, componentId: patchFp.elementId },
          patchFp
      ),
      collectFingerprintMarkers(patchFp)
  );

  const patched = updatedSource !== sourceContents;
  if (!patched) {
    console.warn("[patchSelectedElementInSource] text patch produced no change", {
      fileToUpdate,
      elementId: patchFp.elementId,
      previousText: previousText?.slice?.(0, 40),
      newText: String(newText).slice(0, 40),
    });
  }

  const updatedCodes = editedCodes.some((c) =>
      c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
  )
      ? editedCodes.map((c) =>
          c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
              ? { ...c, name: fileToUpdate, contents: updatedSource }
              : c
      )
      : [...editedCodes, { name: fileToUpdate, contents: updatedSource }];

  return { updatedCodes, patched, fileToUpdate };
};

// ─── replaceSvgInSource ───────────────────────────────────────────────────────
//
// Replaces an SVG / Lucide icon in the source file.
//
// Search order:
//  A. Lucide JSX component   <FileText className="…" /> or <FileText />
//  B. Lucide data-icon attr  <svg data-icon="file-text" …>…</svg>
//  C. Lucide CSS class       <svg class="lucide lucide-file-text" …>…</svg>
//  D. Native id match        <svg id="myIcon" …>…</svg>
//  E. General replaceInSource

const replaceSvgInSource = (
    sourceContents,
    newIconJsxOrName, // JSX string OR PascalCase icon name (preferred)
    fingerprint,
    oldIconName,      // PascalCase name of the OLD icon  e.g. "FileText"
    oldSvgOuterHTML,  // raw outerHTML of the old DOM SVG (may be null)
    options = {}
) => {
  const newIconName =
      typeof newIconJsxOrName === "string" &&
      !newIconJsxOrName.trim().startsWith("<")
          ? newIconJsxOrName
          : null;

  const makeJsx = (oldTag) => {
    if (newIconName) {
      const preserved = extractPreservedIconProps(
          oldTag || oldSvgOuterHTML || "",
          options.className || fingerprint?.fullClassName || fingerprint?.className || "",
          options.style || null
      );
      if (options.dataElementId && !preserved.dataElementId) {
        preserved.dataElementId = options.dataElementId;
      }
      return buildIconJsx(newIconName, preserved.className, preserved.style, preserved);
    }
    return newIconJsxOrName;
  };

  // ── A. Lucide JSX component (self-closing) ────────────────────────────────
  // Matches: <FileText />, <FileText className="..." />, <FileText size={…} />
  if (oldIconName) {
    const lucideJsxPat = new RegExp(
        `<${escapeRegex(oldIconName)}(?:\\s[^>]*)?\\/?>`,
        "g"
    );
    if (lucideJsxPat.test(sourceContents)) {
      // Reset lastIndex after test()
      lucideJsxPat.lastIndex = 0;
      return sourceContents.replace(lucideJsxPat, (match) => makeJsx(match));
    }
  }

  // ── B. data-icon attribute on raw SVG ────────────────────────────────────
  if (oldIconName) {
    const lucideKebab  = iconNameToLucideClass(oldIconName); // "lucide-file-text"
    const dataIconFull = lucideKebab.replace("lucide-", ""); // "file-text"
    for (const attrVal of [lucideKebab, dataIconFull]) {
      const dataIconPat = new RegExp(
          `<svg[^>]*data-icon=["']${escapeRegex(attrVal)}["'][^>]*>[\\s\\S]*?<\\/svg>`,
          "i"
      );
      const replaced = sourceContents.replace(dataIconPat, (match) => makeJsx(match));
      if (replaced !== sourceContents) return replaced;
    }
  }

  // ── C. Lucide CSS class on raw SVG ────────────────────────────────────────
  if (oldSvgOuterHTML) {
    const lucideClassMatch = oldSvgOuterHTML.match(/lucide-([a-z0-9-]+)/);
    if (lucideClassMatch) {
      const lucideClass = lucideClassMatch[0]; // "lucide-file-text"
      const classPat = new RegExp(
          `<svg[^>]*class(?:Name)?=["'][^"']*${escapeRegex(lucideClass)}[^"']*["'][^>]*>[\\s\\S]*?<\\/svg>`,
          "i"
      );
      const replaced = sourceContents.replace(classPat, (match) => makeJsx(match));
      if (replaced !== sourceContents) return replaced;
    }
  }

  // ── D. Native id ─────────────────────────────────────────────────────────
  const { nativeId } = fingerprint;
  if (nativeId && !nativeId.startsWith("rt-")) {
    const idPat = new RegExp(
        `<svg[^>]*id=["']${escapeRegex(nativeId)}["'][^>]*>[\\s\\S]*?<\\/svg>`,
        "i"
    );
    const replaced = sourceContents.replace(idPat, (match) => makeJsx(match));
    if (replaced !== sourceContents) return replaced;
  }

  // ── E. Fallback: general replaceInSource ──────────────────────────────────
  return replaceInSource(sourceContents, makeJsx(oldSvgOuterHTML || ""), fingerprint);
};

const resolveFileFromPagePath = (
    pathname,          // e.g. "/login" or "/dashboard/settings"
    appCodes,
    editedCodes,
    tagName = "",
    textSnippet = ""
) => {
  if (!pathname) return null;

  // Edited files take priority
  const prioritized = [
    ...editedCodes,
    ...appCodes.filter((a) => !editedCodes.some((e) => e.name === a.name)),
  ];

  // Only JSX/TSX files
  const candidates = prioritized.filter((c) => /\.(jsx?|tsx?)$/.test(c.name));
  if (candidates.length === 0) return null;

  // Normalise pathname to tokens
  // "/login"              → ["login"]
  // "/dashboard/settings" → ["dashboard", "settings"]
  // "/"                   → ["index", "home", "app"]  (root page)
  const raw    = pathname.replace(/^\/|\/$/g, "").toLowerCase();
  const tokens = raw
      ? raw.split("/").filter(Boolean)
      : ["index", "home", "app", "root"];

  // Score each file
  const scored = candidates.map((file) => {
    const nameLower = file.name.toLowerCase();
    let   score     = 0;

    tokens.forEach((token) => {
      // Exact segment match in file path:  "login" in "pages/login.tsx"
      const segmentMatch = new RegExp(`(^|[/\\\\._-])${escapeRegex(token)}([/\\\\._-]|$)`);
      if (segmentMatch.test(nameLower)) {
        score += 100;  // strong: "login" found as a path segment
      } else if (nameLower.includes(token)) {
        score += 50;   // weaker: "login" found anywhere in name
      }
    });

    // Bonus: file also contains the text being edited
    if (textSnippet && textSnippet.length > 3 && file.contents.includes(textSnippet)) {
      score += 30;
    }

    // Bonus: file contains the tag
    if (tagName && file.contents.includes(`<${tagName}`)) {
      score += 10;
    }

    return { file, score };
  });

  scored.sort((a, b) => b.score - a.score);

  const best = scored[0];
  if (!best || best.score === 0) return null;

  console.debug(
      "[resolveFileFromPagePath] pathname:", pathname,
      "→ tokens:", tokens,
      "→ winner:", best.file.name, "score:", best.score
  );

  return { name: best.file.name, contents: best.file.contents };
};

// ─── Component ────────────────────────────────────────────────────────────────

const ProjectView = ({ projectData, activeSection, onRequireAgentsSection }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [state, setState] = React.useState({
    currentView: "preview", githubViewActive: false,selectedScreenSize: "desktop", zoomLevel: ZOOM_DEFAULT,
    isChatVisible: true, isCodeEditEnabled: false, previewUrl: DEFAULT_PREVIEW_URL,
    previewLoaded: false, selectedElement: null, highlightBox: null,
    chatLoaded: false, hasTasks: false, enableEdit: false, hasUnsavedChanges: false, forceChat: false,
    environmentType: "K8S", activeBackendTab: "database", chatWidth: 400,
    isWorkflowRunning: false, runningAgentId: null, isRunButtonExpanded: false,
    isClearButtonExpanded: false, isSaveButtonExpanded: false, isConsoleButtonExpanded: false,
    hasAgents: false, hasInputs: false, projectMasterUser: null,
    hasCodeAccess: null,
    previewReloadKey: 0,
    chatStreaming: false,
    expandedSections: { content: true, buttonProperties: true, buttonOnClick: true, typography: true, basicInfo: true, icon: true, imageProperties: true, uploadImage: true, crop: false, rotate: false },
    workflowEventStatus: null, activeButton: null, isWorkflowSaved: false,
    appCodes: [], editedCodes: [], codeSyncTrigger: false, codeRefreshTrigger: 0, codeDiscardTrigger: 0,
    discardedFilePaths: [],
    renderLoading: false, uploadingImage: false, uploadedImageUrl: null,
    exportingDb: false,
    dbConnected: false,
    storageId: null, rootFolder: null,
    imageFilters: { brightness: 100, contrast: 100, saturation: 100, exposure: 100, blur: 0, hue: 0 },
    transform:    { rotate: 0, scaleX: 1, scaleY: 1, translateX: 0, translateY: 0 },
    crop:         { top: 0, right: 0, bottom: 0, left: 0 },
    filters:      { brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 },

    // UI event mappings (persisted to backend)
    uiEventMappings: { version: 1, elements: {} },
    uiEventSaving: false,

    // Selector + config for current selection (computed from iframe click)
    selectedElementSelectorKey: null,
    selectedElementEventConfig: null,

    // Workflow dropdown options (fetched)
    availableWorkflows: [],

    // Service (Agent Apps) dropdown options (fetched)
    availableServiceApps: [],
  });

  const podControlRef      = useRef(null);
  const previewIframeRef   = useRef(null);
  const previewUrlRef      = useRef(state.previewUrl);
  const isResizingRef      = useRef(false);
  const chatContainerRef   = useRef(null);
  const leftPanelRef       = useRef(null);
  const animationFrameRef  = useRef(null);
  const startXRef          = useRef(0);
  const startWidthRef      = useRef(0);
  const appCodesRef        = useRef([]);
  const editedCodesRef     = useRef([]);
  const uiEventMappingsRef = useRef(state.uiEventMappings);
  const activePagePathRef = useRef(null); // stores pathname from last SELECT_COMPONENT
  const runtimeIdToFileRef = useRef(new Map()); // runtimeId -> last resolved source file name
  const selectedElementRef = useRef(null);
  const projectIdRef = useRef(null);
  const codeSyncUserIdRef = useRef(null);
  const developModeRef = useRef("frontend");
  const updateComponentTimersRef = useRef(new Map());
  const updateComponentPendingRef = useRef(new Map());
  const textContentPatchTimerRef = useRef(null);
  const pendingTextContentRef = useRef(null);
  const [developMode, setDevelopMode] = React.useState("frontend");
  const [isCheckpointDrawerOpen, setIsCheckpointDrawerOpen] = React.useState(false);
  const [codeOverflowOpen, setCodeOverflowOpen] = useState(false);
  const [sourceCompressTrigger, setSourceCompressTrigger] = useState(0);
  const [compressingSource, setCompressingSource] = useState(false);
  const codeOverflowRef = useRef(null);
  const [showConnectors, setShowConnectors] = useState(false);
  const [githubConnected, setGithubConnected] = React.useState(false);

  React.useEffect(() => {
    if (!codeOverflowOpen) return undefined;
    const onPointerDown = (event) => {
      if (codeOverflowRef.current && !codeOverflowRef.current.contains(event.target)) {
        setCodeOverflowOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setCodeOverflowOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [codeOverflowOpen]);
  const [planModeTick, setPlanModeTick] = useState(0);

  const showPlanWorkspace = useMemo(
    () => isProjectInPlanMode(projectData, projectData?.id),
    // planModeTick forces recompute when composer mode / build transition changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectData, projectData?.id, projectData?.appType, projectData?.app_type, projectData?.messages, projectData?.plan, planModeTick]
  );

  const canViewExistingPlan = useMemo(
    () => hasExistingLivingPlan(projectData, projectData?.id, projectData?.messages || []),
    [projectData, projectData?.id, projectData?.plan, projectData?.messages, planModeTick]
  );

  const handleViewExistingPlan = useCallback(() => {
    const projectId = projectData?.id;
    if (!projectId) return;
    writePlanWorkspaceViewOpen(projectId, true);
    setPlanModeTick((value) => value + 1);
  }, [projectData?.id]);

  React.useEffect(() => {
    const onMode = (event) => {
      if (event?.detail?.projectId && String(event.detail.projectId) !== String(projectData?.id || "")) return;
      setPlanModeTick((value) => value + 1);
    };
    window.addEventListener(PLAN_WORKSPACE_MODE_EVENT, onMode);
    return () => window.removeEventListener(PLAN_WORKSPACE_MODE_EVENT, onMode);
  }, [projectData?.id]);

  // Keep session plan meta in sync with DB living plan so View Plan / Build / REFINE work after reload.
  React.useEffect(() => {
    const projectId = projectData?.id;
    const plan = projectData?.plan;
    if (!projectId || !plan?.planText) return;
    try {
      writePlanMeta(projectId, {
        planId: plan.planId || plan.id || undefined,
        planText: plan.planText,
        planStatus: plan.planStatus || "ready",
        buildAvailable: plan.planStatus
          ? String(plan.planStatus).toLowerCase() !== "updating" &&
            String(plan.planStatus).toLowerCase() !== "failed" &&
            String(plan.planStatus).toLowerCase() !== "planning"
          : true,
        version: plan.version,
      });
      setPlanModeTick((value) => value + 1);
    } catch {
      /* ignore */
    }
  }, [projectData?.id, projectData?.plan?.planId, projectData?.plan?.planText, projectData?.plan?.planStatus, projectData?.plan?.version]);
  
  React.useEffect(() => {
    const currentProjectId = projectData?.id;

    if (!currentProjectId) {
      setGithubConnected(false);
      return;
    }

    let cancelled = false;
    let timeoutId = null;

    const loadSCMStatus = async () => {
      let connected = false;

      try {
        const response = await SCMService.getProjectSCMMapping(
          currentProjectId
        );

        const mapping = response?.data || response;

        connected = mapping?.mappingStatus === "CONNECTED";

        if (!cancelled) {
          setGithubConnected(connected);
        }
      } catch {
        if (!cancelled) {
          setGithubConnected(false);
        }
      }

      if (!cancelled && state.githubViewActive && !connected) {
        timeoutId = window.setTimeout(loadSCMStatus, 3000);
      }
    };

    loadSCMStatus();

    return () => {
      cancelled = true;

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [projectData?.id, state.githubViewActive]);
  const handleStartBuildingFromPlan = useCallback(async (detail = {}) => {
    const projectId = projectData?.id;
    if (!projectId) {
      showNotificationGlobal("Project ID not available", "error", "topRight");
      return;
    }
    const hasPlan = hasExistingLivingPlan(projectData, projectId);
    if (!hasPlan && !detail?.request) {
      showNotificationGlobal("No approved plan available to build from.", "error", "topRight");
      return;
    }

    // Switch PLAN → APP mode, then reuse the existing APP/resume request flow.
    writePlanWorkspaceViewOpen(projectId, false);
    writePlanWorkspaceBuildMode(projectId, true);
    const composerApp = readComposerMode(projectId);
    const buildAppType =
      composerApp === "APP_AGENT" || composerApp === "WEBSITE" ? composerApp : "APP";
    writeComposerMode(projectId, buildAppType);
    setPlanModeTick((value) => value + 1);

    const request =
      detail?.request ||
      buildAppRequestFromPlan({ projectId, appType: buildAppType });
    const kickoffPrompt = request?.prompt || "Start building the approved plan now. Implement the MVP end-to-end.";

    try {
      window.dispatchEvent(
        new CustomEvent("sa-plan-start-build", {
          detail: {
            projectId,
            prompt: kickoffPrompt,
            request,
            userMessage: {
              role: "user",
              content: kickoffPrompt,
              createdAt: new Date(),
              projectId,
              prompt: kickoffPrompt,
              type: projectType.RESUME,
              appType: buildAppType,
            },
          },
        })
      );
    } catch {
      /* ignore */
    }

    const formData = new FormData();
    appendProjectJsonPart(formData, request);

    try {
      const response = await AgentService.createProject(formData);
      if (response?.status === API_RESPONSE.FAILURE) {
        showNotificationGlobal(
          response?.errorMessage || "Failed to start build from plan.",
          "error",
          "topRight"
        );
        return;
      }
      showNotificationGlobal("Plan accepted. Building with APP mode.", "success", "topRight");
    } catch (error) {
      showNotificationGlobal(
        error?.message || "Failed to start build from plan.",
        "error",
        "topRight"
      );
    }
  }, [projectData]);

  const handleUpdatePlan = useCallback(async (detail = {}) => {
    const projectId = projectData?.id;
    const planId = detail?.planId;
    const additionalInput = String(detail?.additionalInput || "").trim();
    if (!projectId) {
      showNotificationGlobal("Project ID not available", "error", "topRight");
      return;
    }
    if (!planId || !additionalInput) {
      showNotificationGlobal("Enter what you want to change in the plan.", "error", "topRight");
      return;
    }

    const formData = new FormData();
    appendProjectJsonPart(
      formData,
      buildPlanRequestBody({
        prompt: additionalInput,
        projectId,
        planAction: planAction.REFINE,
        additionalInput,
        planId,
        plan: detail?.plan || { id: planId, planText: detail?.existingPlanText || "", format: "markdown" },
      })
    );

    try {
      const response = await AgentService.createProject(formData);
      if (response?.status === API_RESPONSE.FAILURE) {
        throw new Error(response?.errorMessage || "Failed to update the plan.");
      }
      showNotificationGlobal("Updating plan from your notes…", "success", "topRight");
    } catch (error) {
      showNotificationGlobal(
        error?.message || "Failed to update the plan.",
        "error",
        "topRight"
      );
      throw error;
    }
  }, [projectData?.id]);


  // Keep a ref of the latest previewUrl to avoid stale closures in callbacks.
  React.useEffect(() => { previewUrlRef.current = state.previewUrl; }, [state.previewUrl]);
  React.useEffect(() => { uiEventMappingsRef.current = state.uiEventMappings; }, [state.uiEventMappings]);
  React.useEffect(() => { projectIdRef.current = projectData?.id ?? null; }, [projectData?.id]);
  React.useEffect(() => {
    codeSyncUserIdRef.current = state.projectMasterUser ?? projectData?.userId ?? null;
  }, [state.projectMasterUser, projectData?.userId]);
  React.useEffect(() => { developModeRef.current = developMode; }, [developMode]);
  React.useEffect(() => { selectedElementRef.current = state.selectedElement; }, [state.selectedElement]);

  const isXrayApp = String(projectData?.appType ?? "").toUpperCase() === "XRAY";
  const showChatPanel =
    (projectData?.messages?.length ?? 0) > 0 || state.forceChat || isXrayApp;

  React.useEffect(() => {
    if (!isXrayApp) return;
    setState((prev) => {
      if (prev.forceChat && prev.isChatVisible) return prev;
      return { ...prev, forceChat: true, isChatVisible: true };
    });
  }, [isXrayApp, projectData?.id]);

  // Seed UI event mappings from project payload (if present)
  React.useEffect(() => {
    const incoming =
        projectData?.uiEventMappings ||
        projectData?.projectInfo?.uiEventMappings ||
        projectData?.projectInfo?.ui_event_mappings ||
        projectData?.ui_event_mappings ||
        null;

    if (!incoming) return;

    try {
      const parsed = typeof incoming === "string" ? JSON.parse(incoming) : incoming;
      if (parsed && typeof parsed === "object") {
        setState((p) => ({
          ...p,
          uiEventMappings: {
            version: Number(parsed.version || 1),
            elements: parsed.elements && typeof parsed.elements === "object" ? parsed.elements : {},
          },
        }));
      }
    } catch (e) {
      console.warn("[uiEventMappings] failed to parse from projectData:", e);
    }
  }, [projectData?.id]);

  // Compute a stable selector key for an element coming from iframe
  const getSelectorKeyForElement = React.useCallback((element) => {
    if (!element) return null;

    // Prefer stable data-element-id for On Click / event listing
    const dataElementId =
        element.elementId ||
        element.id ||
        element.componentId ||
        null;
    if (dataElementId && typeof dataElementId === "string" && dataElementId.trim()) {
      const id = dataElementId.trim();
      if (!id.startsWith("rt-")) return `data-element-id:${id}`;
    }

    const nativeId =
        element.nativeId ||
        element.elementRect?.elementId ||
        element.attrs?.id ||
        element.attributes?.id ||
        null;

    if (nativeId && typeof nativeId === "string" && nativeId.trim() && !nativeId.startsWith("rt-")) {
      return `id:${nativeId.trim()}`;
    }

    const tag = (element.tag || element.tagName || element.type || "").toString().toLowerCase() || "div";
    const className = (element.className || element.fullClassName || "").toString().trim();
    const firstClass = className.split(/\s+/).filter(Boolean)[0] || null;

    if (firstClass) {
      return `css:${tag}.${firstClass}`;
    }

    if (element.id) return `runtime:${String(element.id)}`;
    return null;
  }, []);

  const getSavedEventConfigForElement = React.useCallback((selectorKey) => {
    if (!selectorKey) return null;
    const map = uiEventMappingsRef.current?.elements || {};
    const entry = map[selectorKey];
    return entry?.events || null;
  }, []);

  const fetchAvailableWorkflows = React.useCallback(async () => {
    const projectId = projectData?.id;
    if (!projectId) return;

    try {
      // Use workflow history as a best-effort “list” until a dedicated endpoint exists.
      const res = await WorkflowService.getWorkflowHistory(projectId, 0, 200);
      const content =
          res?.workflowHistoryDTOPage?.content ||
          res?.workflowExecutionDTOPage?.content ||
          res?.content ||
          res?.data?.content ||
          [];

      const rows = Array.isArray(content) ? content : [];
      const uniq = new Map();
      rows.forEach((r) => {
        const workflowId = r?.workflowId || r?.workflow?.id || r?.id || null;
        if (!workflowId) return;
        const name = r?.workflowName || r?.workflow?.name || `Workflow ${workflowId}`;
        if (!uniq.has(String(workflowId))) {
          uniq.set(String(workflowId), { id: String(workflowId), name: String(name) });
        }
      });
      const list = Array.from(uniq.values()).sort((a, b) => a.name.localeCompare(b.name));

      setState((p) => ({ ...p, availableWorkflows: list }));
    } catch (e) {
      console.warn("[fetchAvailableWorkflows] failed:", e);
      setState((p) => ({ ...p, availableWorkflows: [] }));
    }
  }, [projectData?.id]);

  const fetchAvailableServiceApps = React.useCallback(async () => {
    // Reuse "Connected Apps" list as the source of configured Agent Apps.
    // REST API Agent App provider id in our UI catalog is "open_api".
    try {
      const all = [];
      // default_size is 12; iterate a few pages to avoid missing items
      for (let page = 0; page < 10; page++) {
        // eslint-disable-next-line no-await-in-loop
        const res = await AgentService.getConnectedAppsListByUser(page);
        const dtoPage = res?.oauthCredentialDTOPage || res?.oAuthCredentialDTOPage || res?.data?.oauthCredentialDTOPage || null;
        const content = dtoPage?.content || [];
        if (Array.isArray(content) && content.length) all.push(...content);
        const isLast = dtoPage?.last === true || dtoPage?.last === "true";
        const totalPages = Number(dtoPage?.totalPages || 0);
        if (isLast || (totalPages && page >= totalPages - 1) || (Array.isArray(content) && content.length === 0)) break;
      }

      const normalized = (Array.isArray(all) ? all : []).map((a) => ({
        id: String(a?.id ?? ""),
        name: String(a?.name || a?.credentialName || a?.providerName || "Unnamed"),
        provider: String(a?.provider || a?.providerName || a?.app || ""),
        raw: a,
      })).filter((a) => a.id);

      const restOnly = normalized.filter((a) => {
        const p = (a.provider || "").toLowerCase();
        const n = (a.name || "").toLowerCase();
        return p.includes("open_api") || p.includes("openapi") || n.includes("rest api") || n.includes("restapi");
      });

      // De-dupe by id
      const uniq = new Map();
      restOnly.forEach((a) => { if (!uniq.has(a.id)) uniq.set(a.id, a); });

      const list = Array.from(uniq.values()).sort((x, y) => (x.name || "").localeCompare(y.name || ""));
      setState((p) => ({ ...p, availableServiceApps: list }));
    } catch (e) {
      console.warn("[fetchAvailableServiceApps] failed:", e);
      setState((p) => ({ ...p, availableServiceApps: [] }));
    }
  }, []);

  React.useEffect(() => {
    if (activeSection !== "frontend") return;
    fetchAvailableWorkflows();
    fetchAvailableServiceApps();
  }, [activeSection, fetchAvailableWorkflows]);

  const saveUiEventMappings = React.useCallback(async (nextMappings) => {
    const projectId = projectData?.id;
    if (!projectId) return false;

    setState((p) => ({ ...p, uiEventSaving: true }));
    try {
      const payload = {
        uiEventMappings: nextMappings,
      };
      const res = await AgentService.updateProject(projectId, payload);
      const ok = String(res?.status || "").toUpperCase() === "SUCCESS";
      if (!ok) {
        showNotificationGlobal(res?.errorMessage || res?.message || "Failed to save button event config", "error", "bottomRight");
        return false;
      }
      return true;
    } catch (e) {
      showNotificationGlobal(e?.response?.data?.errorMessage || e?.message || "Failed to save button event config", "error", "bottomRight");
      return false;
    } finally {
      setState((p) => ({ ...p, uiEventSaving: false }));
    }
  }, [projectData?.id]);

  const upsertElementEventConfig = React.useCallback(async (selectorKey, selector, events) => {
    if (!selectorKey) return;

    const nextMappings = (() => {
      const prev = uiEventMappingsRef.current || { version: 1, elements: {} };
      const elements = { ...(prev.elements || {}) };
      elements[selectorKey] = {
        selector,
        events,
        updatedAt: new Date().toISOString(),
      };
      return { version: Number(prev.version || 1), elements };
    })();

    // Optimistic UI update
    setState((p) => ({
      ...p,
      uiEventMappings: nextMappings,
      selectedElementEventConfig: events,
    }));

    await saveUiEventMappings(nextMappings);
  }, [saveUiEventMappings]);

  // postMessage targetOrigin must be the iframe's *origin* (not full URL).
  const getIframeTargetOrigin = useCallback(() => {
    const raw = previewIframeRef.current?.src || previewUrlRef.current;
    if (!raw) return window.location.origin;
    try { return new URL(raw).origin; } catch { return window.location.origin; }
  }, []);

  const cancelTextContentSourcePatch = useCallback(() => {
    if (textContentPatchTimerRef.current) {
      clearTimeout(textContentPatchTimerRef.current);
      textContentPatchTimerRef.current = null;
    }
    pendingTextContentRef.current = null;
  }, []);

  const cancelScheduledUpdateComponentPostMessages = useCallback(() => {
    updateComponentTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    updateComponentTimersRef.current.clear();
    updateComponentPendingRef.current.clear();
    cancelTextContentSourcePatch();
  }, [cancelTextContentSourcePatch]);
  const cancelScheduledUpdateComponentRef = useRef(cancelScheduledUpdateComponentPostMessages);
  cancelScheduledUpdateComponentRef.current = cancelScheduledUpdateComponentPostMessages;

  const scheduleUpdateComponentPostMessage = useCallback((message, { immediate = false, delay = UPDATE_COMPONENT_DEBOUNCE_MS } = {}) => {
    if (!message?.type || !previewIframeRef.current?.contentWindow) return;

    if (message.type !== "UPDATE_COMPONENT") {
      previewIframeRef.current.contentWindow.postMessage(message, getIframeTargetOrigin());
      return;
    }

    // Preview hook looks up by elementId / runtimeId / componentId (= data-element-id).
    const elementId =
        message.elementId ??
        message.runtimeId ??
        message.componentId ??
        null;

    if (!elementId) {
      console.warn("[scheduleUpdateComponentPostMessage] missing element id — skipping", message);
      return;
    }

    const normalizedMessage = {
      ...message,
      runtimeId: elementId,
      elementId,
      componentId: elementId,
    };

    const key = `${normalizedMessage.runtimeId ?? ""}:${normalizedMessage.property ?? ""}`;
    const timers = updateComponentTimersRef.current;
    const pending = updateComponentPendingRef.current;

    if (timers.has(key)) clearTimeout(timers.get(key));

    const existing = pending.get(key);
    let nextMessage = normalizedMessage;
    if (
        normalizedMessage.property === "style" &&
        existing?.property === "style" &&
        existing.value &&
        normalizedMessage.value &&
        typeof existing.value === "object" &&
        typeof normalizedMessage.value === "object"
    ) {
      nextMessage = { ...normalizedMessage, value: { ...existing.value, ...normalizedMessage.value } };
    }
    pending.set(key, nextMessage);

    const flush = () => {
      timers.delete(key);
      const payload = pending.get(key);
      pending.delete(key);
      if (!payload || !previewIframeRef.current?.contentWindow) return;
      previewIframeRef.current.contentWindow.postMessage(payload, getIframeTargetOrigin());
    };

    if (immediate) {
      flush();
      return;
    }

    timers.set(key, setTimeout(flush, delay));
  }, [getIframeTargetOrigin]);

  React.useEffect(
      () => () => {
        cancelScheduledUpdateComponentPostMessages();
        cancelTextContentSourcePatch();
      },
      [cancelScheduledUpdateComponentPostMessages, cancelTextContentSourcePatch]
  );


  // Track the OLD icon name and SVG when an SVG element is selected.
  // Used by replaceSvgInSource when COMPONENT_UPDATED arrives.
  const selectedIconNameRef    = useRef(null);   // PascalCase e.g. "FileText"
  const selectedSvgOuterHTMLRef = useRef(null);  // raw outerHTML of old SVG

  // ─── Auth ─────────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (localStorage.getItem(ACCESS_TOKEN) === null) navigate("/auth/login");
  }, [navigate]);

  const derivedPreviewUrl = useMemo(
      () => resolvePreviewUrl(projectData),
      [projectData]
  );
  const syncedPreviewRef = useRef("");

  // Point the preview at the configured URL as soon as the project is selected.
  // Previous highlight, edit mode, and readiness are cleared before paint.
  React.useLayoutEffect(() => {
    const environmentType = projectData?.environmentType || "K8S";
    localStorage.setItem("environmentType", environmentType);
    const previewUrl = derivedPreviewUrl;
    const previewChanged = syncedPreviewRef.current !== previewUrl;
    syncedPreviewRef.current = previewUrl;

    setState((prev) => ({
      ...prev,
      environmentType,
      previewUrl,
      ...(previewChanged
          ? {
            previewLoaded: false,
            selectedElement: null,
            highlightBox: null,
            isCodeEditEnabled: false,
            generationConfirmed: false,
          }
          : {}),
    }));
  }, [derivedPreviewUrl, projectData?.environmentType]);

  // ─── Workflow listeners ───────────────────────────────────────────────────

  React.useEffect(() => {
    const on  = (ev, fn) => window.addEventListener(ev, fn);
    const off = (ev, fn) => window.removeEventListener(ev, fn);
    const wRun   = (e) => { const { nodeId } = e.detail || {}; setState((p) => ({ ...p, isWorkflowRunning: true, runningAgentId: nodeId, activeButton: "run", workflowEventStatus: "workflow_started" })); };
    const wPod   = (e) => {
      const status = e?.detail?.status;
      podControlRef.current = status;
      if (status === "Running" || status === "Stopping") {
        setState((p) => ({ ...p, isWorkflowRunning: true, activeButton: "run" }));
      } else if (status === "Stopped" || status === "Failed") {
        setState((p) => ({ ...p, isWorkflowRunning: false, activeButton: "run" }));
      }
    };
    const wStop  = ()  => {
      if (podControlRef.current === "Running" || podControlRef.current === "Stopping") return;
      setState((p) => ({ ...p, isWorkflowRunning: false, runningAgentId: null, activeButton: "run", workflowEventStatus: null }));
    };
    const wEvent = (e) => { const { eventType } = e.detail || {}; if (eventType) setState((p) => ({ ...p, workflowEventStatus: eventType })); };
    const wAgent = (e) => { const { hasAgents, hasTasks } = e.detail || {}; setState((p) => ({ ...p, hasAgents, hasTasks })); };
    const wInput = (e) => { const { hasInputs } = e.detail || {}; setState((p) => ({ ...p, hasInputs })); };
    const wClose = ()  => setState((p) => ({ ...p, activeButton: p.activeButton === "console" ? null : p.activeButton }));
    const wSaved = ()  => setState((p) => ({ ...p, isWorkflowSaved: true  }));
    const wClear = ()  => setState((p) => ({ ...p, isWorkflowSaved: false }));
    on("workflow-run", wRun); on("workflow-stop", wStop); on("workflow-pod-status", wPod); on("workflow-agents-changed", wAgent);
    on("workflow-inputs-changed", wInput); on("workflow-event", wEvent);
    on("close-console-drawer", wClose); on("workflow-saved", wSaved); on("clear-workflow", wClear);
    return () => {
      off("workflow-run", wRun); off("workflow-stop", wStop); off("workflow-pod-status", wPod); off("workflow-agents-changed", wAgent);
      off("workflow-inputs-changed", wInput); off("workflow-event", wEvent);
      off("close-console-drawer", wClose); off("workflow-saved", wSaved); off("clear-workflow", wClear);
    };
  }, []);

  // ─── Reset filters on selection change ───────────────────────────────────

  React.useEffect(() => {
    if (!state.selectedElement || state.selectedElement.tag !== "img") return;
    setState((p) => ({ ...p, imageFilters: { exposure: 100, brightness: 100, contrast: 100, saturation: 100, blur: 0, hue: 0 } }));
  }, [state.selectedElement?.id]);

  React.useEffect(() => {
    if (!state.selectedElement) return;
    setState((p) => ({ ...p, transform: { rotate: 0, scaleX: 1, scaleY: 1, translateX: 0, translateY: 0 }, crop: { top: 0, right: 0, bottom: 0, left: 0 } }));
  }, [state.selectedElement?.id]);

  // ─── Image filter helpers ─────────────────────────────────────────────────

  const buildImageFilter = (f) => `brightness(${f.brightness}%) contrast(${f.contrast}%) saturate(${f.saturation}%) blur(${f.blur}px) hue-rotate(${f.hue}deg)`.trim();
  const buildFilter      = (f) => `brightness(${f.brightness}%) contrast(${f.contrast}%) saturate(${f.saturation}%) blur(${f.blur}px) hue-rotate(${f.hue}deg)`;

  const updateImageFilter = React.useCallback((key, value) => {
    setState((prev) => {
      const updatedFilters = { ...prev.imageFilters, [key]: value };
      if (prev.selectedElement?.id) {
        const runtimeId = prev.selectedElement.id;
        const componentId = getSelectionDataElementId(prev.selectedElement);
        scheduleUpdateComponentPostMessage(
            { type: "UPDATE_COMPONENT", runtimeId, componentId, property: "filter", value: buildImageFilter(updatedFilters) }
        );
      }
      return { ...prev, imageFilters: updatedFilters };
    });
  }, []);

  const updateSelectedElementStyle = (styles) => {
    if (!state.selectedElement?.id) return;
    const runtimeId = state.selectedElement.id;
    const componentId = getSelectionDataElementId(state.selectedElement);
    scheduleUpdateComponentPostMessage(
        { type: "UPDATE_COMPONENT", runtimeId, componentId, property: "style", value: styles }
    );
  };


  React.useEffect(() => {
    const chatTimer = setTimeout(() => {
      setState(prevState => ({
        ...prevState,
        chatLoaded: true
      }));
    }, CHAT_LOAD_DELAY);
    return () => clearTimeout(chatTimer);
  }, []);

  // Parent owns activeSection: sync Agents (no messages) / Frontend (has messages).
  React.useEffect(() => {
    if (typeof onRequireAgentsSection === 'function') {
      onRequireAgentsSection();
    }
  }, [projectData?.messages?.length, onRequireAgentsSection]);

  // Memoized callbacks to prevent unnecessary re-renders
  const handleViewChange = useCallback(async (view) => {
    if (view === "code") {
      try {
        const response = await AgentService.checkProjectAccess(projectData.id);
        const hasAccess = response?.status === API_RESPONSE.SUCCESS;
        setState((prevState) => ({
          ...prevState,
          currentView: view,
          hasCodeAccess: hasAccess,
          projectMasterUser: hasAccess
              ? response?.projectMemberDTOS?.[0]?.masterUserId ?? null
              : null,
        }));
      } catch {
        setState((prevState) => ({
          ...prevState,
          currentView: view,
          hasCodeAccess: false,
          projectMasterUser: null,
        }));
      }
      return;
    }

    setState((prevState) => ({
      ...prevState,
      currentView: view,
    }));
  }, [projectData?.id]);

  React.useEffect(() => {
    const fromNav = Boolean(location.state?.findingChat || location.state?.findingChatDraft);
    const draft = location.state?.findingChatDraft || peekFindingChatDraft();
    if (!fromNav && !draft) return;
    if (!projectData?.id) return;
    setState((prev) => ({
      ...prev,
      isChatVisible: true,
      forceChat: true,
    }));
    if (location.state?.openCode !== false && draft?.openCode !== false) {
      handleViewChange("code");
    }
  }, [projectData?.id, handleViewChange, location.state]);

  const handleScreenSizeChange = useCallback((size) => {
    setState(prevState => ({
      ...prevState,
      selectedScreenSize: size
    }));
  }, []);

  const refreshPreview = useCallback(() => {
    setState((prev) => ({
      ...prev,
      previewLoaded: false,
      renderLoading: false,
      previewReloadKey: (prev.previewReloadKey || 0) + 1,
    }));
  }, []);

  const handleZoomIn = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      zoomLevel: Math.min(prevState.zoomLevel + ZOOM_STEP, ZOOM_MAX)
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      zoomLevel: Math.max(prevState.zoomLevel - ZOOM_STEP, ZOOM_MIN)
    }));
  }, []);

  const resetZoom = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      zoomLevel: ZOOM_DEFAULT
    }));
  }, []);

  const toggleChatVisibility = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isChatVisible: !prevState.isChatVisible
    }));
  }, []);


  const toggleClearButton = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isClearButtonExpanded: !prevState.isClearButtonExpanded
    }));
  }, []);

  const toggleConsoleButton = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isConsoleButtonExpanded: !prevState.isConsoleButtonExpanded
    }));
  }, []);

  const handleOpenConsole = useCallback(() => {
    // Dispatch window event to open console drawer
    window.dispatchEvent(new CustomEvent('open-console-drawer'));
    // Toggle button to show icon + text
    toggleConsoleButton();
  }, [toggleConsoleButton]);

  // Function to clear workflow
  const handleClearWorkflow = useCallback(() => {
    // Toggle button expansion
    toggleClearButton();
    // Dispatch event to clear workflow
    const event = new CustomEvent('clear-workflow');
    window.dispatchEvent(event);
  }, [toggleClearButton]);

  const toggleCodeEditMode = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isCodeEditEnabled: !prevState.isCodeEditEnabled,
      selectedElement: null,
      highlightBox: null,
      codeSyncTrigger: true
    }));
  }, []);


  const handlePreviewUrlChange = React.useCallback(
      (previewUrl) => {
        const environmentType =
            state.environmentType || localStorage.getItem("environmentType");

        const finalUrl =
            environmentType === "K8S"
                ? previewUrl
                : PREVIEW_UI_LOCALHOST;

        setState((prevState) => ({
          ...prevState,
          previewUrl: finalUrl,
          generationConfirmed: true,
          // bump a key so IframeComponent remounts even if URL string stays the same
          previewReloadKey: (prevState.previewReloadKey || 0) + 1,
        }));
      },
      [state.environmentType]
  );

  const handleChatActivity = React.useCallback((active) => {
    setState((prevState) => (
      prevState.chatStreaming === active ? prevState : { ...prevState, chatStreaming: active }
    ));
  }, []);

  const handleBackendTabChange = useCallback((tab) => {
    setState(prevState => ({
      ...prevState,
      activeBackendTab: tab
    }));
  }, []);

  const joinUrl = useCallback((base, path) => {
    if (!base) return path || '';
    if (!path) return base;
    const left = String(base).replace(/\/+$/, '');
    const right = String(path).replace(/^\/+/, '');
    return `${left}/${right}`;
  }, []);

  const extractExportUrl = useCallback((payload) => {
    if (!payload) return null;
    if (typeof payload === 'string') return payload;
    if (typeof payload === 'object') {
      // Support common shapes: { url }, { link }, { downloadUrl }, { data: { url } }, etc.
      const candidates = [
        payload.url,
        payload.link,
        payload.downloadUrl,
        payload.download_url,
        payload.fileUrl,
        payload.file_url,
        payload?.data?.url,
        payload?.data?.link,
        payload?.data?.downloadUrl,
        payload?.data?.download_url,
      ];
      return candidates.find(v => typeof v === 'string' && v.trim().length > 0) || null;
    }
    return null;
  }, []);

  const triggerBrowserDownload = useCallback(async (url, filename) => {
    if (!url) return false;
    try {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      if (filename) a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      return true;
    } catch (e) {
      console.error('Failed to trigger browser download:', e);
      return false;
    }
  }, []);

  const handleExportDb = useCallback(async () => {
    if (!projectData?.id) {
      showNotificationGlobal('Project ID not available', 'error', 'bottomRight');
      return;
    }
    setState(prev => ({ ...prev, exportingDb: true }));
    try {
      const base = constructPreviewUrls(PREVIEW_DATABASE_URL, projectData?.id);
      const exportEndpoint = joinUrl(base, 'db/api/v1/export/pgdump');
      const token = localStorage.getItem(ACCESS_TOKEN);

      const response = await axios.get(exportEndpoint, {
        headers: {
          accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      const exportUrl = extractExportUrl(response?.data) || extractExportUrl(response);
      if (!exportUrl) {
        showNotificationGlobal('Export started, but no download link returned', 'warning', 'bottomRight');
        return;
      }

      const filename = `project-${projectData?.id}-pgdump-${new Date().toISOString().replace(/[:.]/g, '-')}.dump`;
      const didDownload = await triggerBrowserDownload(exportUrl, filename);
      if (didDownload) {
        showNotificationGlobal('Database export download started', 'success', 'bottomRight');
        return;
      }

      // Fallback: copy link
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(exportUrl);
        showNotificationGlobal('Download link copied to clipboard', 'info', 'bottomRight');
      } else {
        showNotificationGlobal('Unable to auto-download. Please open the export link.', 'info', 'bottomRight');
        window.open(exportUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error exporting DB:', error);
      const errorMessage =
          error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to export database';
      showNotificationGlobal(errorMessage, 'error', 'bottomRight');
    } finally {
      setState(prev => ({ ...prev, exportingDb: false }));
    }
  }, [extractExportUrl, joinUrl, projectData?.id, triggerBrowserDownload]);

  const applyTransform = () => {
    if (!state.selectedElement) return;
    const t = state.transform;
    updateSelectedElementStyle({ transform: `translate(${t.translateX}px, ${t.translateY}px) rotate(${t.rotate}deg) scale(${t.scaleX}, ${t.scaleY})`, transformOrigin: "center center", filter: buildFilter(state.filters) });
  };

  const applyCrop = (nextCrop) =>
      updateSelectedElementStyle({ clipPath: `inset(${nextCrop.top}% ${nextCrop.right}% ${nextCrop.bottom}% ${nextCrop.left}%)` });

  const handleResetFilters = useCallback(() => {
    const resetFilters   = { brightness: 100, contrast: 100, saturation: 100, exposure: 100, blur: 0, hue: 0 };
    const resetTransform = { ...state.transform, rotate: 0 };
    const resetCrop      = { top: 0, right: 0, bottom: 0, left: 0 };
    setState((p) => ({ ...p, imageFilters: resetFilters, transform: resetTransform, crop: resetCrop }));
    if (state.selectedElement?.id && previewIframeRef.current) {
      const runtimeId = state.selectedElement.id;
      const componentId = getSelectionDataElementId(state.selectedElement);
      scheduleUpdateComponentPostMessage(
          { type: "UPDATE_COMPONENT", runtimeId, componentId, property: "filter", value: buildImageFilter(resetFilters) },
          { immediate: true }
      );
      updateSelectedElementStyle({ transform: `translate(${resetTransform.translateX}px, ${resetTransform.translateY}px) rotate(0deg) scale(1, 1)`, transformOrigin: "center center", filter: buildFilter(state.filters), clipPath: "inset(0% 0% 0% 0%)" });
    }
  }, [state.selectedElement?.id, state.transform, state.filters]);

  // ─── Chat ─────────────────────────────────────────────────────────────────

  React.useEffect(() => {
    const t = setTimeout(() => setState((p) => ({ ...p, chatLoaded: true })), CHAT_LOAD_DELAY);
    return () => clearTimeout(t);
  }, []);

  const clearIframeHighlight = useCallback(() => {
    previewIframeRef.current?.contentWindow?.postMessage({ type: "CLEAR_HIGHLIGHT" }, getIframeTargetOrigin());
  }, []);

  // ─── postMessage handler ──────────────────────────────────────────────────

  React.useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data || typeof event.data !== "object") return;
      const {
        type,
        element,
        fingerprint,
        runtimeId: msgRuntimeId,
        elementId,
        componentId,
        changedElement,
        svgOuterHTML,
        iconContent,
        isIconUpdate,
      } = event.data;

      const runtimeId = msgRuntimeId || elementId || componentId || fingerprint?.elementId || null;

      // ── SELECT_COMPONENT ─────────────────────────────────────────────────
      if (type === "SELECT_COMPONENT") {
        const incomingElement = element || null;
        const dataElementId =
            incomingElement?.elementId ??
            incomingElement?.id ??
            fingerprint?.elementId ??
            elementId ??
            msgRuntimeId ??
            incomingElement?.componentId ??
            componentId ??
            null;

        if (!incomingElement || !dataElementId) return;

        cancelScheduledUpdateComponentRef.current();
        if (event.data.activePagePath) {
          activePagePathRef.current = event.data.activePagePath;
        }

        const resolvedFilePath =
            resolveMessageFilePath(event.data.filePath || incomingElement.filePath) || null;

        if (resolvedFilePath) {
          runtimeIdToFileRef.current?.set(dataElementId, resolvedFilePath);
        }

        const isSvg =
            incomingElement.tag === "svg" ||
            String(incomingElement.tagName || "").toLowerCase() === "svg" ||
            incomingElement.type === "icon";

        if (isSvg) {
          selectedIconNameRef.current     = incomingElement.iconContent || iconContent || null;
          selectedSvgOuterHTMLRef.current = incomingElement.svgOuterHTML || svgOuterHTML || null;
        } else {
          selectedIconNameRef.current     = null;
          selectedSvgOuterHTMLRef.current = null;
        }

        const normalizedElement = {
          ...incomingElement,
          id: dataElementId,
          elementId: dataElementId,
          // Parent source-patching treats componentId as data-element-id
          componentId: dataElementId,
          mapComponentId:
              event.data.mapComponentId ||
              incomingElement.mapComponentId ||
              incomingElement.componentId ||
              dataElementId,
          mapTagName:
              event.data.mapTagName ||
              incomingElement.mapTagName ||
              null,
          filePath: resolvedFilePath,
        };

        const selectorKey = getSelectorKeyForElement(normalizedElement);
        const savedEvents = getSavedEventConfigForElement(selectorKey);

        setState((prev) => ({
          ...prev,
          selectedElement: normalizedElement,
          highlightBox: null,
          selectedElementSelectorKey: selectorKey,
          selectedElementEventConfig: savedEvents,
        }));

        // Resolve filePath for this elementId, fetch fresh source (no appCodes cache),
        // and only keep the path mapping — edits will paste into editedCodes later.
        if (resolvedFilePath && projectIdRef.current && codeSyncUserIdRef.current) {
          (async () => {
            try {
              const loaded = await loadSourceFileByPath({
                filePath: resolvedFilePath,
                editedCodes: [],
                appCodes: [],
                projectId: projectIdRef.current,
                userId: codeSyncUserIdRef.current,
                settingsTab: developModeRef.current,
                forceFresh: true,
                useSessionEdits: false,
              });
              if (!loaded?.contents && loaded?.contents !== "") {
                console.warn(
                    "[SELECT_COMPONENT] fresh source not found for",
                    dataElementId,
                    resolvedFilePath
                );
                return;
              }

              const fileName = resolveMessageFilePath(loaded.name) || resolvedFilePath;
              runtimeIdToFileRef.current?.set(dataElementId, fileName);

              // If no saved On Click mapping, recover workflow from injected source.
              const parsedOrch = parseCallOrchestratorFromSource(loaded.contents, dataElementId);
              const existingEvents = getSavedEventConfigForElement(selectorKey);
              const hasSavedWorkflow = Boolean(existingEvents?.onClick?.workflowId);

              setState((prev) => {
                const sameSelection = prev.selectedElement?.id === dataElementId;
                let nextEventConfig = prev.selectedElementEventConfig;

                if (sameSelection && parsedOrch?.workflowId && !hasSavedWorkflow) {
                  nextEventConfig = {
                    ...(prev.selectedElementEventConfig || {}),
                    onClick: {
                      enabled: true,
                      actionType: "workflow",
                      executionType: "ORCHESTRATOR",
                      workflowId: parsedOrch.workflowId,
                      query: parsedOrch.query || "",
                      workflowName:
                          prev.selectedElementEventConfig?.onClick?.workflowName ||
                          `Orchestrator ${parsedOrch.workflowId}`,
                      orchestrator: {
                        method: "POST",
                        endpointTemplate: "/orchestrator/execute/{workflowId}",
                        query: parsedOrch.query || "",
                      },
                    },
                  };
                }

                return {
                  ...prev,
                  selectedElement: sameSelection
                      ? { ...prev.selectedElement, filePath: fileName }
                      : prev.selectedElement,
                  selectedElementEventConfig: sameSelection
                      ? nextEventConfig
                      : prev.selectedElementEventConfig,
                };
              });
            } catch (err) {
              console.warn("[SELECT_COMPONENT] failed to load fresh source for", resolvedFilePath, err);
            }
          })();
        }

        return;
      }

      // ── COMPONENT_UPDATED ────────────────────────────────────────────────
      if (type === "COMPONENT_UPDATED") {
        if (!runtimeId || !changedElement) return;

        const dataElementId =
            event.data.elementId ??
            fingerprint?.elementId ??
            runtimeId ??
            componentId ??
            null;

        const fp = {
          nativeId:      event.data.nativeId      ?? fingerprint?.nativeId      ?? null,
          className:     event.data.className     ?? fingerprint?.className     ?? "",
          fullClassName: event.data.fullClassName ?? fingerprint?.fullClassName ?? "",
          tagName:       event.data.tagName       ?? fingerprint?.tagName       ?? "div",
          textSnippet:   event.data.textSnippet   ?? fingerprint?.textSnippet   ?? "",
          nthChild:      event.data.nthChild      ?? fingerprint?.nthChild      ?? 0,
          siblingIndex:  event.data.siblingIndex  ?? fingerprint?.siblingIndex ?? 0,
          parentTag:     event.data.parentTag     ?? fingerprint?.parentTag     ?? "body",
          parentId:      event.data.parentId      ?? fingerprint?.parentId      ?? null,
          ancestorPath:  event.data.ancestorPath  ?? fingerprint?.ancestorPath  ?? [],
          runtimeId,
          componentId:   dataElementId,
          elementId:     dataElementId,
        };

        const messageFilePath =
            resolveMessageFilePath(event.data.filePath) ||
            resolveMessageFilePath(selectedElementRef.current?.filePath) ||
            runtimeIdToFileRef.current?.get(dataElementId || runtimeId) ||
            null;

        if (messageFilePath && dataElementId) {
          runtimeIdToFileRef.current?.set(dataElementId, messageFilePath);
        }

        (async () => {
          try {
            const updateProperty = String(event.data.property || "").toLowerCase();
            const valueIsStyleObject =
                event.data.value &&
                typeof event.data.value === "object" &&
                !Array.isArray(event.data.value);
            const isStyleLikeUpdate =
                updateProperty === "style" ||
                updateProperty === "color" ||
                updateProperty === "fill" ||
                updateProperty === "stroke" ||
                valueIsStyleObject;
            const shouldReplaceIcon =
                Boolean(isIconUpdate) &&
                (updateProperty === "icon" || updateProperty === "svg" || (!updateProperty && !valueIsStyleObject));

            // Icon swaps still use dedicated SVG replace
            if (shouldReplaceIcon && !isStyleLikeUpdate) {
              let result = null;
              if (messageFilePath) {
                result = await loadSourceFileByPath({
                  filePath: messageFilePath,
                  editedCodes: editedCodesRef.current || [],
                  appCodes: [],
                  projectId: projectIdRef.current,
                  userId: codeSyncUserIdRef.current,
                  settingsTab: developModeRef.current,
                  forceFresh: true,
                  useSessionEdits: true,
                });
              }
              if (!result) {
                result = findSourceFile(
                    [],
                    editedCodesRef.current || [],
                    fp,
                    {
                      preferJsx: true,
                      oldIconName: selectedIconNameRef.current || null,
                      activePagePath: event.data.activePagePath || activePagePathRef.current,
                    }
                );
              }
              if (!result) {
                console.warn("[COMPONENT_UPDATED] icon source not found", dataElementId, messageFilePath);
                return;
              }

              const { name: fileToUpdate, contents: sourceContents } = result;
              runtimeIdToFileRef.current?.set(dataElementId || runtimeId, fileToUpdate);

              const newIconName = resolveLucideIconName(
                  event.data.iconName,
                  event.data.iconContent,
                  event.data.value,
                  iconContent
              );
              const oldIconName = resolveLucideIconName(
                  selectedIconNameRef.current,
                  selectedElementRef.current?.iconContent
              );
              if (!newIconName) {
                console.warn("[COMPONENT_UPDATED] icon update missing PascalCase iconName", {
                  value: event.data.value,
                  iconContent,
                });
              }
              let updatedContents = replaceSvgInSource(
                  sourceContents,
                  newIconName || stripPreviewAttributes(safeConvertToJsx(changedElement)),
                  fp,
                  oldIconName,
                  selectedSvgOuterHTMLRef.current || svgOuterHTML || null,
                  {
                    className: event.data.className || fp.fullClassName || fp.className || "",
                    style: pickIconStyles(
                        (event.data.style && typeof event.data.style === "object" && event.data.style) ||
                        selectedElementRef.current?.style ||
                        null
                    ),
                    dataElementId: dataElementId || event.data.elementId || null,
                  }
              );

              if (newIconName && updatedContents !== sourceContents) {
                updatedContents = patchLucideImport(
                    updatedContents,
                    oldIconName,
                    newIconName
                );
              }
              if (newIconName) selectedIconNameRef.current = newIconName;
              if (svgOuterHTML) selectedSvgOuterHTMLRef.current = svgOuterHTML;

              const latestEdited = editedCodesRef.current || [];
              const updatedCodes = latestEdited.some(
                  (c) => c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
              )
                  ? latestEdited.map((c) =>
                      c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
                          ? { ...c, name: fileToUpdate, contents: updatedContents }
                          : c
                  )
                  : [...latestEdited, { name: fileToUpdate, contents: updatedContents }];

              editedCodesRef.current = updatedCodes;
              setState((prev) => ({ ...prev, editedCodes: updatedCodes, hasUnsavedChanges: true }));
              return;
            }

            // Canonical path: componentId → filePath → source → editedCodes
            const property = isStyleLikeUpdate
                ? "style"
                : event.data.property || inferUpdateProperty(event.data);
            const value = isStyleLikeUpdate
                ? (event.data.value || event.data.style)
                : event.data.value;

            const patchResult = await patchEditedCodeByComponentId({
              componentId: dataElementId,
              filePath: messageFilePath,
              property,
              value,
              fingerprint: {
                ...fp,
                textSnippet:
                    selectedElementRef.current?.fingerprint?.textSnippet ||
                    event.data.previousText ||
                    fp.textSnippet,
              },
              eventData: {
                ...event.data,
                elementId: dataElementId,
                componentId: dataElementId,
                runtimeId: dataElementId,
                previousText:
                    event.data.previousText ||
                    selectedElementRef.current?.fingerprint?.textSnippet ||
                    null,
                mapTagName:
                    event.data.mapTagName ||
                    selectedElementRef.current?.mapTagName ||
                    null,
              },
              previousText:
                  event.data.previousText ||
                  selectedElementRef.current?.fingerprint?.textSnippet ||
                  null,
              mapTagName:
                  event.data.mapTagName ||
                  selectedElementRef.current?.mapTagName ||
                  null,
              editedCodes: editedCodesRef.current || [],
              appCodes: appCodesRef.current || [],
              runtimeIdToFile: runtimeIdToFileRef.current,
              projectId: projectIdRef.current,
              userId: codeSyncUserIdRef.current,
              settingsTab: developModeRef.current,
              activePagePath: event.data.activePagePath || activePagePathRef.current,
            });

            // Don't clobber a newer successful eager patch with a no-op
            if (!patchResult.patched && patchResult.fileToUpdate) {
              const existing = (editedCodesRef.current || []).find((c) =>
                  c.name === patchResult.fileToUpdate ||
                  pathsLooselyEqual(c.name, patchResult.fileToUpdate)
              );
              if (existing) return;
            }

            if (patchResult.reason === "source-not-found") {
              const pendingKey = `__pending__${runtimeId}`;
              const pendingEntry = {
                name: pendingKey,
                contents: changedElement,
                isPending: true,
                runtimeId,
                nativeId: fp.nativeId,
              };
              const updatedCodes = (editedCodesRef.current || []).some((c) => c.name === pendingKey)
                  ? (editedCodesRef.current || []).map((c) =>
                      c.name === pendingKey ? pendingEntry : c
                  )
                  : [...(editedCodesRef.current || []), pendingEntry];
              editedCodesRef.current = updatedCodes;
              setState((prev) => ({ ...prev, editedCodes: updatedCodes, hasUnsavedChanges: true }));
              return;
            }

            mergeEditedCodesIntoState(editedCodesRef, setState, patchResult, {
              filePath: patchResult.fileToUpdate || selectedElementRef.current?.filePath,
            });
          } catch (err) {
            console.error("[COMPONENT_UPDATED] failed to load/patch source:", err);
          }
        })();

        return;
      }
      if (type === "REQUEST_ICON_SVG") {
        const { iconName } = event.data;
        const reqRuntimeId =
            event.data.runtimeId ||
            event.data.elementId ||
            event.data.componentId ||
            null;
        if (!iconName || !reqRuntimeId) return;

        // Generate SVG on the ProjectView side using dynamic import
        // (react-dom/server works in the ProjectView context)
        const generateAndSend = async () => {
          try {
            const IconComponent = LucideIcons[iconName];
            if (!IconComponent) {
              console.warn("[REQUEST_ICON_SVG] Unknown icon:", iconName);
              return;
            }

            let svgString = null;

            // Method 1: react-dom/server renderToStaticMarkup (works in Vite/CRA)
            try {
              const { renderToStaticMarkup } = await import("react-dom/server");
              svgString = renderToStaticMarkup(
                  React.createElement(IconComponent, {
                    width: 24, height: 24,
                    stroke: "currentColor", strokeWidth: 2, fill: "none",
                  })
              );
            } catch (serverErr) {
              console.debug("[REQUEST_ICON_SVG] renderToStaticMarkup failed, trying DOM method:", serverErr.message);
            }

            // Method 2: render into temporary DOM container
            if (!svgString) {
              await new Promise((resolve) => {
                const container = document.createElement("div");
                container.style.cssText = "position:absolute;visibility:hidden;pointer-events:none;top:-9999px";
                document.body.appendChild(container);

                try {
                  // Use React 18 createRoot if available
                  const ReactDOM = require("react-dom/client") || require("react-dom");
                  const root = ReactDOM.createRoot
                      ? ReactDOM.createRoot(container)
                      : null;

                  const cleanup = () => {
                    try {
                      root ? root.unmount() : ReactDOM.unmountComponentAtNode(container);
                    } catch {}
                    document.body.removeChild(container);
                  };

                  const rendered = React.createElement(IconComponent, {
                    width: 24, height: 24, stroke: "currentColor", strokeWidth: 2,
                  });

                  if (root) {
                    root.render(rendered);
                  } else {
                    ReactDOM.render(rendered, container);
                  }

                  setTimeout(() => {
                    const svgEl = container.querySelector("svg");
                    if (svgEl) svgString = svgEl.outerHTML;
                    cleanup();
                    resolve(null);
                  }, 50);
                } catch (domErr) {
                  document.body.removeChild(container);
                  resolve(null);
                }
              });
            }

            if (!svgString) {
              console.warn("[REQUEST_ICON_SVG] Could not generate SVG for:", iconName);
              return;
            }

            // Send SVG markup for preview DOM + keep PascalCase iconName for Lucide source patch
            scheduleUpdateComponentPostMessage(
                {
                  type:        "UPDATE_COMPONENT",
                  runtimeId:   reqRuntimeId,
                  elementId:   reqRuntimeId,
                  componentId: reqRuntimeId,
                  property:    "icon",
                  value:       svgString,
                  iconName,
                },
                { immediate: true }
            );

          } catch (err) {
            console.warn("[REQUEST_ICON_SVG] failed:", err);
          }
        };

        generateAndSend();
        return;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // ─── View / zoom / screen controls ────────────────────────────────────────


  const toggleSection = useCallback((sectionName) =>
      setState((p) => ({ ...p, expandedSections: { ...p.expandedSections, [sectionName]: !p.expandedSections[sectionName] } })), []);

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const currentEditedCodes = editedCodesRef.current || [];

    if (!currentEditedCodes.length) {
      showNotificationGlobal("No changes to save", "info", "bottomRight");
      return;
    }

    const cleanContents = (content) =>
        stripPreviewAttributes(
            content
                .replace(/\s*data-component-id="[^"]*"/g, "")
                .replace(/\s*data-component-type="[^"]*"/g, "")
        );

    const realEdits = currentEditedCodes
        .filter(
            (file) =>
                !file.isPending &&
                !String(file.name || "").startsWith("__pending__") &&
                !/\.svg$/i.test(file.name) &&
                typeof file.contents === "string" &&
                file.contents.length > 0
        )
        .map((file) => ({
          name: resolveMessageFilePath(file.name) || file.name,
          contents: cleanContents(file.contents),
        }));

    if (realEdits.length === 0) {
      const pendingCount = currentEditedCodes.filter(
          (f) => f.isPending || String(f.name || "").startsWith("__pending__")
      ).length;
      showNotificationGlobal(
          pendingCount > 0
              ? "Changes applied to preview only — source file path from element-map could not be loaded for sync."
              : "Changes applied to preview only — no source file found to save.",
          "info",
          "bottomRight"
      );
      return;
    }

    console.info("[handleSave] syncing files:", realEdits.map((f) => f.name));

    const codeSyncUserId =
        state.projectMasterUser ?? projectData?.userId;

    if (!codeSyncUserId) {
      showNotificationGlobal(
          "Unable to save: missing code sync user.",
          "error",
          "bottomRight"
      );
      return;
    }

    const payload = {
      projectId: projectData?.id,
      files: realEdits,
    };

    try {
      const response = await AgentService.syncCode(
          payload,
          codeSyncUserId,
          developMode
      );

      const isSuccess =
          response?.status === API_RESPONSE.SUCCESS;

      showNotificationGlobal(
          isSuccess
              ? "Code Saved Successfully.."
              : response?.errorMessage ||
              response?.message ||
              response?.status ||
              "Failed to sync code",
          isSuccess ? "success" : "error",
          "bottomRight"
      );

      if (isSuccess) {
        clearIframeHighlight();

        selectedIconNameRef.current = null;
        selectedSvgOuterHTMLRef.current = null;
        runtimeIdToFileRef.current = new Map();
        editedCodesRef.current = [];

        setState((prevState) => ({
          ...prevState,
          hasUnsavedChanges: false,
          selectedElement: null,
          highlightBox: null,
          editedCodes: [],
          isCodeEditEnabled: false,
          codeSyncTrigger: true,
          codeRefreshTrigger: (prevState.codeRefreshTrigger || 0) + 1,
        }));

        try {
          const refreshResponse = await UtilsService.getCodeview(
              projectData?.id,
              projectData?.userId,
              developMode
          );

          const data = refreshResponse?.data || [];

          if (data.length > 0) {
            appCodesRef.current = data;

            setState((prevState) => ({
              ...prevState,
              appCodes: data,
            }));

            if (previewIframeRef.current) {
              const currentSrc = previewIframeRef.current.src;
              const separator = currentSrc.includes("?") ? "&" : "?";

              previewIframeRef.current.src = `${currentSrc}${separator}_t=${Date.now()}`;
            }
          }
        } catch (err) {
          console.error("Error refreshing code after save:", err);
        }
      } else {
        setState((prevState) => ({
          ...prevState,
          hasUnsavedChanges: true,
        }));
      }
    } catch (error) {
      console.error("Error syncing code:", error);

      showNotificationGlobal(
          error?.response?.data?.errorMessage ||
          error?.message ||
          "Failed to sync code",
          "error",
          "bottomRight"
      );

      setState((prevState) => ({
        ...prevState,
        hasUnsavedChanges: true,
      }));
    }
  }, [
    projectData?.id,
    projectData?.userId,
    state.projectMasterUser,
    developMode,
    clearIframeHighlight,
  ]);

  // ─── Reset ────────────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    cancelScheduledUpdateComponentPostMessages();
    clearIframeHighlight();
    selectedIconNameRef.current    = null;
    selectedSvgOuterHTMLRef.current = null;
    runtimeIdToFileRef.current      = new Map();
    const dirtyPaths = (editedCodesRef.current || [])
        .map((c) => c?.name)
        .filter(Boolean);
    editedCodesRef.current = [];
    setState((p) => ({
      ...p,
      hasUnsavedChanges: false,
      selectedElement: null,
      highlightBox: null,
      editedCodes: [],
      isCodeEditEnabled: false,
      discardedFilePaths: dirtyPaths,
      // Toggle to re-fetch last-saved app codes; discard closes only changed tabs
      codeSyncTrigger: !p.codeSyncTrigger,
      codeDiscardTrigger: (p.codeDiscardTrigger || 0) + 1,
      previewReloadKey: (p.previewReloadKey || 0) + 1,
      previewLoaded: false,
      renderLoading: false,
    }));
  }, [clearIframeHighlight, cancelScheduledUpdateComponentPostMessages]);

  // ─── Property handlers ────────────────────────────────────────────────────

  const handleTextContentChange = useCallback((newText) => {
    pendingTextContentRef.current = newText;

    const selected = selectedElementRef.current;
    const runtimeId = selected?.id;
    if (previewIframeRef.current && runtimeId) {
      const elementId = getSelectionDataElementId(selected);
      scheduleUpdateComponentPostMessage(
          {
            type: "UPDATE_COMPONENT",
            runtimeId: elementId || runtimeId,
            elementId: elementId || runtimeId,
            componentId: elementId || runtimeId,
            value: newText,
            property: "textContent",
          }
      );
    }

    if (textContentPatchTimerRef.current) clearTimeout(textContentPatchTimerRef.current);
    textContentPatchTimerRef.current = setTimeout(async () => {
      textContentPatchTimerRef.current = null;
      const textToPatch = pendingTextContentRef.current;
      const selectedElement = selectedElementRef.current;
      if (!selectedElement?.id || textToPatch == null) return;

      try {
        // 1) component id  2) filePath  3) load source  4) update editedCodes
        const componentId = getSelectionDataElementId(selectedElement);
        const patchResult = await patchEditedCodeByComponentId({
          componentId,
          filePath: selectedElement.filePath,
          property: "textContent",
          value: textToPatch,
          fingerprint: buildSelectionFingerprint(selectedElement),
          previousText: selectedElement.fingerprint?.textSnippet || "",
          mapTagName: selectedElement.mapTagName || selectedElement.tag,
          editedCodes: editedCodesRef.current || [],
          appCodes: appCodesRef.current || [],
          runtimeIdToFile: runtimeIdToFileRef.current,
          projectId: projectIdRef.current,
          userId: codeSyncUserIdRef.current,
          settingsTab: developModeRef.current,
          activePagePath: activePagePathRef.current,
        });

        mergeEditedCodesIntoState(editedCodesRef, setState, patchResult, {
          textContent: textToPatch,
          text: textToPatch,
          innerText: textToPatch,
          filePath: patchResult.fileToUpdate || selectedElement.filePath,
        });
      } catch (err) {
        console.error("Error updating code for text content change:", err);
      }
    }, UPDATE_COMPONENT_DEBOUNCE_MS);
  }, [scheduleUpdateComponentPostMessage]);

  const handleOrchestratorOnClickChange = useCallback(async (config) => {
    if (!config) return;

    const isRemove = Boolean(config.remove || config.clear);
    if (!isRemove && (config.executionType !== "ORCHESTRATOR" || !config.workflowId)) {
      return;
    }

    const selectedElement = selectedElementRef.current;
    const runtimeId = selectedElement?.id;

    if (!runtimeId) {
      showNotificationGlobal(
          isRemove
              ? "Select an element before removing the orchestrator."
              : "Select an element before assigning an orchestrator.",
          "info",
          "bottomRight"
      );
      return;
    }

    const orchestratorId = isRemove ? "" : String(config.workflowId);
    const query = isRemove ? "" : String(config.query ?? config.orchestrator?.query ?? "");

    const dataElementId = getSelectionDataElementId(selectedElement);
    const filePath =
        resolveMessageFilePath(selectedElement?.filePath) ||
        resolveMessageFilePath(selectedElement?.sourceFilePath) ||
        runtimeIdToFileRef.current?.get(dataElementId || runtimeId) ||
        null;

    const fp = buildSelectionFingerprint({
      ...selectedElement,
      id: dataElementId,
      elementId: dataElementId,
      componentId: dataElementId,
      filePath,
    });
    const patchFingerprint = { ...fp, componentId: dataElementId, elementId: dataElementId };
    const currentEditedCodes = editedCodesRef.current || [];

    try {
      let result = null;

      if (filePath && isLikelySharedUiKitPath(filePath)) {
        showNotificationGlobal(
            "Selected file looks like a shared Button component. Select the page usage instead.",
            "warning",
            "bottomRight"
        );
        return;
      }

      if (filePath) {
        result = await loadSourceFileByPath({
          filePath,
          editedCodes: currentEditedCodes,
          appCodes: [],
          projectId: projectIdRef.current,
          userId: codeSyncUserIdRef.current,
          settingsTab: developModeRef.current,
          forceFresh: true,
          useSessionEdits: true,
        });
      }

      if (!result || !findJsxElementRangeByDataElementId(result.contents, dataElementId)) {
        const fromSession = findSourceFile([], currentEditedCodes, patchFingerprint, {
          activePagePath: activePagePathRef.current,
        });
        if (
            fromSession &&
            !isLikelySharedUiKitPath(fromSession.name) &&
            findJsxElementRangeByDataElementId(fromSession.contents, dataElementId)
        ) {
          result = fromSession;
        } else {
          result = null;
        }
      }

      if (!result) {
        showNotificationGlobal(
            "Could not find usage-site source for this data-element-id.",
            "info",
            "bottomRight"
        );
        return;
      }

      const { name: fileToUpdate, contents: sourceContents } = result;
      const updatedContents = isRemove
          ? removeOnClickHandler(sourceContents, patchFingerprint)
          : injectOnClickHandler(sourceContents, patchFingerprint, orchestratorId, query);

      if (updatedContents === sourceContents) {
        if (isRemove) {
          showNotificationGlobal(
              "No orchestrator onClick found on this element in source.",
              "info",
              "bottomRight"
          );
          return;
        }

        const range = resolvePatchTargetRange(sourceContents, patchFingerprint, dataElementId);
        const tag = range
            ? sourceContents.substring(range.openStart, range.openEnd)
            : "";
        const alreadyAssigned = Boolean(
            tag &&
            new RegExp(
                String.raw`callOrchestrator\(\s*["']${escapeRegex(String(orchestratorId))}["'](?:\s*,\s*[^)]*)?\s*\)`
            ).test(tag)
        );

        if (alreadyAssigned) {
          runtimeIdToFileRef.current?.set(dataElementId || runtimeId, fileToUpdate);
          showNotificationGlobal("Orchestrator already assigned to this element.", "info", "bottomRight");
          try {
            await ensureOrchestratorApiTokenInEnv({
              projectId: projectIdRef.current,
              userId: codeSyncUserIdRef.current,
            });
          } catch (envErr) {
            console.error("[handleOrchestratorOnClickChange] .env token update failed:", envErr);
          }
          return;
        }

        console.warn("[handleOrchestratorOnClickChange] inject failed:", {
          file: fileToUpdate,
          dataElementId,
          tagName: fp.tagName,
          className: fp.className,
        });
        showNotificationGlobal(
            "Could not inject onClick handler into source.",
            "warning",
            "bottomRight"
        );
        return;
      }

      runtimeIdToFileRef.current?.set(dataElementId || runtimeId, fileToUpdate);

      const updatedCodes = currentEditedCodes.some((c) =>
          c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
      )
          ? currentEditedCodes.map((c) =>
              c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
                  ? { ...c, name: fileToUpdate, contents: updatedContents, isPending: false }
                  : c
          )
          : [...currentEditedCodes, { name: fileToUpdate, contents: updatedContents }];

      editedCodesRef.current = updatedCodes;
      setState((prev) => ({
        ...prev,
        editedCodes: updatedCodes,
        hasUnsavedChanges: true,
      }));

      if (isRemove) {
        showNotificationGlobal("Orchestrator onClick removed from source.", "success", "bottomRight");
        return;
      }

      try {
        await ensureOrchestratorApiTokenInEnv({
          projectId: projectIdRef.current,
          userId: codeSyncUserIdRef.current,
        });
      } catch (envErr) {
        console.error("[handleOrchestratorOnClickChange] .env token update failed:", envErr);
        showNotificationGlobal(
            "Orchestrator assigned, but failed to write ORCHESTRATOR_API_TOKEN to backend .env.",
            "warning",
            "bottomRight"
        );
      }
    } catch (err) {
      console.error("[handleOrchestratorOnClickChange] failed:", err);
      showNotificationGlobal(
          isRemove
              ? "Failed to remove orchestrator onClick handler."
              : "Failed to inject orchestrator onClick handler.",
          "error",
          "bottomRight"
      );
    }
  }, []);

  const updateParentState = useCallback((s) => setState((p) => ({ ...p, enableEdit: s?.enableEdit })), []);
  const normalizeFontFamily = useCallback((ff) => (!ff ? "" : ff.trim().replace(/\s+/g, " ").replace(/"/g, "'")), []);

  const handlePropertyChange = useCallback((property, value) => {
    const selected = selectedElementRef.current;
    if (previewIframeRef.current && selected?.id) {
      const styleProperties = [
        "backgroundColor","background",
        "color","fill","stroke",
        "border","borderWidth","borderStyle","borderColor","borderRadius",
        "padding","margin",
        "width","height",
        "fontSize","fontWeight","fontFamily","textAlign",
        "display","position","top","left","right","bottom","zIndex",
        "opacity","transform","transition",
      ];
      const runtimeId = selected.id;
      const componentId = getSelectionDataElementId(selected);
      const isStyleProp = styleProperties.includes(property);
      scheduleUpdateComponentPostMessage(
          isStyleProp
              ? { type: "UPDATE_COMPONENT", runtimeId, elementId: componentId, componentId, property: "style", value: { [property]: value } }
              : { type: "UPDATE_COMPONENT", runtimeId, elementId: componentId, componentId, value, property }
      );

      // 1) component id  2) filePath  3) load source  4) update editedCodes
      (async () => {
        try {
          const patchResult = await patchEditedCodeByComponentId({
            componentId,
            filePath: selected.filePath,
            property: isStyleProp ? "style" : property,
            value: isStyleProp ? { [property]: value } : value,
            fingerprint: buildSelectionFingerprint({
              ...selected,
              style: { ...selected?.style, [property]: value },
            }),
            mapTagName: selected.mapTagName || selected.tag,
            previousText: selected.fingerprint?.textSnippet || "",
            editedCodes: editedCodesRef.current || [],
            appCodes: appCodesRef.current || [],
            runtimeIdToFile: runtimeIdToFileRef.current,
            projectId: projectIdRef.current,
            userId: codeSyncUserIdRef.current,
            settingsTab: developModeRef.current,
            activePagePath: activePagePathRef.current,
          });

          mergeEditedCodesIntoState(editedCodesRef, setState, patchResult, {
            style: { ...selected?.style, [property]: value },
            filePath: patchResult.fileToUpdate || selected.filePath,
          });
        } catch (err) {
          console.warn("[handlePropertyChange] source patch failed:", err);
          setState((prev) => ({
            ...prev,
            selectedElement: {
              ...prev.selectedElement,
              style: { ...prev.selectedElement?.style, [property]: value },
            },
            hasUnsavedChanges: true,
          }));
        }
      })();
      return;
    }

    setState((prev) => ({
      ...prev,
      selectedElement: {
        ...prev.selectedElement,
        style: { ...prev.selectedElement?.style, [property]: value },
      },
      hasUnsavedChanges: true,
    }));
  }, [scheduleUpdateComponentPostMessage]);

  /**
   * handleIconChange
   *
   * Called by PropertiesPanel when IconSelect fires onChange(iconName).
   * iconName is PascalCase: "ArrowRight", "FileText", etc.
   *
   * 1. Preview: UPDATE_COMPONENT with Lucide name (iframe may REQUEST_ICON_SVG)
   * 2. Source: componentId → filePath → load → replaceSvgInSource + patchLucideImport → editedCodes
   */
  const handleIconChange = useCallback((iconName) => {
    if (!isLucideIconName(iconName)) return;

    const selected = selectedElementRef.current;
    const componentId = getSelectionDataElementId(selected);
    if (!selected?.id || !componentId) return;

    const oldIconName = resolveLucideIconName(
        selectedIconNameRef.current,
        selected.iconContent
    );
    // Keep old SVG markup for class/data-icon matching before preview swaps
    const oldSvgOuterHTML =
        selectedSvgOuterHTMLRef.current ||
        selected.svgOuterHTML ||
        null;

    selectedIconNameRef.current = iconName;

    // Preview DOM update via existing Lucide request/render path
    if (previewIframeRef.current) {
      scheduleUpdateComponentPostMessage(
          {
            type: "UPDATE_COMPONENT",
            runtimeId: componentId,
            elementId: componentId,
            componentId,
            property: "icon",
            value: iconName,
            iconName,
          },
          { immediate: true }
      );
    }

    // Eager Lucide source patch so Save/sync gets <NewIcon /> + import update
    (async () => {
      try {
        const filePath =
            resolveMessageFilePath(selected.filePath) ||
            runtimeIdToFileRef.current?.get(componentId) ||
            null;

        let sourceResult = null;
        if (filePath) {
          sourceResult = await loadSourceFileByPath({
            filePath,
            editedCodes: editedCodesRef.current || [],
            appCodes: [],
            projectId: projectIdRef.current,
            userId: codeSyncUserIdRef.current,
            settingsTab: developModeRef.current,
            forceFresh: true,
            useSessionEdits: true,
          });
        }

        const fp = buildSelectionFingerprint({
          ...selected,
          iconContent: iconName,
        });

        if (!sourceResult) {
          sourceResult = findSourceFile(
              [],
              editedCodesRef.current || [],
              fp,
              {
                preferJsx: true,
                oldIconName,
                activePagePath: activePagePathRef.current,
              }
          );
        }

        if (!sourceResult?.contents && sourceResult?.contents !== "") {
          console.warn("[handleIconChange] source not found", componentId, filePath);
          setState((prev) => ({
            ...prev,
            selectedElement: {
              ...prev.selectedElement,
              iconContent: iconName,
            },
            hasUnsavedChanges: true,
          }));
          return;
        }

        const fileToUpdate = sourceResult.name;
        runtimeIdToFileRef.current?.set(componentId, fileToUpdate);

        let updatedContents = replaceSvgInSource(
            sourceResult.contents,
            iconName,
            fp,
            oldIconName,
            oldSvgOuterHTML,
            {
              className: selected.className || selected.fingerprint?.fullClassName || "",
              style: pickIconStyles(selected.style),
              dataElementId: componentId,
            }
        );

        if (updatedContents !== sourceResult.contents) {
          updatedContents = patchLucideImport(updatedContents, oldIconName, iconName);
        } else {
          console.warn("[handleIconChange] Lucide replace produced no change", {
            componentId,
            fileToUpdate,
            oldIconName,
            iconName,
          });
        }

        const latestEdited = editedCodesRef.current || [];
        const updatedCodes = latestEdited.some(
            (c) => c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
        )
            ? latestEdited.map((c) =>
                c.name === fileToUpdate || pathsLooselyEqual(c.name, fileToUpdate)
                    ? { ...c, name: fileToUpdate, contents: updatedContents }
                    : c
            )
            : [...latestEdited, { name: fileToUpdate, contents: updatedContents }];

        editedCodesRef.current = updatedCodes;
        setState((prev) => ({
          ...prev,
          selectedElement: {
            ...prev.selectedElement,
            iconContent: iconName,
            filePath: fileToUpdate,
          },
          editedCodes: updatedCodes,
          hasUnsavedChanges: true,
        }));

        console.info("[handleIconChange] patched Lucide icon", {
          componentId,
          fileToUpdate,
          oldIconName,
          iconName,
          changed: updatedContents !== sourceResult.contents,
        });
      } catch (err) {
        console.error("[handleIconChange] failed:", err);
        setState((prev) => ({
          ...prev,
          selectedElement: {
            ...prev.selectedElement,
            iconContent: iconName,
          },
          hasUnsavedChanges: true,
        }));
      }
    })();
  }, [scheduleUpdateComponentPostMessage]);

  const adjustNumericValue = useCallback((property, currentValue, direction) => {
    if (!currentValue) { handlePropertyChange(property, direction === "increment" ? "1px" : "0px"); return; }
    const match = currentValue.toString().match(/^(-?\d+(?:\.\d+)?)(.*)$/);
    if (!match)  { handlePropertyChange(property, direction === "increment" ? "1px" : "0px"); return; }
    const n = parseFloat(match[1]), unit = match[2] || "px";
    handlePropertyChange(property, `${Math.max(0, direction === "increment" ? n + 1 : n - 1)}${unit}`);
  }, [handlePropertyChange]);

  const parseBorder = useCallback((borderString) => {
    if (!borderString || borderString === "none" || borderString === "0") return { width: "0px", style: "solid", color: "#000000" };
    const full  = borderString.match(/^(\d+(?:\.\d+)?(?:px|em|rem|%)?)\s+(none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)\s+(.+)$/i);
    if (full)  return { width: full[1]  || "0px", style: full[2]  || "solid", color: full[3]  || "#000000" };
    const sOnly = borderString.match(/^(\d+(?:\.\d+)?(?:px|em|rem|%)?)\s+(none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)$/i);
    if (sOnly) return { width: sOnly[1] || "0px", style: sOnly[2] || "solid", color: "#000000" };
    const wOnly = borderString.match(/^(\d+(?:\.\d+)?(?:px|em|rem|%)?)$/);
    if (wOnly) return { width: wOnly[1] || "0px", style: "solid", color: "#000000" };
    return { width: "0px", style: "solid", color: "#000000" };
  }, []);

  const combineBorder = useCallback((width, style, color) => {
    if (!width || width === "0px" || width === "0" || style === "none") return "none";
    return `${width} ${style} ${color}`;
  }, []);

  const handleBorderPropertyChange = useCallback((property, value) => {
    setState((prev) => {
      const parts     = parseBorder(prev.selectedElement?.style?.border || "");
      const newBorder = property === "borderWidth" ? combineBorder(value, parts.style, parts.color) :
          property === "borderStyle" ? combineBorder(parts.width, value, parts.color) :
              property === "borderColor" ? combineBorder(parts.width, parts.style, value) : value;
      if (previewIframeRef.current && prev.selectedElement?.id) {
        const runtimeId = prev.selectedElement.id;
        const componentId = getSelectionDataElementId(prev.selectedElement);
        scheduleUpdateComponentPostMessage(
            { type: "UPDATE_COMPONENT", runtimeId, componentId, property: "style", value: { border: newBorder } }
        );
      }
      return { ...prev, selectedElement: { ...prev.selectedElement, style: { ...prev.selectedElement?.style, border: newBorder } }, hasUnsavedChanges: true };
    });
  }, [parseBorder, combineBorder]);

  const adjustBorderWidth = useCallback((direction) => {
    setState((prev) => {
      const parts = parseBorder(prev.selectedElement?.style?.border || "");
      const match = (parts.width || "0px").match(/^(-?\d+(?:\.\d+)?)(.*)$/);
      const n = match ? parseFloat(match[1]) : 0, unit = match ? match[2] || "px" : "px";
      const newB = combineBorder(`${Math.max(0, direction === "increment" ? n + 1 : n - 1)}${unit}`, parts.style, parts.color);
      if (previewIframeRef.current && prev.selectedElement?.id) {
        const runtimeId = prev.selectedElement.id;
        const componentId = getSelectionDataElementId(prev.selectedElement);
        scheduleUpdateComponentPostMessage(
            { type: "UPDATE_COMPONENT", runtimeId, componentId, property: "style", value: { border: newB } }
        );
      }
      return { ...prev, selectedElement: { ...prev.selectedElement, style: { ...prev.selectedElement?.style, border: newB } }, hasUnsavedChanges: true };
    });
  }, [parseBorder, combineBorder]);

  // ─── Image upload ─────────────────────────────────────────────────────────

  const handleImageUpload = useCallback(async (file) => {
    const validation = validateImageFile(file, { maxSizeBytes: MAX_IMAGE_UPLOAD_SIZE, allowedMimeTypes: ALLOWED_IMAGE_MIME_TYPES, allowedLabel: `image up to ${formatMB(MAX_IMAGE_UPLOAD_SIZE)}` });
    if (!validation.ok) { showNotificationGlobal(validation.error, "error", "topCenter"); return; }
    const projectId = projectData?.id;
    if (!projectId) { showNotificationGlobal("Project ID not available", "error", "topCenter"); return; }
    const masterUserId = codeSyncUserIdRef.current || projectData?.userId;
    if (!masterUserId) {
      showNotificationGlobal("Unable to upload.", "error", "topCenter");
      return;
    }
    let selectedElementId = null;
    setState((p) => { selectedElementId = p.selectedElement?.id; return { ...p, uploadingImage: true }; });
    if (!selectedElementId) { showNotificationGlobal("Please select an element to upload image", "error", "topCenter"); setState((p) => ({ ...p, uploadingImage: false })); return; }
    try {
      const formData = new FormData(); formData.append("file", file);
      const imageResponse = await AgentService.uploadImage(projectId, formData, masterUserId);
      const imageUrl      = imageResponse?.path;
      if (!imageUrl) throw new Error("Image URL not returned from server");
      const relativeImageUrl = getRelativeImagePath(imageUrl);
      setState((prev) => {
        const updatedElement = { ...prev.selectedElement, imageContent: relativeImageUrl, style: { ...prev.selectedElement?.style, backgroundImage: `url(${relativeImageUrl})` } };
        const currentAppCodes    = appCodesRef.current    || [];
        const currentEditedCodes = editedCodesRef.current || [];
        const fp = prev.selectedElement?.fingerprint ?? { nativeId: prev.selectedElement?.nativeId ?? null, className: prev.selectedElement?.className ?? "", tagName: prev.selectedElement?.tag ?? "div", textSnippet: prev.selectedElement?.textContent ?? "", nthChild: 0, parentTag: "body", parentId: null, runtimeId: prev.selectedElement?.id };
        const result     = findSourceFile(currentAppCodes, currentEditedCodes, fp);
        let updatedCodes = currentEditedCodes;
        if (result) {
          try {
            const { contents: sourceContents, name: fileToUpdate } = result;
            const tagName = updatedElement.tagName || "div";
            const attrs   = [];
            if (updatedElement.nativeId || updatedElement.elementRect?.elementId) attrs.push(`id="${updatedElement.nativeId || updatedElement.elementRect?.elementId}"`);
            if (fp.className) attrs.push(`className="${fp.className}"`);
            if (relativeImageUrl) { if (tagName.toLowerCase() === "img") attrs.push(`src="${relativeImageUrl}"`); else attrs.push(`imageContent="${relativeImageUrl}"`); }
            const styleObj = updatedElement.style || {};
            if (Object.keys(styleObj).length > 0) { const styleStr = Object.entries(styleObj).map(([k, v]) => `${k.replace(/([A-Z])/g, "-$1").toLowerCase()}:${v}`).join("; "); if (styleStr) attrs.push(`style="${styleStr}"`); }
            const attrsStr = attrs.length > 0 ? " " + attrs.join(" ") : "";
            let elementHtml = `<${tagName}${attrsStr}>`; if (updatedElement.textContent) elementHtml += updatedElement.textContent; elementHtml += `</${tagName}>`;
            const changedJsx    = safeConvertToJsx(elementHtml);
            const updatedSource = replaceInSource(sourceContents, changedJsx, fp);
            updatedCodes = currentEditedCodes.some((c) => c.name === fileToUpdate) ? currentEditedCodes.map((c) => c.name === fileToUpdate ? { ...c, contents: updatedSource } : c) : [...currentEditedCodes, { name: fileToUpdate, contents: updatedSource }];
            editedCodesRef.current = updatedCodes;
          } catch (err) { console.error("Error updating code for image upload:", err); }
        }
        if (previewIframeRef.current && prev.selectedElement?.id) {
          const runtimeId = prev.selectedElement.id;
          const componentId = getSelectionDataElementId(prev.selectedElement);
          scheduleUpdateComponentPostMessage(
              { type: "UPDATE_COMPONENT", runtimeId, componentId, value: relativeImageUrl, property: "imageContent" },
              { immediate: true }
          );
          scheduleUpdateComponentPostMessage(
              { type: "UPDATE_COMPONENT", runtimeId, componentId, property: "style", value: { backgroundImage: `url(${relativeImageUrl})` } },
              { immediate: true }
          );
        }
        return { ...prev, selectedElement: updatedElement, uploadedImageUrl: relativeImageUrl, editedCodes: updatedCodes, hasUnsavedChanges: true };
      });
      showNotificationGlobal("Image uploaded successfully", "success", "topCenter");
    } catch (error) { console.error("Error uploading image:", error); showNotificationGlobal("Failed to upload image", "error", "topCenter"); }
    finally { setState((p) => ({ ...p, uploadingImage: false })); }
  }, [projectData, state.projectMasterUser, scheduleUpdateComponentPostMessage]);

  const handleDrop            = useCallback((e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer.files.length > 0) handleImageUpload(e.dataTransfer.files[0]); }, [handleImageUpload]);
  const handleFileInputChange = useCallback((e) => { if (e.target.files.length > 0) handleImageUpload(e.target.files[0]); }, [handleImageUpload]);
  const handleDragOver        = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);

  // ─── Chat resize ──────────────────────────────────────────────────────────

  const handleMouseDown = useCallback((e) => {
    e.preventDefault(); isResizingRef.current = true; startXRef.current = e.clientX; startWidthRef.current = state.chatWidth;
    document.body.style.cursor = "ew-resize"; document.body.style.userSelect = "none"; document.body.classList.add("resizing");
    document.querySelectorAll("iframe").forEach((f) => (f.style.pointerEvents = "none"));
  }, [state.chatWidth]);

  const handleMouseMove = useCallback((e) => {
    const panel = leftPanelRef.current || chatContainerRef.current;
    if (!isResizingRef.current || !panel) return;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    animationFrameRef.current = requestAnimationFrame(() => {
      const w = Math.max(300, Math.min(800, startWidthRef.current + (e.clientX - startXRef.current)));
      if (panel) {
        panel.style.width = `${w}px`;
        panel.style.minWidth = `${w}px`;
      }
    });
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isResizingRef.current) return;
    isResizingRef.current = false; document.body.style.cursor = ""; document.body.style.userSelect = ""; document.body.classList.remove("resizing");
    document.querySelectorAll("iframe").forEach((f) => (f.style.pointerEvents = ""));
    if (animationFrameRef.current) { cancelAnimationFrame(animationFrameRef.current); animationFrameRef.current = null; }
    const panel = leftPanelRef.current || chatContainerRef.current;
    if (panel) setState((p) => ({ ...p, chatWidth: parseInt(panel.style.width, 10) }));
  }, []);

  React.useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove); document.addEventListener("mouseup", handleMouseUp);
    if (!projectData?.id) return;
  
    const fetchData = async () => {
      try {
        const response = await UtilsService.getCodeview(projectData.id, projectData?.userId, developMode);
        const data = response?.data || [];
        if (data.length > 0) {
          setState(prevState => {
            appCodesRef.current = data; // Update ref
            return {
              ...prevState,
              appCodes: data
            };
          });
        }
      } catch (error) {
        console.error('Error fetching code view:', error);
      }
    };
  
    fetchData();
  }, [state?.codeSyncTrigger, projectData?.id, developMode]);

  // Keep refs in sync with state
  React.useEffect(() => {
    appCodesRef.current = state.appCodes || [];
  }, [state.appCodes]);

  React.useEffect(() => {
    editedCodesRef.current = state.editedCodes || [];
  }, [state.editedCodes]);

  React.useEffect(() => {
    editedCodesRef.current = state.editedCodes || [];
  }, [state.editedCodes]);

  // Fetch storage configuration for image uploads
  React.useEffect(() => {
    const fetchStorageConfig = async () => {
      try {
        const response = await StorageConfigService.getAllStorageConfig();
        if (response?.status === 'SUCCESS') {
          const configData = response?.storageDTOPage?.content || [];
          const platformDefault = configData.find(item => item.platformDefault);
          if (platformDefault) {
            setState(prevState => ({
              ...prevState,
              storageId: platformDefault.id,
              rootFolder: null
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching storage config:', error);
      }
    };
    fetchStorageConfig();
  }, []);


  // Add mouse event listeners for resize
  React.useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove); document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("resizing"); document.body.style.cursor = ""; document.body.style.userSelect = "";
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [handleMouseMove, handleMouseUp]);

  // ─── Code fetch ───────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (!projectData?.id) return;
    const fetchData = async () => {
      try {
        const res  = await UtilsService.getCodeview(projectData.id, projectData?.userId, "frontend");
        const data = res?.data || [];
        if (data.length > 0) { appCodesRef.current = data; setState((p) => ({ ...p, appCodes: data })); }
      } catch (err) { console.error("Error fetching code view:", err); }
    };
    fetchData();
  }, [state?.codeSyncTrigger, projectData?.id]);

  React.useEffect(() => {
    const fetch = async () => {
      try {
        const res = await StorageConfigService.getAllStorageConfig();
        if (res?.status === "SUCCESS") {
          const def = (res?.storageDTOPage?.content || []).find((i) => i.platformDefault);
          if (def) setState((p) => ({ ...p, storageId: def.id, rootFolder: null }));
        }
      } catch (err) { console.error("Error fetching storage config:", err); }
    };
    fetch();
  }, []);

  const onEditorChange = useCallback((fileName, content) => {
    const normalized = (content ?? "").replace(/\r\n/g, "\n");
    const entry      = { name: fileName, contents: normalized };
    const next = editedCodesRef.current.some((c) => c.name === fileName)
        ? editedCodesRef.current.map((c) => c.name === fileName ? entry : c)
        : [...editedCodesRef.current, entry];
    editedCodesRef.current = next;
    setState((p) => ({ ...p, hasUnsavedChanges: true, editedCodes: next }));
  }, []);

  // ─── Memoised ─────────────────────────────────────────────────────────────

  const screenSizeStyles = useMemo(() => {
    const s = SCREEN_SIZES[state.selectedScreenSize];
    return s ? { width: s.width, height: s.height } : SCREEN_SIZES.desktop;
  }, [state.selectedScreenSize]);

  const handlePreviewReady = useCallback((ready) => {
    setState((prev) => (prev.previewLoaded === ready ? prev : { ...prev, previewLoaded: ready }));
  }, []);

  const handleOpenNewTab = useCallback(() => {
    const url = sanitizeUrl(derivedPreviewUrl || state.previewUrl);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  }, [derivedPreviewUrl, state.previewUrl]);

  // NOTE: We previously had an inline AI animation placeholder here.
  // That placeholder is now standardized via the shared <BuildingScreen /> component.

  // ─── Render ───────────────────────────────────────────────────────────────


  return (<div className="project-main">
      <CheckpointDrawer
        open={isCheckpointDrawerOpen}
        onClose={() => setIsCheckpointDrawerOpen(false)}
        projectId={projectData?.id}
      />
    
      {/* Left side — Chat, or Properties while an element is selected */}
      {state.selectedElement ? (
        <>
          <div
            ref={leftPanelRef}
            className="properties-chat-slot"
            style={{
              width: `${state.chatWidth}px`,
              minWidth: `${state.chatWidth}px`,
            }}
          >
            <PropertiesPanel
              state={state}
              setState={setState}
              onClose={() => {
                clearIframeHighlight();
                selectedIconNameRef.current = null;
                selectedSvgOuterHTMLRef.current = null;
                setState((p) => ({
                  ...p,
                  selectedElement: null,
                  highlightBox: null,
                }));
              }}
              onToggleSection={toggleSection}
              onTextContentChange={handleTextContentChange}
              onPropertyChange={handlePropertyChange}
              onIconChange={handleIconChange}
              parseBorder={parseBorder}
              onBorderPropertyChange={handleBorderPropertyChange}
              adjustBorderWidth={adjustBorderWidth}
              adjustNumericValue={adjustNumericValue}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onFileInputChange={handleFileInputChange}
              onImageUpload={handleImageUpload}
              onUpdateImageFilter={updateImageFilter}
              applyCrop={applyCrop}
              applyTransform={applyTransform}
              onResetFilters={handleResetFilters}
              formatMB={formatMB}
              maxImageUploadSize={MAX_IMAGE_UPLOAD_SIZE}
              allowedImageMimeTypes={ALLOWED_IMAGE_MIME_TYPES}
              availableWorkflows={state.availableWorkflows}
              availableServiceApps={state.availableServiceApps}
              uiEventSaving={state.uiEventSaving}
              selectedElementSelectorKey={state.selectedElementSelectorKey}
              selectedElementEventConfig={state.selectedElementEventConfig}
              onUpsertElementEventConfig={upsertElementEventConfig}
              projectId={projectData?.id}
              onOrchestratorOnClickChange={handleOrchestratorOnClickChange}
            />
          </div>
          <div
            className="chat-resize-handle"
            onMouseDown={handleMouseDown}
            title="Drag to resize properties panel"
          >
            <div className="chat-resize-handle-line"></div>
          </div>
        </>
      ) : (
        state.isChatVisible &&
        state.chatLoaded &&
        showChatPanel && (
          <>
            <ChatComponent
              key={String(projectData?.id ?? "")}
              ref={chatContainerRef}
              isChatVisible={state.isChatVisible}
              messages={projectData?.messages}
              projectId={projectData?.id}
              onPreviewUrlChange={handlePreviewUrlChange}
              onChatActivity={handleChatActivity}
              projectStatus={projectData?.projectStatus}
              projectAppType={projectData?.appType}
              chatWidth={state.chatWidth}
              messagesPageSize={projectData?.messagesPageSize ?? 50}
              messagesHasMore={Boolean(projectData?.messagesHasMore)}
              findingChatDraft={location.state?.findingChatDraft || null}
            />
            <div
              className="chat-resize-handle"
              onMouseDown={handleMouseDown}
              title="Drag to resize chat panel"
            >
              <div className="chat-resize-handle-line"></div>
            </div>
          </>
        )
      )}

      {showPlanWorkspace ? (
        <div className={`main-left main-left-transition ${
          state.selectedElement || state.isChatVisible
            ? "main-left-with-border"
            : "main-left-no-border"
        }`}>
          <PlanWorkspace
            projectData={projectData}
            chatMessages={projectData?.messages}
            onStartBuilding={handleStartBuildingFromPlan}
            onUpdatePlan={handleUpdatePlan}
            isChatVisible={state.isChatVisible}
            onToggleChat={
              ((projectData?.messages?.length ?? 0) > 0 || state.forceChat)
                ? toggleChatVisibility
                : null
            }
          />
        </div>
      ) : activeSection === "frontend" ? (
        <div className={`main-left main-left-transition ${
          state.selectedElement || state.isChatVisible
            ? "main-left-with-border"
            : "main-left-no-border"
        }`}>
            <>
          <div className="preview-container-header">
            <div className="preview-header-left">
            {showChatPanel && (<ChatToggleButton isChatVisible={state.isChatVisible} onToggle={toggleChatVisibility} iconSize={16}/>)}
              <div className="view-segmented-control">
                <button onClick={() => handleViewChange("preview")} title="Preview" className={`view-control-btn view-control-btn-preview ${state.currentView === "preview" ? "view-control-btn-active" : ""}`}><i className="fa-solid fa-eye" /></button>
                <button onClick={() => handleViewChange("code")}    title="Code Editor" className={`view-control-btn view-control-btn-code ${state.currentView === "code" ? "view-control-btn-active" : ""}`}><i className="fa-solid fa-code" /></button>
              </div>
              {canViewExistingPlan ? (
                <button
                  type="button"
                  className="view-control-btn"
                  title="View project plan"
                  onClick={handleViewExistingPlan}
                  style={{ marginLeft: 8 }}
                >
                  <i className="fa-solid fa-list-check" aria-hidden="true" />
                  <span style={{ marginLeft: 6 }}>Plan</span>
                </button>
              ) : null}
              {state.currentView === "preview" && state.previewUrl && (
                <div className={`code-edit-toggle ${!state?.enableEdit ? "force-disabled" : ""} ${state.isCodeEditEnabled ? "code-edit-toggle-active" : ""}`} onClick={toggleCodeEditMode} title={state.isCodeEditEnabled ? "Disable Code Editing" : "Enable Code Editing"}>
                  <BsCursor className="code-edit-icon" size={16} style={{ transform: "rotate(270deg)" }} />
                  <span className="code-edit-text">Edit</span>
                </div>
              )}

              {state.currentView === "code" && state.hasCodeAccess !== false && (
                <div
                  style={{
                    position: "absolute",
                    right: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                    {/* Connectors */}
                    <div style={{ position: "relative", display: "inline-flex" }}>
                      <button
                        type="button"
                        onClick={() => {
                          setCodeOverflowOpen(false);
                          if (githubConnected) {
                            setShowConnectors(false);
                            setState((prev) => ({
                              ...prev,
                              githubViewActive: true,
                            }));
                          } else {
                            setShowConnectors((v) => !v);
                          }
                        }}
                        style={{
                          border: githubConnected
                            ? "1px solid #bbf7d0"
                            : "1px solid #e5e7eb",

                          background: githubConnected ? "#f0fdf4" : "#fff",
                          cursor: "pointer",
                          padding: "6px 10px",
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        
                        {githubConnected ? (
                          <>
                            <LucideIcons.Github
                              size={18}
                              strokeWidth={2}
                              color="#166534"
                            />
                            <span
                              style={{
                                color: "#166534",
                                fontSize: "13px",
                                fontWeight: 600,
                              }}
                            >
                              GitHub
                            </span>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                padding: "3px 7px",
                                borderRadius: "999px",
                                background: "#dcfce7",
                                color: "#166534",
                                fontSize: "10px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  background: "#16a34a",
                                }}
                              />
                              Connected
                            </span>
                          </>
                        ) : (
                          <>
                            <LucideIcons.PlugZap
                              size={18}
                              strokeWidth={2}
                              color="#4880FF"
                            />
                            <span
                              style={{
                                color: "#4880FF",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                            >
                              Connectors
                            </span>
                          </>
                        )}
                      </button>
                      {!githubConnected && showConnectors && (
                        <div
                          style={{
                            position: "absolute",
                            top: "calc(100% + 8px)",
                            right: 0,
                            width: "320px",
                            padding: "10px",
                            background: "#ffffff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            zIndex: 9999,
                          }}
                        >
                          {/* GitHub */}
                          <button
                            type="button"
                            onClick={() => {
                              setState((prev) => ({
                                ...prev,
                                githubViewActive: true,
                              }));
                              setShowConnectors(false);
                            }}
                            style={{
                              width: "100%",
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              padding: "11px 10px",
                              borderRadius: "10px",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              textAlign: "left",
                              transition: "background 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#f5f7fb";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                background: "#f1f3f5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <i
                                className="fa-brands fa-github"
                                style={{ fontSize: "20px", color: "#111827" }}
                              />
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "13.5px",
                                  fontWeight: 600,
                                  color: "#111827",
                                }}
                              >
                                GitHub
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  marginTop: "3px",
                                }}
                              >
                                Connect your GitHub account
                              </div>
                            </div>

                            <LucideIcons.ChevronRight size={15} color="#9ca3af" />
                          </button>

                          {/* Bitbucket */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "10px",
                              borderRadius: "10px",
                              opacity: 0.55,
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                background: "#f6f7f8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <i
                                className="fa-brands fa-bitbucket"
                                style={{ fontSize: "18px", color: "#2684FF" }}
                              />
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  color: "#374151",
                                }}
                              >
                                Bitbucket
                              </div>
                            </div>

                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#7c8491",
                                background: "#f1f2f4",
                                borderRadius: "999px",
                                padding: "4px 8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Coming soon
                            </span>
                          </div>

                          {/* GitLab */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "10px",
                              borderRadius: "10px",
                              opacity: 0.55,
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "10px",
                                background: "#f6f7f8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <i
                                className="fa-brands fa-gitlab"
                                style={{ fontSize: "19px", color: "#FC6D26" }}
                              />
                            </div>

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "13px",
                                  fontWeight: 500,
                                  color: "#374151",
                                }}
                              >
                                GitLab
                              </div>
                            </div>

                            <span
                              style={{
                                fontSize: "10px",
                                fontWeight: 600,
                                color: "#7c8491",
                                background: "#f1f2f4",
                                borderRadius: "999px",
                                padding: "4px 8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Coming soon
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                  <div
                    ref={codeOverflowRef}
                    style={{
                      position: "relative",
                      display: "inline-flex",
                    }}
                  >
                    <button
                      type="button"
                      aria-label="More actions"
                      aria-haspopup="menu"
                      aria-expanded={codeOverflowOpen}
                      onClick={() => {
                        setShowConnectors(false);
                        setCodeOverflowOpen((open) => !open);
                      }}
                      style={{
                        border: "1px solid #e5e7eb",
                        background: codeOverflowOpen ? "#f3f4f6" : "#fff",
                        cursor: "pointer",
                        width: 34,
                        height: 34,
                        padding: 0,
                        borderRadius: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <LucideIcons.MoreVertical size={18} color="#374151" />
                    </button>

                    {codeOverflowOpen && (
                      <div
                        role="menu"
                        style={{
                          position: "absolute",
                          top: "calc(100% + 8px)",
                          right: 0,
                          minWidth: 196,
                          padding: 6,
                          background: "#ffffff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 12,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          zIndex: 9999,
                        }}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setCodeOverflowOpen(false);
                            setIsCheckpointDrawerOpen(true);
                          }}
                          style={{
                            width: "100%",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            padding: "9px 10px",
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            color: "#111827",
                            fontSize: 13,
                            fontWeight: 550,
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f5f7fb";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <LucideIcons.GitBranch size={16} color="#2563eb" />
                          Checkpoint
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={compressingSource || !projectData?.id}
                          onClick={() => {
                            if (compressingSource || !projectData?.id) return;
                            setCodeOverflowOpen(false);
                            setSourceCompressTrigger((value) => value + 1);
                          }}
                          style={{
                            width: "100%",
                            border: "none",
                            background: "transparent",
                            cursor: compressingSource ? "not-allowed" : "pointer",
                            padding: "9px 10px",
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            color: "#111827",
                            fontSize: 13,
                            fontWeight: 550,
                            textAlign: "left",
                            opacity: compressingSource ? 0.6 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!compressingSource) e.currentTarget.style.background = "#f5f7fb";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <LucideIcons.Archive size={16} color="#374151" />
                          {compressingSource ? "Compressing…" : "Compress"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}  
            </div>
            {state.currentView === "preview" && (
              <div className="preview-controls" role="toolbar">
                <div className="screen-size-selector" role="group">
                  {[{ key: "mobile", icon: "fa-mobile-alt", tooltip: "Mobile (375px)" }, { key: "tablet", icon: "fa-tablet-alt", tooltip: "Tablet (768px)" }, { key: "desktop", icon: "fa-desktop", tooltip: "Desktop (100%)" }].map((sz) => (
                    <button key={sz.key} onClick={() => handleScreenSizeChange(sz.key)} title={sz.tooltip} aria-pressed={state.selectedScreenSize === sz.key} className={`screen-size-btn ${state.selectedScreenSize === sz.key ? "screen-size-btn-active" : ""}`}><i className={`fa-solid ${sz.icon}`} /></button>
                  ))}
                </div>
                <div className="control-divider" />
                <div className="zoom-controls" role="group">
                  <button onClick={handleZoomOut} disabled={state.zoomLevel <= ZOOM_MIN} className="zoom-btn"><i className="fa-solid fa-minus" /></button>
                  <span onClick={resetZoom} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); resetZoom(); } }} className="zoom-level-display">{state.zoomLevel}%</span>
                  <button onClick={handleZoomIn} disabled={state.zoomLevel >= ZOOM_MAX} className="zoom-btn"><i className="fa-solid fa-plus" /></button>
                </div>
                <div className="control-divider" />
                <button onClick={handleOpenNewTab} disabled={!state.previewLoaded} className="preview-action-btn" title={state.previewLoaded ? "Open in New Tab" : "Preview is not ready yet"}><i className="fa-solid fa-external-link-alt" /></button>
                <div className="control-divider" />
                <button onClick={refreshPreview} className="refresh-btn" title="Refresh Preview"><i className="fa-solid fa-refresh" /></button>
              </div>
            )}
          </div>

                {!projectData?.id && state.currentView === "preview" && (
                  <div className="main-left main-left-with-border" style={{ position: "relative" }}>
                    <BuildingScreen />
                  </div>
                )}

          {state.currentView === "code" ? (
            state.hasCodeAccess === false ? (
              <CodeViewSamplePage />
            ) : (
              <CodeViewComponent
                projectId={projectData?.id}
                projectName={projectData?.name}
                onEditorChange={onEditorChange}
                projectMasterUser={state.projectMasterUser}
                onDevelopModeChange={setDevelopMode}
                codeRefreshTrigger={state.codeRefreshTrigger}
                codeDiscardTrigger={state.codeDiscardTrigger}
                discardedFilePaths={state.discardedFilePaths}
                connectorsViewActive={state.githubViewActive}
                      onExitConnectorsView={() =>
                        setState((prev) => ({ ...prev, githubViewActive: false }))
                      }
                sourceCompressTrigger={sourceCompressTrigger}
                onCompressingChange={setCompressingSource}
              />
            )
          ) : (
            <div
              className={`preview-container ${projectData?.id ? "" : "d-none"}`}
            >
              {projectData?.id && (
                <div
                  className="preview-wrapper"
                  style={{
                    ...screenSizeStyles,
                    transform: `scale(${state.zoomLevel / 100})`,
                  }}
                >
                  <IframeComponent
                    key={`${projectData.id}-${state.previewReloadKey}`}
                    onIframeRef={previewIframeRef}
                    isEditEnabled={state.isCodeEditEnabled}
                    updateParentState={updateParentState}
                    onPreviewReady={handlePreviewReady}
                    id={`preview-iframe-${projectData.id}`}
                    emptyElement={state.selectedElement}
                    highlightBox={state.highlightBox}
                    previewUrl={derivedPreviewUrl}
                    projectId={projectData.id}
                    projectName={projectData?.name || ""}
                    projectStatus={
                      state.generationConfirmed &&
                      ["preparing", "processing", "streaming", "creating", "pending", "queued"].includes(
                        String(projectData?.projectStatus || "").toLowerCase()
                      )
                        ? "success"
                        : (projectData?.projectStatus || "")
                    }
                    environmentType={projectData?.environmentType || state.environmentType || "K8S"}
                    chatStreaming={state.chatStreaming}
                  />
                </div>
              )}
              {projectData?.id && state.selectedScreenSize !== "desktop" && (
                <div className="screen-size-label">
                  {SCREEN_SIZES[state.selectedScreenSize]?.label}
                </div>
              )}
            </div>
          )}
            </>
        </div>
      ) : null}

      {!showPlanWorkspace && activeSection === "backend" && (
        <div className={`main-left main-left-transition ${state.isChatVisible ? "main-left-with-border" : "main-left-no-border"}`} style={{ overflowX: "auto" }}>
          <div className="preview-container-header preview-container-header-sticky">
            <div className="preview-header-left">
              {showChatPanel && (<ChatToggleButton isChatVisible={state.isChatVisible} onToggle={toggleChatVisibility} iconSize={16} />)}
              <RadioButtonGroup options={[{ key: "database", label: "Data", icon: "fa-database", color: "#10b981", tooltip: "Database Tables" }, { key: "restapi", label: "Rest API", icon: "fa-file-code", color: "#3b82f6", tooltip: "REST API Documentation" }]} activeKey={state.activeBackendTab} onChange={handleBackendTabChange} size="small" />
            </div>
            {state.activeBackendTab === 'database' && (
              <div role="toolbar" aria-label="Database actions">
                {state.dbConnected && (
                  <button
                    type="button"
                    className="export-db-btn"
                    onClick={handleExportDb}
                    title="Export database as SQL (pg_dump)"
                    aria-label="Export database as SQL (pg_dump)"
                    disabled={state.exportingDb}
                  >
                    <span className="export-db-btn__icon" aria-hidden="true">
                      {state.exportingDb ? (
                        <PiSpinnerGapLight className="export-db-btn__spinner" />
                      ) : (
                        <i className="fa-solid fa-database" />
                      )}
                    </span>
                    <span className="export-db-btn__text">
                      <span className="export-db-btn__label">Export DB</span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
          {state.activeBackendTab === "database" && <div style={{ height: "100vh", overflowY: "auto" }}>
            <DatabaseComponent
              projectId={projectData?.id}
              onConnectionChange={(connected) =>
                setState(prev => ({
                  ...prev,
                  dbConnected: Boolean(connected),
                }))
              }
            />
            </div>}
          {state.activeBackendTab === "restapi"  && <CustomSwaggerUI specUrl={`${constructPreviewUrls(PREVIEW_BACKEND_API_URL, projectData?.id)}openapi.json`} />}
        </div>
      )}

      {!showPlanWorkspace && activeSection === "agents" && (
        <div className={`main-left main-left-transition ${state.isChatVisible ? "main-left-with-border" : "main-left-no-border"}`} style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <WorkflowHeader
            projectName={projectData?.name || "Untitled Project"}
            agentNodes={(projectData?.workFlowTemplateDTO?.nodes || []).filter(n => n?.type === 'agent' || n?.type === 'task')}
            isWorkflowRunning={state.isWorkflowRunning} hasAgents={state.hasAgents} hasInputs={state.hasInputs} hasTasks={state.hasTasks}
            activeButton={state.activeButton} workflowEventStatus={state.workflowEventStatus} workflowError={null} workflowResult={null} 
            projectId={projectData?.id || projectData?.projectInfo?.id}
            isWorkflowSaved={state.isWorkflowSaved}
            orchestratorId={projectData?.workFlowTemplateDTO?.id || projectData?.projectInfo?.workFlowTemplateDTO?.id}
            onSetActiveButton={(buttonName) => {
              setState(prevState => ({ ...prevState, activeButton: buttonName }));
            }}
            onExecute={() => {
              if (state.isWorkflowRunning) {
                setState(prevState => ({ ...prevState, activeButton: 'run' }));
                window.dispatchEvent(new CustomEvent('stop-workflow-pod', {
                  detail: { source: 'execute-button' },
                }));
                return;
              }
              if (!state.hasAgents && !state.hasTasks) return;
              setState(prevState => ({ ...prevState, activeButton: 'run' }));
              window.dispatchEvent(new CustomEvent('check-and-execute-workflow'));
            }}
            onGenerate={() => {
              if (!state.hasAgents && !state.hasTasks || state.isWorkflowRunning) return;
              setState(prevState => ({ ...prevState, activeButton: 'generate' }));
              window.dispatchEvent(new CustomEvent('generate-workflow'));
            }}
            onSave={(options) => {
              if (!state.hasAgents && !state.hasTasks) return;
              setState(prevState => ({ ...prevState, activeButton: 'save' }));
              const event = new CustomEvent('save-workflow', { detail: options || {} });
              window.dispatchEvent(event);
            }}
            onPublish={(meta) => {
              if (!state.hasAgents && !state.hasTasks) return;
              setState(prevState => ({ ...prevState, activeButton: 'publish' }));
              window.dispatchEvent(new CustomEvent('publish-workflow', { detail: meta || {} }));
            }}
            onClear={() => {
              setState(prevState => ({ ...prevState, activeButton: 'clear' }));
              handleClearWorkflow();
            }}
            onConsole={() => {
              if (!state.hasInputs) return;
              setState(prevState => ({ ...prevState, activeButton: 'console' }));
              handleOpenConsole();
            }}
            onClose={() => navigate('/apps')}
            showProjectInfo={false}
            isChatVisible={state.isChatVisible}
            onToggleChat={showChatPanel ? toggleChatVisibility : null}
          />
          <div style={{ flex: 1, overflow: "hidden" }}>
            <DrawWorkFlow appInfo={projectData} chatVisible={state.isChatVisible} chatWidth={state.chatWidth} />
          </div>
        </div>
      )}

      {state.hasUnsavedChanges && (
        <div className="project-toast-view">
          <div className="project-toast-content">
            <div className="project-toast-message"><i className="fa-solid fa-exclamation-circle project-toast-icon" /><span>You have unsaved changes</span></div>
            <div className="project-toast-actions">
              <button className="project-toast-btn project-toast-btn-reset" onClick={handleReset} title="Reset Changes"><i className="fa-solid fa-rotate-left" /><span>Reset</span></button>
              <button className="project-toast-btn project-toast-btn-save"  onClick={handleSave}  title="Save Changes" ><i className="fa-solid fa-save"        /><span>Save</span> </button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

ProjectView.propTypes = {
  projectData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    messages: PropTypes.array,
    projectStatus: PropTypes.string,
  }),
  activeSection: PropTypes.oneOf(['frontend', 'backend', 'agents']).isRequired,
  onRequireAgentsSection: PropTypes.func,
};

ProjectView.defaultProps = {
  projectData: null,
  onRequireAgentsSection: null,
};
ProjectView.defaultProps = { projectData: null };

export default ProjectView;